/**
 * Full text of the Pillar Industrial Supply procurement demo agreements.
 *
 * PURE DATA. No runtime imports: this module is loaded by the browser app and
 * by `scripts/demo/procurement/generate-contract-pdfs.ts` (plain Node, native
 * type stripping), so it may only use erasable TypeScript syntax.
 *
 * All suppliers except Gently AI are fictional, apart from the four Pillar-cast
 * vendors shared with the AP and Replenishment demos (MRO Supply Co., Border
 * States, Brazos Valley Logistics and Sentinel Freight). Dates are absolute (ISO
 * `YYYY-MM-DD`) because these are static, signed documents. Section numbers
 * are the source of truth for citations: `data/clauses.ts` quotes text from
 * these sections verbatim (enforced by `clauses.test.ts`).
 *
 * Level 1 sections are Articles ('4') or Exhibits ('B'); level 2 sections are
 * numbered subsections ('4.2', 'B.3'). Each paragraph is rendered as its own
 * paragraph, so a clause quote must sit inside a single paragraph.
 */

export interface ContractParty {
  name: string;
  location: string;
}

export interface DocSection {
  /** '4', '4.2', 'B', 'B.3' */
  number: string;
  heading: string;
  paragraphs: string[];
  level: 1 | 2;
}

export interface ContractSignatures {
  buyerSigner: string;
  buyerTitle: string;
  supplierSigner: string;
  supplierTitle: string;
  /** ISO date */
  signedDate: string;
  envelopeId: string;
}

export interface ContractDocument {
  contractId: string;
  title: string;
  subtitle?: string;
  buyer: ContractParty;
  supplier: ContractParty;
  /** ISO date */
  effectiveDate: string;
  recitals: string[];
  sections: DocSection[];
  signatures: ContractSignatures;
}

function art(
  number: string,
  heading: string,
  ...paragraphs: string[]
): DocSection {
  return { number, heading, paragraphs, level: 1 };
}

function sub(
  number: string,
  heading: string,
  ...paragraphs: string[]
): DocSection {
  return { number, heading, paragraphs, level: 2 };
}

const BUYER: ContractParty = {
  name: 'Pillar Industrial Supply, Inc.',
  location: 'Dallas, Texas',
};

const MSA_GEN_2025_001: ContractDocument = {
  contractId: 'MSA-GEN-2025-001',
  title: 'Master Subscription and Services Agreement',
  subtitle:
    'Including Exhibit A (Statement of Work No. 1), Exhibit B (Service Level Agreement) and Exhibit C (Data Processing Agreement)',
  buyer: BUYER,
  supplier: { name: 'Gently AI, Inc.', location: 'New York, New York' },
  effectiveDate: '2025-11-01',
  recitals: [
    'This Master Subscription and Services Agreement (together with its Exhibits, this “Agreement”) is entered into as of November 1, 2025 (the “Effective Date”) by and between Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas (“Customer”), and Gently AI, Inc., a Delaware corporation having its principal offices in New York, New York (“Gently”).',
    'Gently provides a cloud-based, AI-native enterprise resource planning platform, including accounts payable automation and procurement agents, delivered as a software-as-a-service offering.',
    'Customer wishes to subscribe to the Platform for use by its finance and procurement organizations and to engage Gently to implement the Platform, and Gently wishes to provide the Platform and those services, in each case on the terms of this Agreement.',
    'NOW, THEREFORE, in consideration of the mutual promises in this Agreement and other good and valuable consideration, the receipt and sufficiency of which are acknowledged, the parties agree as follows:',
  ],
  sections: [
    art('1', 'Definitions and Interpretation'),
    sub(
      '1.1',
      'Definitions',
      'In this Agreement the following terms have the meanings given below. “Affiliate” means any entity that directly or indirectly controls, is controlled by, or is under common control with a party, where “control” means ownership of more than fifty percent (50%) of the voting interests of an entity or the power to direct its management and policies. “Authorized User” means any employee, contractor or agent of Customer or its Affiliates whom Customer permits to use the Platform.',
      '“Customer Data” means all data, documents, records and other content that Customer, its Affiliates or their Authorized Users submit to or generate within the Platform, or that Gently receives from Customer systems in connection with the Services, including supplier records, purchase orders, invoices, contracts, payment instructions and any personal data contained in them. “Documentation” means Gently’s then-current user guides and technical documentation for the Platform.',
      '“Fees” means the Platform Fees, the Implementation Fee and any other amounts payable by Customer under this Agreement or an SOW. “Outputs” means the extractions, classifications, recommendations, draft communications and other results that the Platform generates from Customer Data. “Platform” means Gently’s hosted software platform, including the accounts payable automation module and the procurement agents module, together with all updates and enhancements made generally available to Gently’s customers during the Term.',
      '“Security Incident” means any actual or reasonably suspected unauthorized access to, or acquisition, disclosure, loss, alteration or destruction of, Customer Data in the possession or control of Gently or its sub-processors. “Services” means the Platform subscription, support, and the professional services described in any SOW. “SOW” means a statement of work signed by both parties under this Agreement, the first of which is attached as Exhibit A (“SOW #1”). “Term” has the meaning given in Section 11.1.',
    ),
    sub(
      '1.2',
      'Interpretation',
      'Headings are for convenience only and do not affect interpretation. The words “include” and “including” are not words of limitation. References to Sections and Exhibits are to the sections of and exhibits to this Agreement unless stated otherwise. A reference to a statute or regulation includes it as amended or replaced from time to time.',
    ),
    sub(
      '1.3',
      'Order of precedence',
      'If there is a conflict between the parts of this Agreement, the following order of precedence applies: (a) Exhibit C (Data Processing Agreement) for matters concerning the processing of personal data; (b) the body of this Agreement; (c) Exhibit B (Service Level Agreement); and (d) Exhibit A and any other SOW. Terms contained in any purchase order, invoice, click-through or online terms of service are of no effect, even if signed or accepted by Customer’s personnel, unless they are expressly adopted in a written amendment under Section 14.8.',
    ),

    art('2', 'Platform Access and Use'),
    sub(
      '2.1',
      'Subscription',
      'Subject to the terms of this Agreement, Gently grants Customer and its Affiliates a non-exclusive, non-transferable (except as permitted under Section 14.4) right during the Term to access and use the Platform, and to use the Documentation, for their internal business purposes. The subscription covers the accounts payable automation module and the procurement agents module described in Exhibit A.',
    ),
    sub(
      '2.2',
      'Authorized Users and Affiliates',
      'The Platform Fees cover an unlimited number of Authorized Users. Customer is responsible for its Authorized Users’ compliance with this Agreement and for maintaining the confidentiality of their credentials. Any Affiliate of Customer, including those operating Customer’s distribution centers in Dallas, Texas, Phoenix, Arizona and Hayward, California and its panel shop in Dallas, Texas, may use the Platform under this Agreement, and Customer may enforce this Agreement on behalf of its Affiliates.',
    ),
    sub(
      '2.3',
      'Restrictions',
      'Customer shall not (a) sell, resell, lease or sublicense the Platform to any third party; (b) reverse engineer, decompile or attempt to derive the source code of the Platform, except to the extent applicable law prohibits this restriction; (c) use the Platform to build a competing product; or (d) knowingly transmit malicious code through the Platform. Customer may use the Platform to process data of its suppliers and business partners.',
    ),
    sub(
      '2.4',
      'Platform changes',
      'Gently may update the Platform from time to time. Gently shall not make any change that materially reduces the functionality, security or availability of the Platform during the Term, and shall give Customer at least sixty (60) days’ prior written notice before it retires any feature that Customer actively uses, together with a functionally equivalent replacement.',
    ),
    sub(
      '2.5',
      'Autonomy settings and human oversight',
      'The Platform allows Customer to set, for each agent and workflow, whether the Platform will suggest an action, act only with Customer’s approval, or act autonomously within limits configured by Customer. Gently shall not enable a higher level of autonomy, or raise a configured limit, except at Customer’s written direction through the Platform’s administrative controls. Gently shall maintain an audit log of every action taken by an agent, including the data used and the reasoning recorded, and shall make the log available to Customer for the Term and for seven (7) years thereafter on request.',
    ),
    sub(
      '2.6',
      'Suspension',
      'Gently may suspend Customer’s access to the Platform only (a) to the extent and for the duration necessary to prevent or respond to a material security threat to the Platform or to Customer Data, or (b) if Customer fails to pay undisputed Fees more than sixty (60) days after the due date and fails to cure within ten (10) business days after written notice that identifies the overdue invoice. Gently shall give Customer as much advance notice as is practicable and shall restore access promptly once the cause is resolved.',
    ),

    art('3', 'Implementation Services and Statements of Work'),
    sub(
      '3.1',
      'Statements of work',
      'Gently shall perform the professional services described in each SOW. Each SOW shall identify the deliverables, milestones, acceptance criteria, schedule, fees and personnel for the work. SOW #1 (Exhibit A) is part of this Agreement as of the Effective Date. Additional SOWs, including any expansion of the Platform to additional business units or sites, become effective only when signed by authorized representatives of both parties.',
    ),
    sub(
      '3.2',
      'Performance standard',
      'Gently shall perform the Services in a professional and workmanlike manner, using appropriately skilled and qualified personnel, and in accordance with the schedule in the applicable SOW. Gently is responsible for the acts and omissions of its subcontractors as if they were its own, and shall not subcontract any material portion of the implementation services without Customer’s prior written consent.',
    ),
    sub(
      '3.3',
      'Acceptance',
      'Deliverables and milestones are subject to acceptance by Customer under the procedure in Exhibit A, Section A.8. Fees for a milestone become payable only after Customer has accepted that milestone in writing.',
    ),
    sub(
      '3.4',
      'Customer cooperation',
      'Customer shall provide the information, access and personnel that Gently reasonably requires to perform the Services and that are identified in the applicable SOW. Gently shall promptly notify Customer if it believes a Customer delay will affect the schedule, and shall use reasonable efforts to mitigate the effect of the delay.',
    ),
    sub(
      '3.5',
      'Change requests',
      'Either party may request a change to the scope, schedule or fees of an SOW. No change is effective until documented in a written change order signed by both parties under Section A.10. Gently shall not perform, or invoice for, work outside the agreed scope without a signed change order.',
    ),

    art('4', 'Fees and Payment'),
    sub(
      '4.1',
      'Fees',
      'In consideration of the Services Customer shall pay Gently: (a) a platform subscription fee of Three Hundred Sixty Thousand Dollars ($360,000) per Contract Year (the “Platform Fees”), invoiced in twelve (12) equal monthly installments of Thirty Thousand Dollars ($30,000); (b) a fixed implementation fee of One Hundred Eighty Thousand Dollars ($180,000) for the services described in SOW #1 (the “Implementation Fee”), invoiced on the milestone schedule in Section A.7; and (c) the fees stated in any additional SOW. A “Contract Year” is each twelve (12) month period beginning on the Effective Date or an anniversary of it. The total contract value for the Initial Term, comprising twenty-four (24) monthly installments of the Platform Fees and the Implementation Fee, is Nine Hundred Thousand Dollars ($900,000). All amounts are in United States dollars.',
    ),
    sub(
      '4.2',
      'Invoicing and payment terms',
      'Gently shall invoice Customer in accordance with Section 4.1 and the applicable SOW. Customer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). An invoice is valid only if it (a) references this Agreement by its number, MSA-GEN-2025-001, and any applicable Customer purchase order number; (b) separately identifies the Platform Fees and any milestone fees; (c) states the service period; and (d) is delivered to Customer’s accounts payable intake address in the form the parties have agreed. Payment is deemed made on the date Customer initiates the electronic payment.',
    ),
    sub(
      '4.3',
      'Taxes',
      'The Fees are exclusive of sales, use, value-added and similar taxes. Gently shall separately state any such taxes that it is required to collect, and Customer shall pay them unless Customer provides a valid exemption certificate. Each party is responsible for taxes on its own net income, property and employees.',
    ),
    sub(
      '4.4',
      'Renewal pricing',
      'The Platform Fees are fixed for the Initial Term. For any Renewal Term, Gently may increase the Platform Fees only by written notice delivered at least one hundred twenty (120) days before the end of the then-current Term, and any such increase shall not exceed five percent (5%) of the Platform Fees in effect during the immediately preceding twelve (12) months. If Gently does not deliver a timely notice, the Platform Fees for the Renewal Term remain unchanged.',
    ),
    sub(
      '4.5',
      'Disputed amounts',
      'If Customer disputes any invoiced amount in good faith, Customer shall notify Gently in writing within thirty (30) days after receipt of the invoice, describing the basis for the dispute, and shall pay the undisputed portion on the due date. The parties shall work in good faith to resolve the dispute within thirty (30) days. Gently shall not suspend the Services or charge interest in respect of amounts disputed in good faith.',
    ),
    sub(
      '4.6',
      'Late payment',
      'Undisputed amounts that remain unpaid more than thirty (30) days after the due date bear interest at the rate of one-half of one percent (0.5%) per month, or the maximum rate permitted by law if lower, from the due date until paid.',
    ),
    sub(
      '4.7',
      'Expenses',
      'The Implementation Fee includes all of Gently’s travel and living expenses for SOW #1. For any other work, Customer shall reimburse reasonable, pre-approved travel expenses incurred in accordance with Customer’s travel policy, at cost and without markup, upon presentation of receipts.',
    ),

    art('5', 'Intellectual Property'),
    sub(
      '5.1',
      'Gently property',
      'As between the parties, Gently retains all right, title and interest in the Platform, the Documentation, Gently’s models and algorithms, and all improvements to them, other than Customer Data and Outputs. Except for the rights expressly granted in Section 2.1, no rights are granted to Customer by implication or estoppel.',
    ),
    sub(
      '5.2',
      'Customer data and outputs',
      'As between the parties, Customer owns all right, title and interest in and to Customer Data and the Outputs. Customer grants Gently a limited, non-exclusive, non-transferable license during the Term to host, process and display Customer Data and Outputs solely to provide the Services to Customer in accordance with this Agreement and Exhibit C.',
    ),
    sub(
      '5.3',
      'Deliverables and configurations',
      'Configurations, workflow definitions, rules, prompts and templates that Gently creates specifically for Customer under an SOW (“Customer Configurations”) are licensed to Customer for use with the Platform during the Term. Customer owns any Customer Configurations that consist of or embody Customer’s confidential business rules, policies or approval matrices. Nothing in this Section restricts Gently’s use of its general know-how and generally applicable techniques.',
    ),
    sub(
      '5.4',
      'Feedback',
      'Customer may provide suggestions or feedback about the Platform. Gently may use that feedback without obligation to Customer, provided that Gently does not identify Customer as the source and does not disclose Customer Data or Customer’s confidential information.',
    ),

    art('6', 'Confidentiality'),
    sub(
      '6.1',
      'Confidential information',
      '“Confidential Information” means all non-public information disclosed by or on behalf of a party (the “Discloser”) to the other party (the “Recipient”) that is marked or identified as confidential or that a reasonable person would understand to be confidential given its nature and the circumstances of disclosure. Customer Data, Customer’s supplier pricing and contract terms, and the non-public terms of this Agreement are Customer’s Confidential Information. The non-public architecture and source code of the Platform are Gently’s Confidential Information.',
    ),
    sub(
      '6.2',
      'Obligations',
      'The Recipient shall (a) use the Discloser’s Confidential Information only to perform its obligations or exercise its rights under this Agreement; (b) not disclose it to any third party other than its employees, Affiliates, contractors and professional advisers who need to know it and who are bound by confidentiality obligations at least as protective as this Article 6; and (c) protect it using at least the same degree of care it uses for its own confidential information of a similar nature, and in any event no less than reasonable care.',
    ),
    sub(
      '6.3',
      'Exclusions',
      'Confidential Information does not include information that (a) is or becomes publicly available through no breach of this Agreement; (b) was known to the Recipient without restriction before disclosure; (c) is independently developed by the Recipient without use of the Discloser’s Confidential Information; or (d) is rightfully received from a third party without a duty of confidentiality.',
    ),
    sub(
      '6.4',
      'Compelled disclosure',
      'The Recipient may disclose Confidential Information to the extent required by law, regulation or court order, provided that, where legally permitted, it gives the Discloser prompt written notice and reasonable assistance so that the Discloser may seek a protective order, and discloses only the portion that is legally required.',
    ),
    sub(
      '6.5',
      'Duration and return',
      'The obligations in this Article 6 continue during the Term and for five (5) years after its expiration or termination, and for so long thereafter as the information remains a trade secret under applicable law. On request, and in any event on termination, the Recipient shall return or destroy the Discloser’s Confidential Information, subject to Section 11.5 with respect to Customer Data.',
    ),

    art('7', 'Warranties and Disclaimers'),
    sub(
      '7.1',
      'Mutual warranties',
      'Each party represents and warrants that it is duly organized and in good standing, has full power and authority to enter into and perform this Agreement, and that its execution and performance of this Agreement do not conflict with any other agreement by which it is bound.',
    ),
    sub(
      '7.2',
      'Gently warranties',
      'Gently warrants that (a) the Platform will perform materially in accordance with the Documentation and this Agreement throughout the Term; (b) the Services will be performed in a professional and workmanlike manner by qualified personnel; (c) Gently will use industry-standard measures to prevent the introduction of viruses, malware and other harmful code into the Platform; (d) the Platform and Services will comply with laws applicable to Gently’s provision of them; and (e) Gently has the right to grant the rights granted in this Agreement.',
    ),
    sub(
      '7.3',
      'Remedies',
      'If the Platform or any Services fail to conform to a warranty in Section 7.2, Gently shall, at its own expense and as Customer’s first remedy, promptly correct the non-conformity or re-perform the affected Services. If Gently is unable to do so within thirty (30) days after written notice, Customer may terminate the affected Services and receive a refund of prepaid Fees for the period after termination, in addition to any other remedy available under this Agreement.',
    ),
    sub(
      '7.4',
      'AI outputs',
      'Customer acknowledges that Outputs are generated by statistical models and may contain errors. Gently shall maintain controls designed to reduce the risk of inaccurate Outputs, including the citations, confidence indicators and human-approval steps described in the Documentation. Except as stated in Section 7.2, the Platform is provided without any other warranty, and Gently disclaims all implied warranties of merchantability, fitness for a particular purpose and non-infringement to the extent permitted by law.',
    ),

    art('8', 'Indemnification'),
    sub(
      '8.1',
      'Indemnification by Gently',
      'Gently shall defend Customer, its Affiliates and their respective officers, directors and employees against any third-party claim, suit or proceeding to the extent arising from (a) an allegation that the Platform or the Services infringe or misappropriate a patent, copyright, trademark or trade secret of a third party; or (b) Gently’s gross negligence or willful misconduct, and shall pay all damages, settlements and reasonable attorneys’ fees finally awarded or agreed in settlement.',
    ),
    sub(
      '8.2',
      'Indemnification by Customer',
      'Customer shall defend Gently against any third-party claim to the extent arising from an allegation that Customer Data, as provided by Customer and used by Gently in accordance with this Agreement, infringes or misappropriates the intellectual property rights of a third party, and shall pay all damages, settlements and reasonable attorneys’ fees finally awarded or agreed in settlement.',
    ),
    sub(
      '8.3',
      'Procedure',
      'The indemnified party shall give the indemnifying party prompt written notice of the claim (provided that delay relieves the indemnifying party only to the extent it is prejudiced), allow the indemnifying party sole control of the defense and settlement (provided that no settlement may impose any liability or admission on the indemnified party without its written consent), and provide reasonable cooperation at the indemnifying party’s expense.',
    ),
    sub(
      '8.4',
      'Infringement remedies',
      'If the Platform becomes, or in Gently’s reasonable opinion is likely to become, the subject of an infringement claim, Gently shall at its expense procure the right for Customer to continue using the Platform, modify it so that it is non-infringing without material loss of functionality, or, if neither is commercially practicable, terminate the affected Services and refund prepaid Fees for the period after termination. This Article 8 states each party’s entire liability and the other party’s exclusive remedy for third-party intellectual property claims.',
    ),

    art('9', 'Limitation of Liability'),
    sub(
      '9.1',
      'Liability cap',
      'Except as provided in Section 9.3, each party’s total cumulative liability arising out of or related to this Agreement, whether in contract, tort (including negligence) or otherwise, shall not exceed the Fees paid or payable by Customer under this Agreement during the twelve (12) months immediately preceding the event giving rise to the claim.',
    ),
    sub(
      '9.2',
      'Exclusion of certain damages',
      'Except as provided in Section 9.3, neither party shall be liable to the other for any indirect, incidental, special, exemplary or consequential damages, or for loss of profits, revenue or goodwill, arising out of or related to this Agreement, even if advised of the possibility of such damages.',
    ),
    sub(
      '9.3',
      'Exceptions and data breach cap',
      '(a) The limitations in Sections 9.1 and 9.2 do not apply to (i) a party’s indemnification obligations under Article 8; (ii) a party’s breach of Article 6, other than a Security Incident; (iii) a party’s gross negligence, fraud or willful misconduct; or (iv) Customer’s obligation to pay undisputed Fees.',
      '(b) Gently’s aggregate liability arising out of any Security Incident or any breach of Article 12 or Exhibit C, including the costs of investigation, notification, credit monitoring, regulatory fines and penalties and third-party claims, shall not exceed three (3) times the Fees paid or payable by Customer during the twelve (12) months immediately preceding the Security Incident (the “Data Breach Cap”). The Data Breach Cap is separate from, and in addition to, the cap in Section 9.1.',
    ),

    art('10', 'Insurance'),
    sub(
      '10.1',
      'Required coverage',
      'During the Term and for two (2) years thereafter Gently shall maintain, with insurers rated A- VII or better by A.M. Best, at least the following coverage: (a) commercial general liability insurance of $2,000,000 per occurrence; (b) technology errors and omissions (professional liability) insurance of $5,000,000 per claim; (c) cyber and network security liability insurance of $5,000,000 per claim, including breach response costs; and (d) workers’ compensation insurance as required by law. Gently shall provide certificates of insurance on request and shall give Customer thirty (30) days’ notice of cancellation or material reduction of any required coverage.',
    ),

    art('11', 'Term and Termination'),
    sub(
      '11.1',
      'Term and renewal',
      'This Agreement begins on the Effective Date and continues for an initial term of twenty-four (24) months ending on October 31, 2027 (the “Initial Term”). Thereafter it renews automatically for successive periods of twelve (12) months (each a “Renewal Term” and, together with the Initial Term, the “Term”) unless either party gives written notice of non-renewal at least sixty (60) days before the end of the then-current Initial Term or Renewal Term.',
    ),
    sub(
      '11.2',
      'Termination for convenience',
      'Customer may terminate this Agreement or any SOW for convenience at any time on ninety (90) days’ prior written notice to Gently. On such termination, Gently shall refund any prepaid Fees attributable to the period after the effective date of termination, and Customer shall pay for Services performed through the effective date of termination, including a pro-rated portion of the Implementation Fee for work performed on any milestone not yet accepted. No other termination charge or fee applies.',
    ),
    sub(
      '11.3',
      'Termination for cause',
      'Either party may terminate this Agreement by written notice if the other party materially breaches this Agreement and fails to cure the breach within thirty (30) days after receiving written notice describing it, or immediately if the other party becomes insolvent, makes an assignment for the benefit of creditors or is the subject of a bankruptcy petition that is not dismissed within sixty (60) days.',
    ),
    sub(
      '11.4',
      'Chronic service level failure',
      'Customer may also terminate this Agreement without penalty in the circumstances described in Exhibit B, Section B.4.',
    ),
    sub(
      '11.5',
      'Effect of termination; data return',
      'On expiration or termination for any reason, Customer’s right to use the Platform ends, except that Gently shall make Customer Data available for export in a standard, machine-readable format for sixty (60) days. Gently shall then delete Customer Data in accordance with Section C.8 and certify the deletion in writing. Termination does not affect fees accrued for Services performed before the effective date of termination.',
    ),
    sub(
      '11.6',
      'Transition assistance',
      'For up to ninety (90) days after expiration or termination (other than termination by Gently under Section 11.3), Gently shall, at Customer’s request, continue to provide the Platform and reasonable transition assistance on the terms of this Agreement and at the Fees then in effect, pro-rated for the transition period.',
    ),
    sub(
      '11.7',
      'Survival',
      'Articles 5, 6, 8 and 9, Sections 4.5, 11.5 and 14.1, Exhibit C and any other provisions that by their nature are intended to survive, survive expiration or termination of this Agreement.',
    ),

    art('12', 'Data Protection, Security and Artificial Intelligence'),
    sub(
      '12.1',
      'Security program and SOC 2 report',
      'Gently shall maintain a written information security program with administrative, technical and physical safeguards that are no less protective than those described in Exhibit C, Section C.4. Gently shall obtain, at its own expense, an annual SOC 2 Type II examination covering the security, availability and confidentiality trust services criteria for the Platform, and shall provide Customer with a copy of its SOC 2 Type II report annually, within thirty (30) days after the report is issued. Gently shall remediate any exception noted in the report that is relevant to the Services within the period stated in its remediation plan and report on progress to Customer on request.',
    ),
    sub(
      '12.2',
      'Use of customer data',
      'Gently shall process Customer Data only (a) to provide the Services to Customer, (b) in accordance with Customer’s documented instructions, including those in this Agreement and Exhibit C, and (c) as required by law. Gently shall not sell Customer Data, or use it to market to or profile Customer’s suppliers.',
    ),
    sub(
      '12.3',
      'Security incidents',
      'Gently shall notify Customer of any Security Incident without undue delay and in any event within forty-eight (48) hours after becoming aware of it, in accordance with Exhibit C, Section C.5, and shall promptly take all steps necessary to contain, investigate and remediate it at Gently’s expense.',
    ),
    sub(
      '12.4',
      'No training on customer data',
      'Gently shall not use Customer Data or Outputs, or any content derived from them, to train, fine-tune or otherwise improve any machine learning or artificial intelligence model, whether owned by Gently or by a third party. Gently shall require each sub-processor and third-party model provider that processes Customer Data to agree in writing to the same restriction and to zero retention of Customer Data beyond the time needed to process the individual request.',
    ),
    sub(
      '12.5',
      'Sub-processors and model providers',
      'Gently’s use of sub-processors, including third-party model providers, is governed by Exhibit C, Section C.3. Gently is liable for the acts and omissions of its sub-processors to the same extent as for its own.',
    ),
    sub(
      '12.6',
      'Data location',
      'Gently shall store and process Customer Data only in the locations permitted by Exhibit C, Section C.2.',
    ),

    art('13', 'Service Levels and Support'),
    sub(
      '13.1',
      'Service levels',
      'Gently shall provide the Platform in accordance with the availability commitment, support response times, service credits, disaster recovery objectives and other service levels set out in Exhibit B (the “SLA”).',
    ),
    sub(
      '13.2',
      'Support',
      'Gently shall provide technical support for the Platform at no additional charge, in accordance with Section B.2, through a support portal, email and a telephone hotline.',
    ),
    sub(
      '13.3',
      'Remedies',
      'Service credits under Section B.3 are Customer’s sole and exclusive monetary remedy for a failure to meet the availability commitment in Section B.1. This does not limit Customer’s right to terminate under Section B.4 or Section 11.3, or Customer’s other rights and remedies for any breach that is not a failure to meet the availability commitment.',
    ),

    art('14', 'General Provisions'),
    sub(
      '14.1',
      'Governing law and venue',
      'This Agreement, and any dispute arising out of or related to it, is governed by the laws of the State of New York, without regard to its conflict of laws principles. The parties submit to the exclusive jurisdiction of the state and federal courts located in New York County, New York, for any action arising out of or related to this Agreement. The United Nations Convention on Contracts for the International Sale of Goods does not apply.',
    ),
    sub(
      '14.2',
      'Notices',
      'Notices under this Agreement must be in writing and delivered by hand, by nationally recognized overnight courier or by email with confirmation of transmission, to the receiving party’s principal office (for Customer, Dallas, Texas, with a copy to its Senior Counsel, Commercial; for Gently, New York, New York, with a copy to its General Counsel) or to such other address as a party designates by notice. Notices of breach, termination or non-renewal sent by email must also be sent by courier. Operational notices, including maintenance notices and support communications, may be given through the Platform or by email to the contacts designated by Customer.',
    ),
    sub(
      '14.3',
      'Force majeure',
      'Neither party is liable for any failure or delay in performance (other than an obligation to pay money) caused by events beyond its reasonable control, including natural disasters, war, terrorism, civil unrest, governmental action, epidemics, or failures of public utilities or the internet generally, provided that the affected party gives prompt notice and uses reasonable efforts to resume performance. A force majeure event does not excuse Gently from its obligations to maintain and implement disaster recovery measures under Section B.5, and does not extend any period for which Customer has paid for Services that Gently cannot provide. If a force majeure event continues for more than thirty (30) days, Customer may terminate the affected Services on written notice and receive a refund of prepaid Fees for the period after termination.',
    ),
    sub(
      '14.4',
      'Assignment',
      'Neither party may assign this Agreement without the prior written consent of the other, which shall not be unreasonably withheld, except that either party may assign it without consent to an Affiliate or to a successor in a merger, acquisition or sale of all or substantially all of its assets that relates to this Agreement, provided that the assignee is not a competitor of the other party and assumes this Agreement in writing. Any other attempted assignment is void.',
    ),
    sub(
      '14.5',
      'Independent contractors',
      'The parties are independent contractors. Nothing in this Agreement creates a partnership, joint venture, agency or employment relationship, and neither party may bind the other.',
    ),
    sub(
      '14.6',
      'Publicity',
      'Neither party shall issue a press release or publicly use the other party’s name, logo or trademarks, or state that the other is a customer or supplier, without the other party’s prior written consent in each instance.',
    ),
    sub(
      '14.7',
      'Compliance with laws',
      'Each party shall comply with laws applicable to its performance of this Agreement, including export control, sanctions, anti-bribery and anti-corruption laws. Gently shall comply with Customer’s Supplier Code of Conduct, as made available to Gently, including its provisions on ethical business practices and data protection.',
    ),
    sub(
      '14.8',
      'Entire agreement; amendment; waiver',
      'This Agreement, including its Exhibits and each SOW, is the entire agreement between the parties on its subject matter and supersedes all prior or contemporaneous proposals, understandings and agreements, whether written or oral. It may be amended only by a written instrument signed by authorized representatives of both parties. A waiver is effective only if in writing, and no failure or delay in exercising a right operates as a waiver of it.',
    ),
    sub(
      '14.9',
      'Severability',
      'If any provision of this Agreement is held invalid or unenforceable, it shall be enforced to the maximum extent permissible and the remaining provisions shall remain in full force and effect.',
    ),
    sub(
      '14.10',
      'Counterparts and electronic signature',
      'This Agreement may be executed in counterparts and by electronic signature, each of which is deemed an original and all of which together constitute one instrument. Electronic signatures and electronically delivered copies have the same legal effect as handwritten signatures and originals.',
    ),

    art(
      'A',
      'Statement of Work No. 1: AP Automation and Procurement Agents Rollout',
      'This Statement of Work No. 1 (“SOW #1”) is entered into under, and is governed by, the Master Subscription and Services Agreement between Customer and Gently effective November 1, 2025 (the “MSA”). Capitalized terms not defined in this Exhibit A have the meanings given in the MSA.',
    ),
    sub(
      'A.1',
      'Objectives and scope',
      'Gently shall implement the accounts payable automation module and the procurement agents module of the Platform for Customer, and shall roll them out to Customer’s corporate headquarters in Dallas, Texas and to its distribution centers in Dallas, Texas, Phoenix, Arizona and Hayward, California and its Dallas panel shop. The objectives are to (a) ingest, extract, match and route supplier invoices with minimal manual handling; (b) check every invoice against the terms of the applicable contract; (c) give Customer’s procurement teams an intake channel, contract intelligence and sourcing support through AI agents operating within Customer’s approval policies; and (d) integrate the Platform with Customer’s ERP, supplier master and document repositories.',
    ),
    sub(
      'A.2',
      'Schedule and governance',
      'The project starts on November 3, 2025 and is planned to complete on September 11, 2026, in four milestones (M1 through M4) described in Sections A.3 through A.6. Each party shall appoint a project lead. Customer’s project sponsor is its Director, Indirect Procurement. The project leads shall meet weekly, and a steering committee comprising Customer’s sponsor, Customer’s IT Director and Gently’s executive sponsor shall meet monthly to review progress, risks and change requests.',
    ),
    sub(
      'A.3',
      'Milestone M1: Discovery, design and configuration plan',
      'Target date: December 12, 2025. Fee on acceptance: $27,000.',
      'Gently shall conduct process workshops with Customer’s accounts payable, procurement and IT teams; document Customer’s approval matrices, tolerance rules and supplier onboarding policy; inventory the contracts, supplier records and ERP interfaces in scope; and deliver a signed-off configuration and integration plan.',
      'Acceptance criteria: (a) the configuration plan covers every in-scope invoice type, approval policy and ERP interface; (b) the data migration approach for the supplier master and open purchase orders is documented and approved by Customer’s IT Director; (c) the security and access model has been reviewed by Customer’s information security team; and (d) the project plan has been approved by the steering committee.',
    ),
    sub(
      'A.4',
      'Milestone M2: Accounts payable automation live at Dallas headquarters',
      'Target date: March 13, 2026. Fee on acceptance: $63,000.',
      'Gently shall configure and deploy invoice ingestion from Customer’s accounts payable mailbox, field extraction, two-way and three-way matching against purchase orders and receipts, exception routing, approval workflows and payment scheduling for the Dallas accounts payable team, integrated with Customer’s ERP.',
      'Acceptance criteria: (a) field-level extraction accuracy of at least ninety-seven percent (97%) on a Customer-supplied test set of at least five hundred (500) invoices; (b) at least sixty percent (60%) of in-scope invoices processed without manual intervention during a four (4) week parallel run; (c) all matching exceptions routed to the correct approver with the supporting evidence; (d) bidirectional ERP integration functioning in the production environment; and (e) successful completion of user acceptance testing with no open Severity 1 or Severity 2 defects.',
    ),
    sub(
      'A.5',
      'Milestone M3: Procurement agents',
      'Target date: June 12, 2026. Fee on acceptance: $54,000.',
      'Gently shall configure and deploy the procurement intake agent, the contract intelligence agent (clause extraction and question answering with citations), the invoice contract-compliance agent, approval routing and the agent audit log, and shall load the contracts in scope identified in the M1 plan.',
      'Acceptance criteria: (a) at least one hundred fifty (150) in-scope contracts loaded, with extracted clauses linked to their source text; (b) clause extraction accuracy of at least ninety-five percent (95%) on a validation sample of twenty-five (25) contracts selected by Customer; (c) every invoice processed in the accounts payable module is checked against the applicable contract terms and each exception cites the clause on which it relies; (d) intake requests are classified, checked for existing contracts and routed according to Customer’s approval policy; and (e) every agent action is recorded in the audit log with its reasoning.',
    ),
    sub(
      'A.6',
      'Milestone M4: Site rollout and handover',
      'Target date: September 11, 2026. Fee on acceptance: $36,000.',
      'Gently shall roll out the Platform to the Dallas, Phoenix and Hayward distribution centers and the Dallas panel shop, train Customer’s users and administrators, deliver administrator runbooks, provide thirty (30) days of hypercare support, and hand over day-to-day administration to Customer’s team.',
      'Acceptance criteria: (a) all five (5) in-scope locations are live; (b) at least ninety percent (90%) of identified users have completed role-based training; (c) administrator runbooks have been delivered and walked through with Customer’s team; (d) the thirty (30) day hypercare period has ended with no open Severity 1 or Severity 2 defects; and (e) Customer has confirmed in writing that the handover checklist is complete.',
    ),
    sub(
      'A.7',
      'Fixed fee and invoicing',
      'The fee for SOW #1 is a fixed fee of One Hundred Eighty Thousand Dollars ($180,000), which includes all of Gently’s expenses. Gently shall invoice the fee on Customer’s acceptance of each milestone as follows: M1, $27,000 (15%); M2, $63,000 (35%); M3, $54,000 (30%); and M4, $36,000 (20%). Invoices are payable under Section 4.2 of the MSA. The fixed fee is not subject to adjustment except by a signed change order.',
    ),
    sub(
      'A.8',
      'Acceptance procedure',
      'When Gently believes a milestone is complete it shall notify Customer in writing and provide evidence that each acceptance criterion has been met. Customer shall have ten (10) business days to review and test the deliverables and to either accept the milestone in writing or give written notice of the criteria that have not been met. Gently shall correct the deficiencies promptly and resubmit, and the same procedure applies to each resubmission. A milestone is deemed accepted only if Customer fails to respond within five (5) business days after receiving a second written notice marked “Final Acceptance Request”. If a milestone is not accepted after the second resubmission, Customer may, in addition to its other remedies, terminate the SOW and receive a refund of fees paid for milestones that Customer has not accepted.',
    ),
    sub(
      'A.9',
      'Customer responsibilities',
      'Customer shall (a) provide timely access to its personnel, systems, test environments and documents; (b) make decisions and provide approvals within the periods in the project plan; (c) provide the test data sets required for acceptance; and (d) provide ERP administrator support for the interfaces in scope.',
    ),
    sub(
      'A.10',
      'Change control',
      'A party requesting a change to this SOW shall submit a written change request describing the change. Gently shall within five (5) business days provide an assessment of the effect on scope, schedule and fees. No change is effective, and Gently shall not perform or invoice for out-of-scope work, until a change order has been signed by both parties.',
    ),

    art(
      'B',
      'Service Level Agreement',
      'This Exhibit B sets out the service levels for the Platform. In this Exhibit B, “Monthly Fee” means one-twelfth (1/12) of the annual Platform Fees then in effect (Thirty Thousand Dollars ($30,000) during the Initial Term), and “Production Platform” means the production environment of the Platform through which Customer’s Authorized Users access the Services.',
      '“Downtime” means any minute, measured at one-minute intervals by Gently’s monitoring systems and verifiable by Customer, in which the Production Platform is not available to Authorized Users, or in which a core workflow (invoice ingestion, matching, approval routing or payment-file generation) cannot be performed. “Monthly Uptime Percentage” means the total minutes in a calendar month, less minutes of Scheduled Maintenance and less minutes of Downtime, divided by the total minutes in that month less minutes of Scheduled Maintenance, expressed as a percentage. Gently shall deliver a monthly availability report to Customer within five (5) business days after the end of each month.',
    ),
    sub(
      'B.1',
      'Availability commitment',
      'Gently shall make the Production Platform available with a Monthly Uptime Percentage of at least 99.9% in each calendar month (the “Uptime Commitment”). “Scheduled Maintenance” is excluded from the calculation of Downtime only if it does not exceed four (4) hours in the aggregate in any calendar month and Gently gives Customer at least seventy-two (72) hours’ prior written notice, and Gently shall schedule it outside the hours of 6:00 a.m. to 10:00 p.m. Central Time on business days wherever practicable. Any maintenance in excess of four (4) hours in a calendar month, or performed without the required notice, counts as Downtime.',
    ),
    sub(
      'B.2',
      'Support response times',
      'Gently shall respond to support requests submitted through the support portal, email or telephone hotline in accordance with the priorities below, measured from the time the request is logged.',
      '(a) Priority 1 (Critical): the Production Platform or a core workflow is unavailable or materially impaired and no workaround exists. Gently shall respond within one (1) hour, twenty-four (24) hours a day, seven (7) days a week, and shall provide a status update at least every two (2) hours until the issue is resolved or a workaround is in place.',
      '(b) Priority 2 (High): a significant function is impaired but a reasonable workaround exists. Gently shall respond within four (4) Business Hours.',
      '(c) Priority 3 (Normal): a minor issue, a cosmetic defect or a general question. Gently shall respond within one (1) Business Day.',
      '“Business Hours” means 8:00 a.m. to 6:00 p.m. Central Time on days other than Saturdays, Sundays and United States federal holidays, and a “Business Day” is a day that contains Business Hours.',
    ),
    sub(
      'B.3',
      'Service credits',
      'If the Monthly Uptime Percentage in any calendar month is below the Uptime Commitment, Customer is entitled to a service credit equal to the following percentage of the Monthly Fee for that month: (a) five percent (5%) of the Monthly Fee if the Monthly Uptime Percentage is below 99.9% but at least 99.5%; (b) ten percent (10%) of the Monthly Fee if it is below 99.5% but at least 99.0%; and (c) twenty-five percent (25%) of the Monthly Fee if it is below 99.0%.',
      'The total service credits for any calendar month are capped at twenty-five percent (25%) of the Monthly Fee. To receive a service credit, Customer must request it in writing within thirty (30) days after the end of the month in which the failure occurred, or within thirty (30) days after receipt of the availability report for that month, if later. Gently shall apply approved credits to the next invoice or, if no further invoice will be issued, pay them to Customer within thirty (30) days. Service credits are Customer’s sole monetary remedy for failure to meet the Uptime Commitment, as provided in Section 13.3 of the MSA.',
    ),
    sub(
      'B.4',
      'Chronic failure',
      'If the Monthly Uptime Percentage is below 99.0% in any three (3) calendar months of any rolling six (6) month period, Customer may terminate the Agreement and any SOW without penalty by giving written notice to Gently within sixty (60) days after the end of the third such month. On termination under this Section, Gently shall refund all prepaid Fees for the period after the effective date of termination, no early termination or other charge shall apply, and Section 11.6 of the MSA shall apply to transition assistance.',
    ),
    sub(
      'B.5',
      'Disaster recovery objectives',
      'Gently shall maintain a disaster recovery plan for the Platform with a recovery point objective (RPO) of one (1) hour and a recovery time objective (RTO) of four (4) hours. Gently shall store backups and maintain recovery environments only in the United States, shall test the plan at least once per year, and shall provide Customer with a summary of the results of each test within thirty (30) days after its completion.',
    ),

    art(
      'C',
      'Data Processing Agreement',
      'This Data Processing Agreement (“DPA”) forms part of the Agreement and applies to Gently’s processing of personal data contained in Customer Data on behalf of Customer. It applies in addition to Article 12 of the MSA and, in the event of conflict, prevails as stated in Section 1.3 of the MSA. Terms used in this DPA and not defined in the Agreement have the meanings given in applicable data protection laws, including, to the extent applicable, the California Consumer Privacy Act, other United States state privacy laws, and the EU General Data Protection Regulation.',
    ),
    sub(
      'C.1',
      'Roles and scope of processing',
      'Customer is the controller (or business) and Gently is the processor (or service provider) of personal data contained in Customer Data. Gently shall process personal data only on Customer’s documented instructions, for the purpose of providing the Services, for the duration of the Term. The categories of data subjects are Customer’s employees and Authorized Users and the business contacts of Customer’s suppliers and customers. The categories of personal data are business contact details, user credentials and activity logs, and payment and remittance details provided in supplier records and invoices. Customer shall not instruct Gently to process special categories of personal data through the Platform.',
    ),
    sub(
      'C.2',
      'Data residency',
      'Gently shall store and process Customer Data, including backups and disaster recovery replicas, exclusively in data centers located in the United States. Gently personnel and sub-processors located outside the United States shall not access Customer Data except for support purposes with Customer’s prior written consent in each instance. Where a transfer of personal data subject to the GDPR is made to Gently in the United States, the parties incorporate the Standard Contractual Clauses (Module Two, controller to processor), as completed by the details in this DPA.',
    ),
    sub(
      'C.3',
      'Sub-processors',
      'Customer authorizes Gently to engage the sub-processors listed in the Documentation as of the Effective Date, including its cloud hosting provider and its third-party model providers. Gently shall give Customer at least thirty (30) days’ prior written notice of any new or replacement sub-processor. Customer may object on reasonable data protection grounds within that period. If the parties cannot resolve the objection within thirty (30) days, Customer may terminate the affected Services without penalty and receive a refund of prepaid Fees for the period after termination. Gently shall bind each sub-processor by a written agreement imposing obligations no less protective than this DPA, including the restriction in Section 12.4 of the MSA, and remains liable for each sub-processor’s performance.',
    ),
    sub(
      'C.4',
      'Security measures',
      'Gently shall implement and maintain technical and organizational measures appropriate to the risk, including (a) encryption of Customer Data at rest using AES-256 or stronger and in transit using TLS 1.2 or higher; (b) logical separation of Customer Data from the data of other customers; (c) role-based access control, multi-factor authentication for all administrative access and the principle of least privilege; (d) logging and monitoring of access to Customer Data, with logs retained for at least twelve (12) months; (e) annual third-party penetration testing and remediation of critical findings within thirty (30) days; (f) background screening and confidentiality undertakings for personnel with access to Customer Data; and (g) a documented vulnerability management and incident response program.',
    ),
    sub(
      'C.5',
      'Personal data breach notification',
      'Gently shall notify Customer without undue delay, and in any event within forty-eight (48) hours after becoming aware of a Security Incident, by email to Customer’s designated security contact and by telephone. The notice shall describe, to the extent known, the nature of the incident, the categories and approximate volume of data and data subjects affected, the likely consequences and the measures taken or proposed. Gently shall supplement the notice as information becomes available, cooperate with Customer’s investigation, and shall not notify any regulator or third party of an incident that identifies Customer without Customer’s prior written approval, except as required by law.',
    ),
    sub(
      'C.6',
      'Audits and assessments',
      'Gently shall make available to Customer the SOC 2 Type II report required by Section 12.1 of the MSA and, on request, completed security questionnaires and relevant policies. In addition, Customer or an independent auditor it appoints may audit Gently’s compliance with this DPA once in any twelve (12) month period on at least thirty (30) days’ notice, and at any time following a Security Incident. Each party bears its own costs of an audit, except that Gently bears the reasonable costs of an audit that reveals a material breach of this DPA.',
    ),
    sub(
      'C.7',
      'Data subject requests and assistance',
      'Gently shall promptly notify Customer of any request received from a data subject relating to Customer Data and shall not respond to it except on Customer’s instructions. Gently shall provide reasonable assistance to Customer, at no additional charge, in responding to data subject requests, carrying out data protection impact assessments and consulting with regulators.',
    ),
    sub(
      'C.8',
      'Return and deletion',
      'Within sixty (60) days after expiration or termination of the Agreement, Gently shall return Customer Data to Customer in a standard, machine-readable format and shall then delete all copies of Customer Data from its systems within thirty (30) days, and from its backups within ninety (90) days, unless retention is required by law. Gently shall certify the deletion in writing within ten (10) days after completion.',
    ),
  ],
  signatures: {
    buyerSigner: 'Sam Whitfield',
    buyerTitle: 'Vice President, Finance',
    supplierSigner: 'Authorized signatory',
    supplierTitle: 'Chief Executive Officer',
    signedDate: '2025-10-27',
    envelopeId: 'E-7A3D91C4-52B8-4E0F-A6D1-3C9B84F20E57',
  },
};

const SA_CORV_2024_017: ContractDocument = {
  contractId: 'SA-CORV-2024-017',
  title: 'Supply Agreement: Copper Building Wire',
  subtitle:
    '1/0 AWG THHN/THWN-2 copper building wire supply for distribution, cut-to-length and panel manufacturing',
  buyer: BUYER,
  supplier: {
    name: 'Corvane Wire & Cable, Inc.',
    location: 'Carrollton, Georgia',
  },
  effectiveDate: '2024-01-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of January 1, 2024 (the “Effective Date”) by and between Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Buyer”), and Corvane Wire & Cable, Inc., a Georgia corporation having its principal offices in Carrollton, Georgia (“Supplier”).',
    'Supplier manufactures THHN/THWN-2 copper building wire, which Buyer resells to electrical contractors and uses in its cut-to-length wire service and its UL 508A panel shop. Buyer wishes to purchase, and Supplier wishes to supply, the Product on the terms of this Agreement.',
    'NOW, THEREFORE, in consideration of the mutual promises in this Agreement, the parties agree as follows:',
  ],
  sections: [
    art('1', 'Definitions and Interpretation'),
    sub(
      '1.1',
      'Definitions',
      '“Affiliate” means any entity that directly or indirectly controls, is controlled by, or is under common control with a party. “Mill Test Report” or “MTR” means a document, signed by Supplier’s quality unit, that reports the copper rod certification and the conductor, insulation and dimensional test results required by the Specification for a Lot. “Contract Year” means each calendar year, or part of a calendar year, during the Term. “Delivery Point” has the meaning given in Section 5.1.',
      '“Lot” means a quantity of Product reels manufactured in a single production run from a single copper rod heat and given a unique lot number (a “reel lot”). “Net Purchases” means the aggregate amounts invoiced by Supplier and paid or payable by Buyer for Product in a Contract Year, net of credits, returns, rebates and taxes. “Product” means 1/0 AWG THHN/THWN-2 stranded copper building wire supplied on 500 foot reels, Buyer part number THHN-1/0-CU, meeting the Specification. “Specification” means Buyer’s specification SPC-THHN-1/0-CU as in effect on the Effective Date and as amended in writing by the parties. “Price” has the meaning given in Section 3.1.',
    ),
    sub(
      '1.2',
      'Interpretation',
      'Headings are for convenience only. “Including” means “including without limitation”. A reference to “days” means calendar days unless business days are specified, and a reference to a “week” means seven (7) consecutive days. Incoterms rules referred to in this Agreement are Incoterms 2020 published by the International Chamber of Commerce. All amounts are stated in United States dollars.',
    ),

    art('2', 'Term and Non-Exclusivity'),
    sub(
      '2.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until December 31, 2026 (the “Term”), unless terminated earlier under Article 12. This Agreement does not renew automatically. Any extension of the Term must be agreed in a written amendment signed by both parties.',
    ),
    sub(
      '2.2',
      'Renewal discussions',
      'The parties shall begin good-faith discussions about renewal or replacement of this Agreement no later than June 30, 2026. Buyer shall inform Supplier of its decision whether to renew, and on what volume allocation, no later than ninety (90) days before the end of the Term. Nothing in this Section obliges either party to renew.',
    ),
    sub(
      '2.3',
      'Non-exclusivity; forecasts',
      'This Agreement is non-exclusive. Buyer may purchase product similar to the Product from other suppliers and is not obliged to purchase any minimum quantity. Buyer’s current planning assumption is that it will purchase approximately seven million four hundred thousand (7,400,000) feet of Product in each Contract Year, but that figure is an estimate only and does not bind Buyer.',
    ),

    art('3', 'Price and Price Adjustment'),
    sub(
      '3.1',
      'Pricing',
      'The price for the Product is Four Hundred Twelve Dollars ($412.00) per 100 feet (the “Price”). The Price is fixed and will not be increased for any reason through December 31, 2025.',
    ),
    sub(
      '3.2',
      'Scope of the Price',
      'The Price includes the cost of manufacturing, testing, reeling, packaging and labeling the Product, the MTR and other documentation, and delivery to the Delivery Point in accordance with Article 5. The Price is not subject to any surcharge, including any raw-material, energy, fuel, currency or inflation surcharge, other than the annual adjustment under Section 3.3, unless the parties agree otherwise in a written amendment under Section 13.6.',
    ),
    sub(
      '3.3',
      'Annual price adjustment',
      'With effect from January 1, 2026, the Price shall be adjusted once per Contract Year by the percentage change over the preceding twelve (12) months in the average daily settlement price of front-month high-grade copper futures traded on the COMEX exchange in New York, as measured at September 30 of the preceding Contract Year (the “Index”), provided that no adjustment shall exceed an increase of three percent (3%) of the Price in effect on the preceding December 31 (the “Cap”). If the Index change is negative the Price shall be reduced by the same percentage. The maximum Price that Supplier may charge for the Contract Year beginning January 1, 2026 is therefore $424.36 per 100 feet, being $412.00 increased by three percent (3%).',
      'Supplier shall notify Buyer in writing of any adjustment, with the Index data and its calculation, no later than October 31 of the preceding Contract Year. An adjustment of which Buyer has not been notified by that date does not take effect. Supplier shall not invoice any amount in excess of the Price then in effect, and Buyer may reject or short-pay any invoice line that exceeds it.',
    ),
    sub(
      '3.4',
      'Most favored customer',
      'Supplier represents that the Price is no less favorable than the price it charges any other customer for substantially similar product in comparable volumes and on comparable terms. If Supplier offers a lower price to any such customer during the Term, Supplier shall promptly extend the same price to Buyer with effect from the date it was first offered.',
    ),
    sub(
      '3.5',
      'Volume rebate',
      'Supplier shall pay Buyer a volume rebate equal to two percent (2%) of Net Purchases in each Contract Year in excess of Twenty-Five Million Dollars ($25,000,000). The rebate accrues on each purchase that causes cumulative Net Purchases in the Contract Year to exceed that threshold and shall be credited to Buyer’s account quarterly in arrears, within thirty (30) days after the end of each calendar quarter, by credit note applied against the next invoice. Supplier shall provide a written statement of Net Purchases and the rebate accrued with each credit.',
    ),
    sub(
      '3.6',
      'Currency',
      'All invoices shall be issued, and all payments shall be made, in United States dollars. Supplier bears all risk of changes in exchange rates.',
    ),

    art('4', 'Invoicing and Payment'),
    sub(
      '4.1',
      'Invoicing',
      'Supplier may invoice Buyer for each shipment on or after the date on which Buyer receives it at the Delivery Point. Each invoice shall reference this Agreement by its number, SA-CORV-2024-017, Buyer’s purchase order number, the reel lot numbers, the quantity delivered, the Price per 100 feet and the total, and shall be sent to Buyer’s accounts payable invoice intake address. Buyer is not required to pay an invoice that does not meet these requirements until Supplier corrects it.',
    ),
    sub(
      '4.2',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Any shorter payment term stated on an invoice, order acknowledgment, delivery note or other Supplier document has no effect.',
    ),
    sub(
      '4.3',
      'Taxes',
      'The Price excludes sales and use taxes, which shall be separately stated on invoices where applicable and paid by Buyer, unless a resale certificate or exemption applies. Supplier is responsible for taxes on its own income, and for all other taxes, duties and fees on or relating to the manufacture and sale of the Product, including any tariff or duty on imported copper rod or other inputs.',
    ),
    sub(
      '4.4',
      'Disputed invoices',
      'Buyer may withhold payment of any amount it disputes in good faith, provided that it notifies Supplier in writing of the reason within thirty (30) days after receipt of the invoice and pays the undisputed balance when due. The parties shall resolve the dispute promptly, and Supplier shall continue to perform while it is pending.',
    ),
    sub(
      '4.5',
      'Credits and set-off',
      'Buyer may set off against any amount payable to Supplier any rebate, credit, liquidated damages or other amount that Supplier owes to Buyer under this Agreement.',
    ),

    art('5', 'Delivery'),
    sub(
      '5.1',
      'Incoterms and delivery points',
      'Supplier shall deliver all Product DAP (Delivered at Place, Incoterms 2020) to Buyer’s distribution center in Dallas, Texas or, where the purchase order so specifies, to Buyer’s distribution center in Phoenix, Arizona (each a “Delivery Point”). Supplier shall bear all costs and risks of carriage, including freight, insurance, loading and any customs clearance of inputs, until the Product has been placed at Buyer’s disposal on the arriving means of transport, ready for unloading, at the Delivery Point.',
    ),
    sub(
      '5.2',
      'No separate freight or delivery charges',
      'No freight, insurance, handling, fuel, reel-handling or other delivery-related charge is payable by Buyer in addition to the Price, whether invoiced as a separate line item, an accessorial or a surcharge. Any premium transport that is necessary to recover a delay attributable to Supplier shall be at Supplier’s cost.',
    ),
    sub(
      '5.3',
      'Title and risk',
      'Risk of loss or damage to the Product passes to Buyer at the Delivery Point in accordance with DAP. Title to the Product passes to Buyer on delivery at the Delivery Point, free from liens and encumbrances.',
    ),
    sub(
      '5.4',
      'Packaging and labeling',
      'Supplier shall wind the Product on non-returnable reels of five hundred (500) feet, protect each reel with wrap or a carton, ship it on pallets that meet Buyer’s trailer-loading requirements, and mark each reel with the Product name, the UL listing mark, Buyer’s part number, Lot number, footage and the jacket markings required by UL 83 and the National Electrical Code. Each reel shall contain a continuous length of not less than its labeled footage, and the sequential footage marking shall be accurate to within one percent (1%).',
    ),

    art('6', 'Orders, Lead Time and Delivery Performance'),
    sub(
      '6.1',
      'Forecasts and purchase orders',
      'Buyer shall provide a rolling twelve (12) month non-binding forecast each month. Buyer shall order the Product by written purchase orders, which may be issued electronically and shall state the quantity, delivery date and Delivery Point. Supplier shall accept every purchase order that conforms to this Agreement and does not exceed one hundred twenty percent (120%) of the most recent forecast for the month concerned.',
    ),
    sub(
      '6.2',
      'Lead time',
      'Supplier shall deliver the Product to the Delivery Point within eight (8) weeks after the date of Buyer’s purchase order (the “Lead Time”), or by any later date that Buyer specifies in the purchase order.',
    ),
    sub(
      '6.3',
      'Order acknowledgment',
      'Supplier shall acknowledge each purchase order in writing within three (3) business days after receipt, confirming the quantity, delivery date and Delivery Point. An acknowledgment that departs from the purchase order is effective only if Buyer accepts the departure in writing.',
    ),
    sub(
      '6.4',
      'Early and partial deliveries',
      'Buyer may refuse, or hold at Supplier’s risk and expense, any delivery made more than five (5) days before the delivery date. Partial shipments require Buyer’s prior written consent.',
    ),
    sub(
      '6.5',
      'Late delivery',
      'If Supplier delivers any Product after the delivery date in the purchase order, or after the Lead Time where no date is specified, Supplier shall pay Buyer liquidated damages equal to one and one-half percent (1.5%) of the value of the late line for each week or part of a week by which delivery is late, up to a maximum of ten percent (10%) of the value of that line. The parties agree that these damages are a reasonable pre-estimate of Buyer’s loss and not a penalty. They do not limit Buyer’s right to cancel the late line, or to recover premium freight under Section 5.2.',
    ),

    art('7', 'Continuity of Supply'),
    sub(
      '7.1',
      'Capacity',
      'Supplier shall maintain sufficient manufacturing capacity, raw materials and qualified personnel to supply Buyer’s forecast requirements under this Agreement, and shall promptly notify Buyer if it anticipates being unable to meet any purchase order or forecast.',
    ),
    sub(
      '7.2',
      'Business continuity',
      'Supplier shall maintain and, on request, share with Buyer a written business continuity plan covering its manufacturing plants and critical raw materials, including copper rod and insulation compounds, and shall test it at least annually. Supplier shall qualify at least one alternative source for each raw material that is sole-sourced, and shall disclose any single point of failure in its supply chain.',
    ),
    sub(
      '7.3',
      'Financial condition',
      'Supplier shall notify Buyer in writing within five (5) business days after any downgrade of its credit rating or credit score by a recognized rating agency or credit bureau, or any breach of a financial covenant under its principal financing arrangements. After such a notice Buyer may require Supplier to provide a plan, reasonably satisfactory to Buyer, to assure continuity of supply, which may include the holding of safety stock of the Product.',
    ),
    sub(
      '7.4',
      'Allocation',
      'If Supplier is unable to supply all of its customers’ requirements for the Product for any reason, Supplier shall allocate its available supply to Buyer in a proportion that is not less favorable than the proportion of its total sales of the Product that Buyer represented in the preceding twelve (12) months.',
    ),

    art('8', 'Quality and Specifications'),
    sub(
      '8.1',
      'Quality standards',
      'Supplier shall manufacture, test, store and ship the Product in accordance with the Specification, the UL listing requirements for the Product (including UL 83), its ISO 9001 quality management system, and the Quality Agreement QA-CORV-2024 between the parties (the “Quality Agreement”). Each Lot delivered shall have an incoming defect rate not exceeding five hundred (500) parts per million (PPM), measured in accordance with the Quality Agreement, and each shipment shall be accompanied by a mill test report for every reel lot.',
    ),
    sub(
      '8.2',
      'Mill test reports and release',
      'Supplier shall not ship a Lot until its quality unit has released it against the Specification and the MTR for the Lot reports passing results. Supplier shall retain samples of each Lot for the period stated in the Quality Agreement and shall make them available to Buyer on request.',
    ),
    sub(
      '8.3',
      'Inspection and rejection',
      'Buyer may inspect and test the Product on receipt and while it is in Buyer’s inventory or in cut-to-length processing. Buyer may reject any Lot that does not conform to the Specification or this Agreement by written notice to Supplier. Supplier shall, at its own cost and at Buyer’s option, replace the rejected Lot within ten (10) business days or credit or refund the price paid, and shall reimburse Buyer’s reasonable costs of inspection, return, re-spooling, customer credits, storage, disposal and scrap of the rejected Lot and of any Buyer materials made unusable by it.',
    ),
    sub(
      '8.4',
      'Change notification',
      'Supplier shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in the manufacturing plant, manufacturing process or source of copper rod or any other raw material used to make the Product. Supplier shall not implement any such change until Buyer has qualified it and approved it in writing and, where the change affects the Product’s UL listing, until the change is reflected in the UL listing file, and shall supply qualification samples at its own cost. Product made with an unapproved change is non-conforming.',
    ),
    sub(
      '8.5',
      'Recalls and corrective action',
      'If a Product defect gives rise to a recall, field action, UL notification or customer complaint investigation, Supplier shall cooperate fully and shall bear the reasonable costs that result from the defect, including the costs of notification, retrieval, replacement, re-spooling, disposal and investigation. Supplier shall respond to a supplier corrective action request within the period stated in the Quality Agreement.',
    ),
    sub(
      '8.6',
      'Audits',
      'Buyer or its representatives may audit Supplier’s manufacturing plant, quality system and records relating to the Product once in each Contract Year on ten (10) business days’ notice, and at any time for cause. Supplier shall correct any observation within the period agreed with Buyer.',
    ),

    art('9', 'Warranties'),
    sub(
      '9.1',
      'Product warranty',
      'Supplier warrants that the Product (a) conforms to the Specification and is free from defects in materials and workmanship; (b) is of merchantable quality and fit for its intended use as notified to Supplier; (c) is delivered with good title, free from liens; and (d) is manufactured, listed, marked and delivered in compliance with all applicable laws, codes and listings, including UL 83 and the National Electrical Code.',
    ),
    sub(
      '9.2',
      'Regulatory compliance',
      'Supplier shall comply with all laws applicable to the manufacture, labeling, listing and sale of the Product, including UL follow-up service requirements, RoHS and REACH, California Proposition 65, the Uyghur Forced Labor Prevention Act and other applicable laws on forced labor, anti-bribery and trade sanctions. Supplier shall provide the UL listing documentation, RoHS declarations and other regulatory documentation that Buyer reasonably requests.',
    ),
    sub(
      '9.3',
      'Remedies',
      'Buyer’s remedies for breach of Section 9.1 include those in Section 8.3. They are in addition to, and not in substitution for, any other right or remedy available to Buyer under this Agreement or by law.',
    ),

    art('10', 'Confidentiality and Intellectual Property'),
    sub(
      '10.1',
      'Confidentiality',
      'Each party shall keep confidential all non-public information of the other party that it receives in connection with this Agreement, including Buyer’s Specifications, forecasts and pricing and Supplier’s manufacturing processes, shall use it only to perform this Agreement, and shall disclose it only to employees, Affiliates and advisers who need to know it and are bound by equivalent obligations. These obligations do not apply to information that is public, already known, independently developed or lawfully received from a third party, and continue for five (5) years after the end of the Term.',
    ),
    sub(
      '10.2',
      'Intellectual property',
      'Each party retains its pre-existing intellectual property. Buyer owns the Specification and all information supplied by Buyer. Supplier owns its manufacturing processes, but grants Buyer a perpetual, non-exclusive license to use, cut to length and resell the Product, and any improvements to the Product made specifically for Buyer remain the property of Buyer. Supplier shall indemnify Buyer under Section 11.1 against third-party claims that the Product infringes intellectual property rights.',
    ),

    art('11', 'Indemnification, Liability and Insurance'),
    sub(
      '11.1',
      'Indemnification',
      'Supplier shall defend, indemnify and hold harmless Buyer and its Affiliates against all third-party claims, and resulting losses, damages and reasonable costs, arising from (a) any defect in the Product; (b) any claim that the Product infringes a patent, copyright or trade secret of a third party, except to the extent caused by Buyer’s Specification; (c) Supplier’s breach of Article 9; or (d) Supplier’s negligence or willful misconduct.',
    ),
    sub(
      '11.2',
      'Limitation of liability',
      'Except for the matters described in the next sentence, neither party shall be liable to the other for indirect, special or consequential damages, or for loss of profit. The limitation in the preceding sentence does not apply to Supplier’s indemnification obligations under Section 11.1, to recall costs, to breach of Article 10, to gross negligence or willful misconduct, or to Buyer’s payment obligations. Liquidated damages under Section 6.5 are not consequential damages.',
    ),
    sub(
      '11.3',
      'Insurance',
      'Supplier shall maintain, during the Term and for three (3) years thereafter, product liability insurance of not less than $10,000,000 per occurrence, commercial general liability insurance of not less than $5,000,000 per occurrence, and product recall insurance of not less than $5,000,000, with reputable insurers, and shall provide certificates on request.',
    ),

    art('12', 'Termination'),
    sub(
      '12.1',
      'Termination for cause',
      'Either party may terminate this Agreement by written notice if the other party commits a material breach that is not cured within thirty (30) days after written notice of it, or immediately if the other party becomes insolvent or subject to insolvency proceedings. Buyer may terminate immediately on written notice if Supplier breaches Section 8.4 or Article 9 and the breach affects the quality or UL listing status of the Product.',
    ),
    sub(
      '12.2',
      'Termination for convenience',
      'Buyer may terminate this Agreement for convenience, in whole or in respect of any Product, on one hundred eighty (180) days’ prior written notice to Supplier. Supplier may not terminate this Agreement for convenience. Buyer shall have no liability to Supplier in connection with a termination under this Section other than to pay for Product delivered and accepted before the termination date and for Product that Supplier has manufactured to Buyer’s firm purchase orders within the Lead Time.',
    ),
    sub(
      '12.3',
      'Effect of termination',
      'Expiration or termination does not affect accrued rights and liabilities. Supplier shall complete all firm purchase orders accepted before termination unless Buyer cancels them, and shall continue to supply Buyer for a transition period of up to six (6) months at the then-current Price if Buyer so requests. Articles 8, 9, 10 and 11 and Sections 3.5, 4.5 and 13.1 survive.',
    ),

    art('13', 'General Provisions'),
    sub(
      '13.1',
      'Governing law and disputes',
      'This Agreement is governed by the laws of the State of New York, without regard to its conflict of laws principles. The United Nations Convention on Contracts for the International Sale of Goods does not apply. The state and federal courts located in New York County, New York have exclusive jurisdiction over any dispute arising out of or related to this Agreement, and each party submits to their jurisdiction.',
    ),
    sub(
      '13.2',
      'Notices',
      'Notices under this Agreement must be in writing and delivered by hand, nationally recognized overnight courier or email with confirmation of transmission, addressed in the case of Buyer to its Senior Counsel, Commercial, at its Dallas, Texas offices, with a copy to its VP Supply Chain, and in the case of Supplier to its President at its Carrollton, Georgia offices, or to any other address a party designates by notice.',
    ),
    sub(
      '13.3',
      'Force majeure',
      'Neither party is liable for delay or failure to perform (other than a payment obligation) caused by an event beyond its reasonable control that could not have been prevented by reasonable precautions, including natural disaster, war, terrorism or governmental action, provided that it gives prompt notice and uses reasonable efforts to resume performance. A failure of Supplier’s own suppliers is not force majeure unless it is itself caused by such an event. If the event continues for more than sixty (60) days, Buyer may cancel the affected orders and purchase the Product from other sources without liability.',
    ),
    sub(
      '13.4',
      'Assignment',
      'Neither party may assign this Agreement without the other’s prior written consent, which shall not be unreasonably withheld, except that Buyer may assign it to an Affiliate or to a successor to the business to which it relates. A change of control of Supplier, or the sale of any of its manufacturing plants, requires Buyer’s prior written consent.',
    ),
    sub(
      '13.5',
      'Compliance and supplier code',
      'Supplier shall comply with Buyer’s Supplier Code of Conduct, as provided to Supplier, and with all applicable export control, sanctions and anti-corruption laws. Supplier shall promptly notify Buyer of any investigation or enforcement action that could affect its performance of this Agreement.',
    ),
    sub(
      '13.6',
      'Entire agreement and amendment',
      'This Agreement, including the Quality Agreement, is the entire agreement between the parties on its subject matter and supersedes all prior understandings. If there is a conflict between this Agreement and the Quality Agreement on a quality matter, the Quality Agreement prevails. This Agreement may be amended, and a right under it waived, only in writing signed by authorized representatives of both parties. The terms of any purchase order acknowledgment, invoice or other Supplier document that conflict with this Agreement have no effect.',
    ),
    sub(
      '13.7',
      'Counterparts and electronic signature',
      'This Agreement may be executed in counterparts and by electronic signature, each of which is an original and all of which together are one instrument.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Robert Haines',
    supplierTitle: 'President',
    signedDate: '2023-12-14',
    envelopeId: 'E-2B84F1D7-9C30-4A65-B7E2-61D5A0C93F18',
  },
};

const QA_VITX_2023_004: ContractDocument = {
  contractId: 'QA-VITX-2023-004',
  title: 'Quality Agreement',
  subtitle:
    '800 A molded-case circuit breakers, supplied under Supply Agreement SA-VITX-2023-004',
  buyer: BUYER,
  supplier: {
    name: 'Vitrex Electric AG',
    location: 'Basel, Switzerland',
  },
  effectiveDate: '2023-07-01',
  recitals: [
    'This Quality Agreement (this “Agreement”) is entered into as of July 1, 2023 (the “Effective Date”) by and between Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Buyer”), and Vitrex Electric AG, a stock corporation organized under the laws of Switzerland having its registered office in Basel, Switzerland (“Supplier”).',
    'Supplier manufactures 800 A frame molded-case circuit breakers for supply to Buyer under Supply Agreement SA-VITX-2023-004 (the “Supply Agreement”). The Product is a UL-listed circuit protection device that Buyer resells to electrical contractors and builds into the control panels made in its UL 508A panel shop, and Buyer is responsible to its customers, to UL and to authorities having jurisdiction for the listing status of what it sells and assembles.',
    'The parties therefore wish to set out their respective responsibilities for the quality of the Product, as follows:',
  ],
  sections: [
    art('1', 'Purpose and Scope'),
    sub(
      '1.1',
      'Purpose',
      'This Agreement defines the quality, regulatory and change control requirements that Supplier shall meet in manufacturing, testing, storing and shipping the Product, and the way in which the parties will cooperate to maintain and improve the quality of the Product.',
    ),
    sub(
      '1.2',
      'Scope',
      'This Agreement applies to the Product and to all factories, subcontractors and component and raw-material sources used in making, testing or packaging it, whether or not they are named in this Agreement. “Product” means 800 A frame molded-case circuit breaker, Buyer part number MCCB-800A, meeting the Specification.',
    ),
    sub(
      '1.3',
      'Relationship to the Supply Agreement',
      'This Agreement supplements the Supply Agreement. On any matter concerning the quality, testing, release, change control or listing status of the Product, this Agreement prevails over the Supply Agreement if they conflict. On commercial matters, including price, payment and delivery, the Supply Agreement prevails.',
    ),

    art('2', 'Definitions'),
    sub(
      '2.1',
      'Defined terms',
      '“Certificate of Conformance” or “CoC” means a document signed by Supplier’s quality unit that reports the results of the production tests required by the Specification for a Lot. “Critical Change” has the meaning given in Section 7.2. “Lot” means a quantity of Product made in a single production run and identified by a unique lot number. “Listing File” means the UL listing file and follow-up service procedure, or the equivalent file of another nationally recognized testing laboratory, that lists the Product and identifies the factories authorized to manufacture and label it. “Regulatory Authority” means UL and any other nationally recognized testing laboratory that lists the Product, and any governmental authority having jurisdiction over the Product or over Buyer’s products and assemblies that incorporate it.',
      '“Specification” means Buyer’s specification SPC-MCCB-800A, as in effect on the Effective Date and as amended in accordance with Section 4.1. “SCAR” means a supplier corrective action request issued by Buyer. “Quality Escape” means any Lot, or part of a Lot, that does not conform to the Specification or this Agreement and that was released by Supplier or shipped to Buyer.',
    ),

    art('3', 'Responsibilities'),
    sub(
      '3.1',
      'Quality units',
      'Each party shall maintain an independent quality unit with the authority and responsibility to approve or reject materials, procedures and specifications and to release or reject each Lot. Supplier’s quality unit shall not be subordinate to its production or sales functions.',
    ),
    sub(
      '3.2',
      'Quality contacts',
      'Each party shall designate a quality representative and a deputy as its primary contacts under this Agreement and shall notify the other of any change. Supplier’s quality representative shall be available to Buyer’s quality unit during business hours in both parties’ time zones.',
    ),
    sub(
      '3.3',
      'Certification and listing status',
      'Supplier shall maintain throughout the term of this Agreement (a) certification of its quality management system to ISO 9001, covering the manufacture of the Product; (b) a current UL listing for the Product, with every factory that makes, assembles or labels the Product identified in the Listing File, and shall provide Buyer with a copy of the listing and the follow-up service procedure on request; and (c) all permits, registrations and licenses required for the manufacture and export of the Product. Supplier shall notify Buyer within two (2) business days after receiving any notice of suspension, withdrawal or major non-conformity relating to any of them.',
    ),

    art('4', 'Specifications and Documentation'),
    sub(
      '4.1',
      'Specification',
      'The Product shall conform to the Specification. Only Buyer may amend the Specification, and an amendment is binding on Supplier when it has been agreed in writing by both parties’ quality units. Supplier shall not accept a specification from any other source that conflicts with it.',
    ),
    sub(
      '4.2',
      'Certificate of Conformance',
      'Supplier shall supply a CoC with each Lot, which shall report the actual production test result for every attribute in the Specification, identify the test methods used, bear the Lot number, the date code and the identification of the listed factory, and be signed by Supplier’s quality unit. The CoC shall be sent to Buyer before or with each shipment.',
    ),
    sub(
      '4.3',
      'Retention samples',
      'Supplier shall retain samples of each Lot, in sufficient quantity for two (2) complete test sequences, for at least twelve (12) months after the date code of the Lot and shall make them available to Buyer on request.',
    ),

    art('5', 'Manufacturing and Quality Systems'),
    sub(
      '5.1',
      'Quality management system',
      'Supplier shall manufacture the Product in accordance with its ISO 9001 certified quality management system, the UL follow-up service requirements for the Product, and documented, validated procedures. Supplier shall validate critical processes and test methods, shall calibrate test equipment, and shall maintain the validation status of equipment and facilities.',
    ),
    sub(
      '5.2',
      'Facilities and environment',
      'Supplier shall manufacture the Product only at a factory identified in the Listing File and shall maintain controlled assembly and storage conditions, including cleanliness, humidity and corrosion protection, appropriate to the Product. Supplier shall have documented procedures to prevent mix-ups between listed and unlisted production and the use of non-conforming or counterfeit components.',
    ),
    sub(
      '5.3',
      'Components and sub-tier suppliers',
      'Supplier shall purchase components and raw materials, including copper alloys, steel, molding compounds and trip units, only from sources approved under its supplier qualification procedure and, where the listing so requires, identified in the Listing File, shall test or verify each incoming lot against its specification, and shall flow down to its sub-tier suppliers the quality and change notification requirements of this Agreement that apply to them.',
    ),
    sub(
      '5.4',
      'Traceability',
      'Supplier shall maintain records that allow each Lot of Product to be traced to the component lots, equipment, personnel and test records used in making it, and each shipped quantity to its recipient, for the period in Section 11.1.',
    ),

    art('6', 'Testing, Release and Disposition'),
    sub(
      '6.1',
      'Lot release',
      'Supplier shall not ship a Lot until its quality unit has reviewed the build record and test results and released the Lot as conforming to the Specification. Supplier shall ship only released Lots.',
    ),
    sub(
      '6.2',
      'Buyer’s inspection',
      'Buyer may sample and test each Lot on receipt and while it is in Buyer’s inventory or panel shop, using the methods in the Specification. A Lot that fails any attribute in the Specification may be rejected by written notice to Supplier, and the provisions of Section 7.5 apply to the recovery of costs.',
    ),
    sub(
      '6.3',
      'Out-of-specification results',
      'If Supplier obtains an out-of-specification or atypical result on any Lot that has been or may be shipped to Buyer, Supplier shall notify Buyer within twenty-four (24) hours, shall conduct and document an investigation, and shall not ship the Lot or related Lots until the investigation is complete and Buyer’s quality unit has agreed to the disposition.',
    ),

    art('7', 'Change Control'),
    sub(
      '7.1',
      'General principle',
      'The Product is a UL-listed device, and the listings of the panels and assemblies that Buyer builds with it depend on the consistency of its design, materials and factory of manufacture. Supplier shall therefore manage all changes that could affect the identity, construction, performance or listing status of the Product under a documented change control procedure that is consistent with this Article 7.',
    ),
    sub(
      '7.2',
      'Notice and approval of critical changes',
      'Supplier shall not change the manufacturing site, any subcontractor used in the manufacture, testing or packaging of the Product, or the source of any critical component or raw material (each a “Critical Change”) without first giving Buyer at least one hundred eighty (180) days’ prior written notice and obtaining Buyer’s written approval. Supplier shall ship the Product only from factories that are identified in the Listing File and approved by Buyer. The notice shall describe the change, the reason for it, the Lots and quantities affected and Supplier’s assessment of its effect on the Product, and shall be accompanied by the supporting data.',
    ),
    sub(
      '7.3',
      'Other changes',
      'Supplier shall give Buyer at least ninety (90) days’ prior written notice of any other change in the manufacturing process, equipment, test methods, packaging or specifications of components or raw materials that could affect the Product, and shall not implement it until Buyer has confirmed in writing that the change is not a Critical Change or has approved it.',
    ),
    sub(
      '7.4',
      'Evaluation and qualification',
      'Buyer may require Supplier to supply qualification Lots and data, at Supplier’s cost, and may evaluate the change in its own panel-shop processes and its listings. Buyer shall endeavor to complete its evaluation within the notice period, but the notice period does not shorten if Supplier supplies incomplete data. Buyer’s approval of a change does not relieve Supplier of responsibility for the Product.',
    ),
    sub(
      '7.5',
      'Rejection of affected lots and recovery of costs',
      'If Supplier makes a Critical Change without complying with Section 7.2, Buyer may reject any Lot that was made, tested or packaged in connection with it (an “Affected Lot”), whether or not the Lot has been used, and Supplier shall promptly replace or credit each Affected Lot and reimburse Buyer for all costs that result, including the costs of investigation, quarantine, retesting, scrap, rework, replacement product, panel-shop downtime, customer holds, recalls, and relisting or re-certification of affected panels and assemblies. These remedies are in addition to Buyer’s other rights under the Supply Agreement.',
    ),
    sub(
      '7.6',
      'Change log',
      'Supplier shall maintain a log of all changes made in connection with the Product and shall provide it to Buyer at each annual quality review and on request.',
    ),

    art('8', 'Audits and Inspections'),
    sub(
      '8.1',
      'Audits by Buyer',
      'Buyer may audit Supplier’s factories, quality system and records relating to the Product, and those of its subcontractors, once in each calendar year on at least ten (10) business days’ notice, and at any time on forty-eight (48) hours’ notice following a Quality Escape or a suspected unapproved change.',
    ),
    sub(
      '8.2',
      'Regulatory inspections',
      'Supplier shall notify Buyer within two (2) business days of any audit, follow-up service finding or inquiry by a Regulatory Authority that relates to the Product, and shall provide Buyer with the relevant observations and its responses, redacted as necessary to protect other customers’ confidential information.',
    ),
    sub(
      '8.3',
      'Audit observations',
      'Supplier shall respond in writing to each audit observation within thirty (30) days, with a corrective action plan and implementation dates, and shall complete the corrective actions on the dates agreed.',
    ),

    art('9', 'Nonconformances and Corrective Action'),
    sub(
      '9.1',
      'Supplier corrective action requests',
      'When Buyer issues a SCAR, Supplier shall acknowledge it within two (2) business days and shall respond within ten (10) business days after receipt with a documented root cause investigation, containment actions, and a corrective and preventive action plan with owners and completion dates. Where Supplier cannot complete the investigation within that period it shall provide an interim response and Buyer’s quality unit shall agree a revised date in writing.',
    ),
    sub(
      '9.2',
      'Containment',
      'On notice of a Quality Escape, Supplier shall immediately quarantine all related Lots in its control, notify Buyer of any related Lots shipped to other customers that may affect the Product, and take the other containment actions that Buyer reasonably requests.',
    ),
    sub(
      '9.3',
      'Root cause method',
      'Supplier shall use a structured problem-solving method, such as eight disciplines (8D) or an equivalent, to identify the root cause of any nonconformance and shall provide the supporting evidence to Buyer on request.',
    ),
    sub(
      '9.4',
      'Effectiveness verification',
      'Supplier shall verify the effectiveness of each corrective action, over at least three (3) consecutive Lots or ninety (90) days, whichever is longer, and shall report the results to Buyer before the SCAR is closed.',
    ),
    sub(
      '9.5',
      'Escalation',
      'If Supplier fails to respond to a SCAR on time, or if a Quality Escape recurs, Buyer may place Supplier on a controlled shipping or enhanced inspection status, at Supplier’s cost, until Buyer’s quality unit is satisfied that the cause has been eliminated.',
    ),

    art('10', 'Complaints, Field Actions and Recalls'),
    sub(
      '10.1',
      'Complaint support',
      'Supplier shall provide the information, samples and technical support that Buyer requests to investigate complaints, field failures and incidents that may involve the Product, within the periods Buyer specifies having regard to its reporting and notification obligations.',
    ),
    sub(
      '10.2',
      'Field actions',
      'Buyer has sole authority to decide whether to initiate a recall, field correction or other field action involving the products and assemblies it sells or builds. Where a field action results from a nonconformance of the Product, Supplier shall reimburse Buyer’s reasonable costs of it.',
    ),
    sub(
      '10.3',
      'Supplier-identified issues',
      'Supplier shall notify Buyer within twenty-four (24) hours of becoming aware of any issue, wherever identified, that could affect the safety or performance of the Product, including any counterfeit component, listing deviation or test failure that affects a Lot already shipped.',
    ),

    art('11', 'Records and Retention'),
    sub(
      '11.1',
      'Retention',
      'Supplier shall retain build records, test records, CoCs, change records and distribution records for the Product for at least ten (10) years after the date code of the Lot to which they relate, or longer if required by applicable law, and shall make them available to Buyer and Regulatory Authorities on request.',
    ),
    sub(
      '11.2',
      'Data integrity',
      'Supplier shall maintain controls to ensure that quality records are attributable, legible, contemporaneous, original and accurate, and that electronic records and signatures meet the requirements of applicable standards and listings.',
    ),

    art('12', 'Term and General Provisions'),
    sub(
      '12.1',
      'Term',
      'This Agreement takes effect on the Effective Date and continues for as long as Supplier supplies the Product to Buyer, including under any extension or replacement of the Supply Agreement, unless the parties agree otherwise in writing. Articles 7, 9 and 10 and Section 11.1 survive its termination.',
    ),
    sub(
      '12.2',
      'Annual review',
      'The parties’ quality units shall meet at least once in each calendar year to review the performance of the Product, including Lot acceptance, nonconformances, SCARs and changes, and to agree improvement actions.',
    ),
    sub(
      '12.3',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York, without regard to its conflict of laws principles, and the courts located in New York County, New York have exclusive jurisdiction over any dispute arising out of it.',
    ),
    sub(
      '12.4',
      'Amendment and notices',
      'This Agreement may be amended only in writing signed by the quality heads of both parties. Notices shall be in writing and sent to the quality representative of the receiving party, with a copy, in the case of notices to Buyer, to its Senior Counsel, Commercial.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Dr. Lukas Brenner',
    supplierTitle: 'Chief Operating Officer',
    signedDate: '2023-06-22',
    envelopeId: 'E-5E19C0A3-7D42-4B8F-92A6-D4F03B71C8E5',
  },
};

const SA_LUMN_2025_002: ContractDocument = {
  contractId: 'SA-LUMN-2025-002',
  title: 'Supply Agreement: 50 HP Variable Frequency Drives',
  subtitle:
    'Supply of 50 HP, 480 V variable frequency drives for resale and for panel shop assemblies, delivered duty paid to Hayward DC',
  buyer: BUYER,
  supplier: {
    name: 'Lumen Drive Technology Co., Ltd.',
    location: 'Ningbo, China',
  },
  effectiveDate: '2025-07-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of July 1, 2025 (the “Effective Date”) by and between Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Buyer”), and Lumen Drive Technology Co., Ltd., a company organized under the laws of the People’s Republic of China having its registered office in Ningbo, China (“Supplier”).',
    'Supplier manufactures 50 HP variable frequency drives, which Buyer resells through its branches and builds into the control panels and retrofit kits assembled at its UL 508A panel shop in Dallas, Texas. Buyer wishes to purchase, and Supplier wishes to supply, the Product on the terms of this Agreement.',
    'NOW, THEREFORE, in consideration of the mutual promises in this Agreement, the parties agree as follows:',
  ],
  sections: [
    art('1', 'Definitions and Interpretation'),
    sub(
      '1.1',
      'Definitions',
      '“Affiliate” means any entity that directly or indirectly controls, is controlled by, or is under common control with a party. “Certificate of Conformance” means a document signed by Supplier’s quality unit that reports the results of the end-of-line tests required by the Specification for a Lot. “Delivery Point” means Buyer’s Hayward distribution center in Hayward, California (“Hayward DC”). “Lot” means a quantity of Product made in a single production run and identified by a unique lot and date code.',
      '“Product” means the 50 HP, 480 V, three-phase variable frequency drive, Buyer part number VFD-50HP-480, meeting the Specification. “Specification” means Buyer’s specification SPC-VFD-50HP-480 as in effect on the Effective Date and as amended in writing by the parties. “Safety Stock” has the meaning given in Section 7.1. “Price” has the meaning given in Section 4.1. “Contract Year” means each twelve (12) month period beginning on July 1.',
    ),
    sub(
      '1.2',
      'Interpretation',
      'Headings are for convenience only. “Including” means “including without limitation”. Incoterms rules referred to in this Agreement are Incoterms 2020 published by the International Chamber of Commerce. All amounts are stated in United States dollars.',
    ),

    art('2', 'Term'),
    sub(
      '2.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until June 30, 2027 (the “Term”), unless terminated earlier under Article 11. It may be extended only by a written amendment signed by both parties.',
    ),
    sub(
      '2.2',
      'Non-exclusivity and alternative sources',
      'This Agreement is non-exclusive. Supplier acknowledges that Buyer may qualify and purchase product similar to the Product from other suppliers at any time, and that Buyer is not obliged to purchase any minimum quantity. Supplier shall give reasonable cooperation, at no charge, to Buyer’s technical qualification of an alternative source, including by providing historical specification and test data.',
    ),

    art('3', 'Supply and Orders'),
    sub(
      '3.1',
      'Supply commitment',
      'Supplier shall supply, and shall have sufficient capacity and components to supply, all quantities of the Product that Buyer orders in accordance with this Article 3, up to one hundred twenty percent (120%) of Buyer’s forecast for each month. Buyer’s current planning assumption is that it will require approximately three thousand four hundred (3,400) units of Product in each Contract Year. That figure is an estimate only and does not bind Buyer.',
    ),
    sub(
      '3.2',
      'Forecasts and purchase orders',
      'Buyer shall provide a rolling twelve (12) month non-binding forecast each quarter and shall order Product by written purchase orders, which may be issued electronically and shall state the quantity and requested delivery date. Supplier shall acknowledge each purchase order within three (3) business days after receipt.',
    ),
    sub(
      '3.3',
      'Delivery performance',
      'Supplier shall deliver each order on the delivery date confirmed in its acknowledgment, in the full quantity ordered. Supplier shall notify Buyer immediately if it anticipates any delay, and shall at its own expense expedite shipment by air freight to recover any delay attributable to Supplier.',
    ),

    art('4', 'Price and Payment'),
    sub(
      '4.1',
      'Price',
      'The price for the Product is Five Thousand Five Hundred Dollars ($5,500.00) per drive, delivered duty paid to the Delivery Point (the “Price”). The Price is fixed for the Term and is not subject to adjustment for any reason, including changes in component, energy, labor, freight or currency costs.',
    ),
    sub(
      '4.2',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within sixty (60) days after receipt of a valid invoice (“Net 60”). Any shorter payment term on an invoice or other Supplier document has no effect.',
    ),
    sub(
      '4.3',
      'Invoicing',
      'Supplier may invoice each shipment when it has been delivered to the Delivery Point. Each invoice shall reference this Agreement by its number, SA-LUMN-2025-002, Buyer’s purchase order number, the Lot numbers, the quantity delivered and the Price per drive, and shall be sent to Buyer’s accounts payable invoice intake address. An invoice shall contain no line items other than the Product at the Price, and any other charge on an invoice is invalid unless it has been agreed in a written amendment under Section 12.5.',
    ),
    sub(
      '4.4',
      'Disputed invoices',
      'Buyer may withhold payment of any amount that it disputes in good faith, provided that it notifies Supplier in writing of the reason within thirty (30) days after receipt of the invoice and pays the undisputed balance when due. Supplier shall continue to perform while the dispute is pending.',
    ),
    sub(
      '4.5',
      'Currency and taxes',
      'All invoices shall be issued, and all payments made, in United States dollars. The Price is inclusive of all taxes and charges other than state and local sales and use taxes in the United States, which Buyer shall pay where applicable and which Supplier shall separately state on invoices.',
    ),

    art('5', 'Delivery, Duties and Tariffs'),
    sub(
      '5.1',
      'Incoterms',
      'Supplier shall deliver all Product DDP (Delivered Duty Paid, Incoterms 2020) to Buyer’s Hayward DC in Hayward, California, or to Buyer’s panel shop in Dallas, Texas, as stated in each purchase order. Supplier shall bear all costs and risks of carriage, including freight, insurance, export and import clearance and customs brokerage, until the Product has been placed at Buyer’s disposal on the arriving means of transport, cleared for import and ready for unloading, at the Delivery Point.',
    ),
    sub(
      '5.2',
      'Packaging and transport',
      'Supplier shall pack the Product in export-grade cartons on pallets that protect each drive from moisture, vibration and electrostatic discharge, shall ship it under the storage and transport conditions in the Specification, and shall label each carton with the Product name, Buyer’s part number, serial number, Lot and date code, UL listing mark and country of origin. Risk of loss passes to Buyer at the Delivery Point, and title passes on delivery.',
    ),
    sub(
      '5.3',
      'Duties, tariffs and import taxes',
      'All duties, tariffs, import taxes and other charges imposed on the importation of the Product into the United States, whenever imposed and including any new or increased charge introduced after the Effective Date (for example, an additional duty under Section 232 or Section 301), are borne by Supplier and are included in the Price. Supplier shall not invoice Buyer for any such amount, whether as a surcharge, pass-through, adjustment or otherwise, and no change in any such charge entitles Supplier to adjust the Price.',
    ),
    sub(
      '5.4',
      'Customs compliance',
      'Supplier shall act as, or shall appoint a customs broker to act as, the importer of record for each shipment and shall be responsible for the accuracy of all customs declarations, classification, valuation and country-of-origin statements. Supplier shall provide Buyer, on request, with the entry documentation, country-of-origin certificates and supply chain records that Buyer reasonably needs to demonstrate compliance with applicable import laws, including laws on forced labor such as the Uyghur Forced Labor Prevention Act.',
    ),

    art('6', 'Quality and Specifications'),
    sub(
      '6.1',
      'Quality standards',
      'Supplier shall manufacture, test, store and ship the Product in accordance with the Specification, its ISO 9001 certified quality management system, and the Quality Agreement between the parties in effect from time to time. Supplier shall maintain the UL listing (UL 508C and UL 61800-5-1) and the CE marking required for the Product, shall keep every factory at which the Product is made in the UL listing file, and shall provide Buyer with a copy of its UL follow-up service report on request.',
    ),
    sub(
      '6.2',
      'Certificate of Conformance',
      'Each shipment shall be accompanied by a Certificate of Conformance for every Lot, which reports the actual end-of-line test result for each attribute in the Specification and is signed by Supplier’s quality unit. Supplier shall not ship a Lot until it has been released against the Specification.',
    ),
    sub(
      '6.3',
      'Inspection and rejection',
      'Buyer may test each Lot on receipt and during the warranty period and may reject any Lot that does not conform to the Specification by written notice to Supplier. Supplier shall, at Buyer’s option, replace the rejected Lot within fifteen (15) business days at its own cost, or credit or refund the price paid, and shall reimburse Buyer’s reasonable costs of testing, return freight and disposal.',
    ),
    sub(
      '6.4',
      'Change notification',
      'Supplier shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in the manufacturing site, manufacturing process, firmware or hardware revision, or source of any critical component (including power semiconductors, capacitors and control boards) used to make the Product, and shall not implement the change until Buyer has qualified it and approved it in writing.',
    ),
    sub(
      '6.5',
      'Audits',
      'Buyer may audit Supplier’s manufacturing site, quality system and records relating to the Product once in each Contract Year on ten (10) business days’ notice, and at any time for cause.',
    ),

    art('7', 'Continuity of Supply'),
    sub(
      '7.1',
      'Safety stock',
      'Supplier shall hold, at its own cost at its warehouse in Ningbo, China, a dedicated stock of finished Product, released against the Specification, equal to ninety (90) days of Buyer’s forecast requirements (the “Safety Stock”). Supplier shall keep the Safety Stock separate from other inventory, store it under the conditions required by the Specification, rotate it on a first-in, first-out basis so that it is replenished with current-revision Lots, and report its quantity to Buyer monthly. Buyer may inspect the Safety Stock on request, and Supplier shall ship Safety Stock against Buyer’s purchase orders on an expedited basis. Title to the Safety Stock remains with Supplier until delivery.',
    ),
    sub(
      '7.2',
      'Business continuity',
      'Supplier shall maintain a written business continuity plan that covers its manufacturing site, its critical raw materials and its export logistics, shall test it at least annually, and shall provide Buyer with a summary of it and of the results of each test on request.',
    ),
    sub(
      '7.3',
      'Allocation and supply interruption',
      'If Supplier is unable to supply all of its customers’ requirements for the Product, it shall allocate its available supply to Buyer in a proportion that is not less than Buyer’s proportion of Supplier’s total sales of the Product in the preceding twelve (12) months. If Supplier fails to supply the Product for more than thirty (30) days, Buyer may purchase the Product from other sources for as long as the failure continues, and Supplier shall reimburse the reasonable excess cost.',
    ),

    art('8', 'Warranties'),
    sub(
      '8.1',
      'Product warranty',
      'Supplier warrants that the Product (a) conforms to the Specification, carries the UL listing mark for which it is listed and is free from defects in material and workmanship; (b) is of merchantable quality; (c) is delivered with good title, free from liens; and (d) is manufactured, labeled, exported and imported in compliance with all applicable laws.',
    ),
    sub(
      '8.2',
      'Compliance',
      'Supplier shall comply with Buyer’s Supplier Code of Conduct, as provided to Supplier, and with all applicable laws, including laws on anti-bribery, trade sanctions, export controls, forced labor, conflict minerals and the protection of the environment. Supplier shall not use forced, prison or child labor in the manufacture of the Product or its raw materials.',
    ),

    art('9', 'Confidentiality'),
    sub(
      '9.1',
      'Confidentiality',
      'Each party shall keep confidential all non-public information of the other party that it receives in connection with this Agreement, shall use it only to perform this Agreement, and shall disclose it only to employees, Affiliates and advisers who need to know it and are bound by equivalent obligations. These obligations do not apply to information that is public, already known, independently developed or lawfully received from a third party, and continue for five (5) years after the end of the Term.',
    ),

    art('10', 'Indemnification and Liability'),
    sub(
      '10.1',
      'Indemnification',
      'Supplier shall defend, indemnify and hold harmless Buyer and its Affiliates against all third-party claims, and resulting losses and reasonable costs, arising from (a) any defect in the Product; (b) any claim that the Product infringes the intellectual property rights of a third party; (c) Supplier’s breach of Article 8; or (d) Supplier’s negligence or willful misconduct.',
    ),
    sub(
      '10.2',
      'Limitation of liability',
      'Neither party is liable to the other for indirect, special or consequential damages or for loss of profit, except that this limitation does not apply to Supplier’s indemnification obligations, to breach of Article 9, to Supplier’s obligations under Sections 5.3 and 7.3, or to gross negligence or willful misconduct.',
    ),
    sub(
      '10.3',
      'Insurance',
      'Supplier shall maintain, during the Term and for two (2) years thereafter, product liability insurance of not less than $10,000,000 per occurrence and cargo insurance covering the Product in transit to the Delivery Point, and shall provide certificates on request.',
    ),

    art('11', 'Termination'),
    sub(
      '11.1',
      'Termination for cause',
      'Either party may terminate this Agreement by written notice if the other party commits a material breach that is not cured within thirty (30) days after written notice of it, or immediately if the other party becomes insolvent. Buyer may terminate immediately on written notice if Supplier breaches Section 5.3 or Section 6.4.',
    ),
    sub(
      '11.2',
      'Termination for convenience',
      'Buyer may terminate this Agreement for convenience on one hundred twenty (120) days’ prior written notice to Supplier. Supplier may not terminate this Agreement for convenience.',
    ),
    sub(
      '11.3',
      'Effect of termination',
      'On expiration or termination Supplier shall complete all firm purchase orders accepted before the termination date unless Buyer cancels them, and shall, if Buyer so requests, continue to supply the Product at the Price for up to six (6) months to allow Buyer to transition to another source. Articles 8, 9 and 10 and Sections 5.3 and 12.1 survive.',
    ),

    art('12', 'General Provisions'),
    sub(
      '12.1',
      'Governing law and arbitration',
      'This Agreement is governed by the laws of the State of New York, without regard to its conflict of laws principles, and the United Nations Convention on Contracts for the International Sale of Goods does not apply. Any dispute arising out of or relating to this Agreement shall be finally resolved by arbitration under the Rules of Arbitration of the International Chamber of Commerce by three (3) arbitrators, seated in New York, New York, and conducted in English. Either party may seek interim relief from a court of competent jurisdiction.',
    ),
    sub(
      '12.2',
      'Notices',
      'Notices must be in writing and delivered by hand, internationally recognized courier or email with confirmation of transmission, addressed in the case of Buyer to its Senior Counsel, Commercial, at its Dallas, Texas offices, and in the case of Supplier to its Chief Executive Officer at its Ningbo offices, or to any other address a party designates by notice.',
    ),
    sub(
      '12.3',
      'Force majeure',
      'Neither party is liable for delay or failure to perform (other than a payment obligation) caused by an event beyond its reasonable control, provided that it gives prompt notice and uses reasonable efforts to resume performance. A change in tariffs, duties or other import charges, or in the cost of raw materials, is not a force majeure event. If the event continues for more than sixty (60) days, Buyer may cancel the affected orders without liability.',
    ),
    sub(
      '12.4',
      'Assignment',
      'Neither party may assign this Agreement without the other’s prior written consent, which shall not be unreasonably withheld, except that Buyer may assign it to an Affiliate or to a successor to the business to which it relates.',
    ),
    sub(
      '12.5',
      'Entire agreement and amendment',
      'This Agreement, with the Quality Agreement, is the entire agreement between the parties on its subject matter and supersedes all prior understandings. It may be amended, and a right under it waived, only in writing signed by authorized representatives of both parties. The terms of any invoice, acknowledgment or other Supplier document that conflict with this Agreement have no effect.',
    ),
    sub(
      '12.6',
      'Counterparts and electronic signature',
      'This Agreement may be executed in counterparts and by electronic signature, each of which is an original and all of which together are one instrument.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Chen Liwei',
    supplierTitle: 'Chief Executive Officer',
    signedDate: '2025-06-18',
    envelopeId: 'E-C3A70E52-1B96-4F48-8D2A-9E5B17F6A024',
  },
};

const SA_AURE_2024_009: ContractDocument = {
  contractId: 'SA-AURE-2024-009',
  title: 'Supply Agreement: Pillar Pro LED High-Bay Fixtures',
  subtitle:
    'Private-label 150 W LED high-bay luminaires for resale under the Pillar Pro brand',
  buyer: BUYER,
  supplier: {
    name: 'Aurelia Lighting S.A. de C.V.',
    location: 'Monterrey, Nuevo Leon, Mexico',
  },
  effectiveDate: '2024-04-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of April 1, 2024 (the “Effective Date”) by and between Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Buyer”), and Aurelia Lighting S.A. de C.V., a variable-capital company incorporated in Mexico having its registered office in Monterrey, Nuevo Leon, Mexico (“Supplier”).',
    'Supplier manufactures LED high-bay luminaires, which Buyer sells under its Pillar Pro private-label brand through its branches and distribution centers. Buyer wishes to purchase, and Supplier wishes to supply, the Product on the terms of this Agreement.',
    'NOW, THEREFORE, in consideration of the mutual promises in this Agreement, the parties agree as follows:',
  ],
  sections: [
    art('1', 'Definitions and Interpretation'),
    sub(
      '1.1',
      'Definitions',
      '“Affiliate” means any entity that directly or indirectly controls, is controlled by, or is under common control with a party. “Delivery Point” has the meaning given in Section 5.1. “Lot” means a quantity of Product made in a single production run and identified by a unique lot and date code. “Price” has the meaning given in Section 3.1. “Product” means the 150 W, 5000K LED high-bay luminaire, Buyer part number LED-HB150-PP, in the Pillar Pro packaging and labeling versions notified by Buyer, meeting the Specification.',
      '“Specification” means Buyer’s specification SPC-LED-HB150 as in effect on the Effective Date and as amended in accordance with this Agreement. “Fixture” means one (1) complete luminaire of Product, with its driver, in its retail carton. “Contract Year” means each twelve (12) month period beginning on April 1.',
    ),
    sub(
      '1.2',
      'Interpretation',
      'Headings are for convenience only. “Including” means “including without limitation”. Incoterms rules referred to in this Agreement are Incoterms 2020 published by the International Chamber of Commerce. All amounts are stated in United States dollars.',
    ),

    art('2', 'Term and Volumes'),
    sub(
      '2.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until March 31, 2027 (the “Term”), unless terminated earlier under Article 12. It does not renew automatically, and any extension must be agreed in a written amendment signed by both parties.',
    ),
    sub(
      '2.2',
      'Share of requirements',
      'Buyer intends to purchase from Supplier approximately sixty-eight percent (68%) of its requirements for the Product, which Buyer currently estimates at 130,000 Fixtures in each Contract Year. The percentage is a planning assumption and not a commitment. Buyer may purchase product similar to the Product from other suppliers and is not obliged to purchase any minimum quantity.',
    ),
    sub(
      '2.3',
      'Forecasts',
      'Buyer shall provide a rolling twelve (12) month non-binding forecast each month. Supplier shall accept every purchase order that conforms to this Agreement and does not exceed one hundred fifteen percent (115%) of the most recent forecast for the month concerned.',
    ),

    art('3', 'Price'),
    sub(
      '3.1',
      'Price',
      'The price for the Product is Eighty-Eight Dollars and Seventy Cents ($88.70) per Fixture (the “Price”).',
    ),
    sub(
      '3.2',
      'Price fixed for the term',
      'The Price is fixed for the Term and is not subject to adjustment, including for changes in the cost of aluminium, LED packages, driver components, energy or labor, or in exchange rates. Supplier shall not impose any surcharge on the Product.',
    ),
    sub(
      '3.3',
      'Scope of the price',
      'The Price includes manufacturing, assembly, testing, private-label packaging and labeling, the Certificate of Conformance and delivery to the Delivery Point in accordance with Article 5. Packaging dies, printing plates and tooling for the Pillar Pro artwork and labeling versions notified by Buyer remain Buyer’s property and are included in the Price. Charges for new artwork or labeling versions shall be agreed in advance in writing and shall not exceed Supplier’s documented cost.',
    ),
    sub(
      '3.4',
      'Benchmarking',
      'Once in each Contract Year, Buyer may benchmark the Price against prices for comparable products with an independent third party. If the benchmark shows that the Price exceeds the market price for comparable volumes by more than five percent (5%), the parties shall negotiate in good faith an equitable reduction in the Price.',
    ),

    art('4', 'Payment and Commercial Commitments'),
    sub(
      '4.1',
      'Invoicing',
      'Supplier may invoice each shipment on or after the date on which Buyer receives it at the Delivery Point. Each invoice shall reference this Agreement by its number, SA-AURE-2024-009, Buyer’s purchase order number, the Lot numbers, the quantity delivered in Fixtures, the Price and the total, and shall be sent to Buyer’s accounts payable invoice intake address.',
    ),
    sub(
      '4.2',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”), subject to Section 4.3. Any different payment term stated on an invoice or other Supplier document has no effect.',
    ),
    sub(
      '4.3',
      'Early payment discount',
      'If Buyer pays an invoice within ten (10) days after its receipt of the valid invoice, Buyer may deduct from the invoice total a discount of two percent (2%) (“2/10 net 45”). A payment made within that period that includes the deduction is a payment in full of the invoice. Buyer is not obliged to take the discount, and the discount does not apply to any invoice amount that is disputed under Section 4.5.',
    ),
    sub(
      '4.4',
      'Taxes',
      'The Price excludes value-added tax and sales and use taxes, which Supplier shall separately state on invoices where applicable and Buyer shall pay unless a reverse charge or exemption applies. Supplier is responsible for taxes on its own income and for all other taxes, duties and fees relating to the manufacture and export of the Product.',
    ),
    sub(
      '4.5',
      'Disputed amounts',
      'Buyer may withhold payment of any amount that it disputes in good faith, provided that it notifies Supplier in writing of the reason within thirty (30) days after receipt of the invoice and pays the undisputed balance when due. Supplier shall continue to perform while the dispute is pending.',
    ),
    sub(
      '4.6',
      'Recyclable packaging roadmap',
      'Supplier shall, within ninety (90) days after the Effective Date, deliver to Buyer a written roadmap for the development of recyclable, plastic-free packaging for the Product that meets the Specification (the “Roadmap”), and shall update the Roadmap and report progress against it at least once in each Contract Year. The Roadmap shall target (a) the supply of qualification samples to Buyer by December 31, 2025; (b) completion of Buyer’s qualification by September 30, 2026; and (c) commercial supply of the recyclable packaging by March 31, 2027. Supplier shall offer the recyclable packaging at a price premium of not more than three percent (3%) over the Price then in effect for the Product, and Buyer shall give it first consideration in the sourcing of the Product for periods after the Term.',
    ),

    art('5', 'Delivery'),
    sub(
      '5.1',
      'Incoterms and delivery points',
      'Supplier shall deliver all Product DAP (Delivered at Place, Incoterms 2020) to Buyer’s Dallas DC in Dallas, Texas, Phoenix DC in Phoenix, Arizona or Hayward DC in Hayward, California, as the purchase order specifies (each a “Delivery Point”). Supplier shall bear all costs and risks of carriage, including freight, insurance and export clearance, until the Product has been placed at Buyer’s disposal on the arriving means of transport, ready for unloading, at the Delivery Point.',
    ),
    sub(
      '5.2',
      'No separate delivery charges',
      'No freight, insurance, handling, fuel or other delivery-related charge is payable by Buyer in addition to the Price. Premium transport needed to recover a delay attributable to Supplier shall be at Supplier’s cost.',
    ),
    sub(
      '5.3',
      'Title and risk',
      'Risk of loss or damage to the Product passes to Buyer at the Delivery Point in accordance with DAP, and title passes to Buyer on delivery at the Delivery Point, free from liens and encumbrances.',
    ),
    sub(
      '5.4',
      'Packaging and labeling',
      'Supplier shall pack each Fixture in its retail carton and the cartons on pallets in clean, dry, sealed packaging that protects them from moisture and damage in transit, and shall label each carton and pallet with the Product name, Buyer’s part number, Lot and date code, UL listing mark and labeling version.',
    ),

    art('6', 'Orders and Delivery Performance'),
    sub(
      '6.1',
      'Purchase orders and lead time',
      'Buyer shall order Product by written purchase orders, which may be issued electronically and shall state the quantity, labeling version, delivery date and Delivery Point. Supplier shall deliver the Product to the Delivery Point within six (6) weeks after the date of Buyer’s purchase order, or by any later date specified in the purchase order, and shall acknowledge each purchase order in writing within three (3) business days.',
    ),
    sub(
      '6.2',
      'On-time in-full performance',
      'Supplier shall deliver at least ninety-five percent (95%) of purchase order lines on time and in full, measured monthly. If Supplier’s performance falls below that level in any calendar quarter, Supplier shall deliver a written recovery plan within ten (10) business days and implement it promptly.',
    ),
    sub(
      '6.3',
      'Early and partial deliveries',
      'Buyer may refuse, or hold at Supplier’s risk and expense, any delivery made more than five (5) days before the delivery date. Partial shipments require Buyer’s prior written consent.',
    ),

    art('7', 'Quality and Regulatory'),
    sub(
      '7.1',
      'Quality standards',
      'Supplier shall manufacture, test, store and ship the Product in accordance with the Specification, its ISO 9001 certified quality management system, and the Quality Agreement between the parties in effect from time to time. The Product shall be UL listed (UL 1598) and DLC listed, shall comply with RoHS, and shall have lumen output, color temperature, driver efficiency, flicker and L70 lifetime within the limits of the Specification, and Supplier shall keep every factory at which the Product is made in the UL listing file.',
    ),
    sub(
      '7.2',
      'Certificate of Conformance',
      'Each shipment shall be accompanied by a Certificate of Conformance for every Lot, signed by Supplier’s quality unit, that reports the actual photometric and electrical test result for each attribute in the Specification. Supplier shall not ship a Lot until it has been released against the Specification.',
    ),
    sub(
      '7.3',
      'Inspection and rejection',
      'Buyer may inspect and test the Product on receipt and on use, and may reject any Lot that does not conform to the Specification by written notice to Supplier. Supplier shall, at Buyer’s option, replace the rejected Lot within ten (10) business days at its own cost or credit or refund the price paid, and shall reimburse Buyer’s reasonable costs of inspection, return freight, scrap, relabeling and rework caused by the nonconforming Lot.',
    ),
    sub(
      '7.4',
      'Change notification',
      'Supplier shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in the manufacturing site, the LED package or driver supplier, the aluminium housing alloy or supplier, the optics or the manufacturing process for the Product, and shall not implement the change until Buyer has qualified it and approved it in writing.',
    ),

    art('8', 'Supply Chain and Continuity'),
    sub(
      '8.1',
      'Capacity and continuity',
      'Supplier shall maintain capacity to supply Buyer’s forecast requirements and a written business continuity plan, which it shall test at least annually and summarize for Buyer on request. Supplier shall notify Buyer immediately of any event that may interrupt supply.',
    ),
    sub(
      '8.2',
      'Responsible sourcing',
      'Supplier shall source aluminium and other raw materials responsibly, shall disclose to Buyer on request the identity and location of its aluminium suppliers, and shall comply with Buyer’s Supplier Code of Conduct, as provided to Supplier. Supplier shall not use forced, prison or child labor in making the Product or its raw materials, and shall provide the supply chain tracing records that Buyer needs to demonstrate compliance with the Uyghur Forced Labor Prevention Act.',
    ),

    art('9', 'Warranties'),
    sub(
      '9.1',
      'Product warranty',
      'Supplier warrants that the Product (a) conforms to the Specification and is free from defects in material and workmanship; (b) is of merchantable quality and fit for installation in commercial and industrial high-bay applications under the National Electrical Code (NEC); (c) is delivered with good title, free from liens; and (d) is manufactured and delivered in compliance with all applicable laws.',
    ),

    art('10', 'Confidentiality and Intellectual Property'),
    sub(
      '10.1',
      'Confidentiality',
      'Each party shall keep confidential all non-public information of the other party that it receives in connection with this Agreement, including Buyer’s Pillar Pro artwork, Specifications, forecasts and pricing, shall use it only to perform this Agreement, and shall disclose it only to employees, Affiliates and advisers who need to know it and are bound by equivalent obligations. These obligations do not apply to information that is public, already known, independently developed or lawfully received from a third party, and continue for five (5) years after the end of the Term.',
    ),
    sub(
      '10.2',
      'Intellectual property',
      'Buyer owns the Pillar Pro brand and trademarks, the Specification, the artwork and all information that it provides to Supplier. Supplier retains its pre-existing manufacturing know-how and shall indemnify Buyer against third-party claims that the Product infringes intellectual property rights, except to the extent that the claim results from Buyer’s artwork or Specification.',
    ),

    art('11', 'Indemnification, Liability and Insurance'),
    sub(
      '11.1',
      'Indemnification',
      'Supplier shall defend, indemnify and hold harmless Buyer and its Affiliates against all third-party claims, and resulting losses and reasonable costs, arising from (a) any defect in the Product; (b) any claim of infringement described in Section 10.2; or (c) Supplier’s negligence or willful misconduct.',
    ),
    sub(
      '11.2',
      'Limitation of liability',
      'Neither party is liable to the other for indirect, special or consequential damages or for loss of profit, except that this limitation does not apply to Supplier’s indemnification obligations, to breach of Article 10, or to gross negligence or willful misconduct.',
    ),
    sub(
      '11.3',
      'Insurance',
      'Supplier shall maintain, during the Term and for two (2) years thereafter, product liability insurance of not less than $5,000,000 per occurrence and commercial general liability insurance of not less than $5,000,000 per occurrence, and shall provide certificates on request.',
    ),

    art('12', 'Termination'),
    sub(
      '12.1',
      'Termination for cause',
      'Either party may terminate this Agreement by written notice if the other party commits a material breach that is not cured within thirty (30) days after written notice of it, or immediately if the other party becomes insolvent.',
    ),
    sub(
      '12.2',
      'Termination for convenience',
      'Buyer may terminate this Agreement for convenience on one hundred twenty (120) days’ prior written notice to Supplier. Supplier may not terminate this Agreement for convenience.',
    ),
    sub(
      '12.3',
      'Effect of termination',
      'On expiration or termination Supplier shall complete all firm purchase orders accepted before the termination date unless Buyer cancels them. Articles 9, 10 and 11 and Section 13.1 survive.',
    ),

    art('13', 'General Provisions'),
    sub(
      '13.1',
      'Governing law and disputes',
      'This Agreement is governed by the laws of the State of New York, without regard to its conflict of laws principles. The United Nations Convention on Contracts for the International Sale of Goods does not apply. The state and federal courts located in New York County, New York have exclusive jurisdiction over any dispute arising out of or relating to this Agreement.',
    ),
    sub(
      '13.2',
      'Notices',
      'Notices must be in writing and delivered by hand, internationally recognized courier or email with confirmation of transmission, addressed in the case of Buyer to its Senior Counsel, Commercial, at its Dallas, Texas offices, and in the case of Supplier to its Managing Director at its Monterrey offices, or to any other address a party designates by notice.',
    ),
    sub(
      '13.3',
      'Force majeure',
      'Neither party is liable for delay or failure to perform (other than a payment obligation) caused by an event beyond its reasonable control, provided that it gives prompt notice and uses reasonable efforts to resume performance. If the event continues for more than sixty (60) days, Buyer may cancel the affected orders without liability.',
    ),
    sub(
      '13.4',
      'Assignment',
      'Neither party may assign this Agreement without the other’s prior written consent, which shall not be unreasonably withheld, except that Buyer may assign it to an Affiliate or to a successor to the business to which it relates.',
    ),
    sub(
      '13.5',
      'Entire agreement and amendment',
      'This Agreement, with the Quality Agreement, is the entire agreement between the parties on its subject matter and supersedes all prior understandings. It may be amended, and a right under it waived, only in writing signed by authorized representatives of both parties. The terms of any invoice, acknowledgment or other Supplier document that conflict with this Agreement have no effect.',
    ),
    sub(
      '13.6',
      'Counterparts and electronic signature',
      'This Agreement may be executed in counterparts and by electronic signature, each of which is an original and all of which together are one instrument.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Sofía Garza',
    supplierTitle: 'Director General',
    signedDate: '2024-03-15',
    envelopeId: 'E-8F26B4D9-0A71-4C35-B3E8-5D2C97A1E640',
  },
};

const MSA_HALB_2023_011: ContractDocument = {
  contractId: 'MSA-HALB-2023-011',
  title: 'Master Consulting Services Agreement',
  subtitle: 'Strategy, operations and supply chain advisory services',
  buyer: BUYER,
  supplier: {
    name: 'Halbrook & Partners LLP',
    location: 'Boston, Massachusetts',
  },
  effectiveDate: '2023-09-01',
  recitals: [
    'This Master Consulting Services Agreement (this “Agreement”) is entered into as of September 1, 2023 (the “Effective Date”) by and between Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Client”), and Halbrook & Partners LLP, a limited liability partnership organized under the laws of Massachusetts having its principal offices in Boston, Massachusetts (“Halbrook”).',
    'Halbrook provides strategy, operations and supply chain advisory services. Client is an industrial and electrical distributor with regional distribution centers and a branch network, and wishes to engage Halbrook from time to time to perform consulting services under statements of work, and Halbrook wishes to perform them, on the terms of this Agreement.',
    'NOW, THEREFORE, in consideration of the mutual promises in this Agreement, the parties agree as follows:',
  ],
  sections: [
    art('1', 'Definitions and Interpretation'),
    sub(
      '1.1',
      'Definitions',
      '“Affiliate” means any entity that directly or indirectly controls, is controlled by, or is under common control with a party. “Deliverables” means the reports, analyses, models and other work product that Halbrook is to deliver under an SOW. “Fees” means the professional fees payable for the Services. “Services” means the consulting services described in an SOW. “SOW” means a statement of work signed by both parties under this Agreement.',
    ),
    sub(
      '1.2',
      'Order of precedence',
      'If there is a conflict between the body of this Agreement and an SOW, this Agreement prevails unless the SOW expressly identifies the provision of this Agreement that it is intended to vary and is signed by Client’s Director, Indirect Procurement or an officer of Client.',
    ),

    art('2', 'Services and Statements of Work'),
    sub(
      '2.1',
      'Engagement',
      'Halbrook shall perform the Services described in each SOW in accordance with this Agreement. Client is not obliged to engage Halbrook for any minimum volume of Services, and nothing in this Agreement is exclusive.',
    ),
    sub(
      '2.2',
      'Contents of an SOW',
      'Each SOW shall describe the scope of the Services, the Deliverables and the timetable, identify the Halbrook personnel and their roles, include the rate card and the fee basis (time and materials, fixed fee or a combination), and, for a time and materials SOW, state a not-to-exceed amount.',
    ),
    sub(
      '2.3',
      'Change orders',
      'No change to the scope, personnel, roles, rates, timetable or fees of an SOW is effective unless it is documented in a written change order signed by Client’s Director, Indirect Procurement or another authorized representative of Client before the work concerned is performed. Halbrook shall not perform, or invoice for, any work outside the scope of an SOW without a signed change order.',
    ),

    art('3', 'Personnel'),
    sub(
      '3.1',
      'Staffing',
      'Halbrook shall provide appropriately qualified and experienced personnel to perform the Services. The personnel identified in an SOW shall be assigned for the duration of the SOW and may not be replaced without Client’s consent, except for reasons beyond Halbrook’s control, in which case the replacement shall be of at least equivalent qualifications and Halbrook shall bear the cost of transition.',
    ),
    sub(
      '3.2',
      'Subcontractors',
      'Halbrook shall not subcontract any part of the Services without Client’s prior written consent. Halbrook is responsible for the acts and omissions of any approved subcontractor as if they were its own.',
    ),
    sub(
      '3.3',
      'Conduct on Client premises',
      'Halbrook’s personnel shall comply with Client’s policies on safety, security, confidentiality, information technology use, anti-harassment and ethics when working on Client premises or systems, as notified to Halbrook.',
    ),

    art('4', 'Deliverables and Acceptance'),
    sub(
      '4.1',
      'Deliverables',
      'Halbrook shall deliver each Deliverable by the date in the SOW. Halbrook shall promptly notify Client if it anticipates a delay and shall propose a recovery plan.',
    ),
    sub(
      '4.2',
      'Acceptance',
      'Client may review each Deliverable for fifteen (15) business days after delivery and shall either accept it or notify Halbrook in writing of the respects in which it does not conform to the SOW. Halbrook shall correct the Deliverable promptly and without additional charge, and the review procedure shall apply again.',
    ),

    art('5', 'Standards of Performance'),
    sub(
      '5.1',
      'Standard of care',
      'Halbrook shall perform the Services with the skill, care and diligence expected of a leading professional services firm performing similar services, and in compliance with applicable laws.',
    ),
    sub(
      '5.2',
      'Conflicts of interest',
      'Halbrook represents that, as of the Effective Date, it has no conflict of interest that would prevent it from performing the Services. Halbrook shall promptly disclose to Client any actual or potential conflict of interest that arises, including any engagement for a supplier of Client in the area covered by an SOW, and shall not advise any supplier of Client on matters that relate to Client in a way that is adverse to Client.',
    ),
    sub(
      '5.3',
      'Independence of recommendations',
      'Halbrook shall not accept any fee, commission or other benefit from any third party in connection with a recommendation made to Client, and shall disclose to Client any financial interest it holds in any person that it recommends.',
    ),

    art('6', 'Fees and Payment'),
    sub(
      '6.1',
      'Rate card',
      'The hourly rates for each role of Halbrook personnel are those stated in the rate card of the applicable SOW, and are fixed for the duration of that SOW. No role, and no rate, that is not set out in the rate card of the applicable SOW may be billed. A role that is not on the rate card may be used and billed only if a written change order under Section 2.3 adds the role and its rate before the work is performed.',
    ),
    sub(
      '6.2',
      'Not-to-exceed amount',
      'Where an SOW is on a time and materials basis, the total of the Fees and expenses billed under it shall not exceed the not-to-exceed amount stated in the SOW. Halbrook shall not perform work that would cause that amount to be exceeded without a prior change order. Halbrook shall notify Client promptly when it has billed or incurred eighty percent (80%) of that amount. Client is not obliged to pay any amount in excess of the not-to-exceed amount that has not been approved by change order.',
    ),
    sub(
      '6.3',
      'Invoicing and payment terms',
      'Halbrook shall invoice monthly in arrears. Each invoice shall reference this Agreement by its number, MSA-HALB-2023-011, and the SOW number, and shall itemize the Fees by individual, role, hours and rate, with the cumulative amount billed against the not-to-exceed amount, and the expenses by category. Client shall pay each undisputed invoice within sixty (60) days after receipt of a valid invoice (“Net 60”).',
    ),
    sub(
      '6.4',
      'Expenses',
      'Client shall reimburse reasonable out-of-pocket travel and living expenses that are incurred in performing the Services in accordance with Client’s travel policy and have been approved in advance, at cost and without markup, provided that (a) the expenses invoiced under an SOW shall not exceed ten percent (10%) of the professional Fees invoiced under that SOW; and (b) each expense is supported by an itemized receipt. Expenses that are not supported by receipts, or that exceed the limit in this Section, are not reimbursable.',
    ),
    sub(
      '6.5',
      'Taxes',
      'The Fees are exclusive of sales, use and similar taxes, which Halbrook shall separately state on invoices where it is required to collect them. Each party is responsible for taxes on its own income and employees.',
    ),
    sub(
      '6.6',
      'Disputed invoices',
      'Client may withhold payment of any amount that it disputes in good faith, provided that it notifies Halbrook of the reason within thirty (30) days after receipt of the invoice and pays the undisputed balance when due.',
    ),
    sub(
      '6.7',
      'Records and audit',
      'Halbrook shall keep complete and accurate time and expense records for three (3) years after the end of each SOW. Client may audit those records, on reasonable notice, to verify amounts invoiced, and Halbrook shall promptly refund any overcharge identified.',
    ),

    art('7', 'Confidentiality'),
    sub(
      '7.1',
      'Confidentiality',
      'Each party shall keep confidential all non-public information of the other party that it receives in connection with this Agreement, including, in the case of Client, its supplier terms, pricing, forecasts, financial information and strategy, shall use it only to perform or receive the Services, and shall disclose it only to employees, Affiliates and advisers who need to know it and are bound by equivalent obligations. These obligations do not apply to information that is public, already known, independently developed or lawfully received from a third party, and continue for five (5) years after the end of the Agreement.',
    ),

    art('8', 'Intellectual Property'),
    sub(
      '8.1',
      'Deliverables',
      'On payment, Client owns all right, title and interest in the Deliverables, other than Halbrook’s pre-existing materials. Halbrook grants Client a perpetual, worldwide, non-exclusive, royalty-free license to use, copy and modify any pre-existing Halbrook materials incorporated in a Deliverable, as part of the Deliverable and for Client’s internal business purposes.',
    ),
    sub(
      '8.2',
      'Client materials',
      'Client retains all right, title and interest in its data and materials. Halbrook may use them only to perform the Services.',
    ),

    art('9', 'Warranties, Indemnification and Liability'),
    sub(
      '9.1',
      'Warranties',
      'Halbrook warrants that the Services will be performed in accordance with Section 5.1, that the Deliverables will conform to the SOW, and that the Deliverables will not infringe the intellectual property rights of any third party.',
    ),
    sub(
      '9.2',
      'Indemnification',
      'Halbrook shall defend and indemnify Client and its Affiliates against third-party claims arising from (a) a breach of Section 9.1 relating to infringement; (b) bodily injury or damage to property caused by the negligence of Halbrook’s personnel; or (c) Halbrook’s breach of Article 7.',
    ),
    sub(
      '9.3',
      'Limitation of liability',
      'Except for the matters described in the next sentence, neither party’s liability under this Agreement shall exceed the Fees paid or payable under the SOW concerned, and neither party is liable for indirect or consequential damages. These limits do not apply to indemnification obligations, breach of Article 7, gross negligence or willful misconduct.',
    ),

    art('10', 'Insurance'),
    sub(
      '10.1',
      'Required coverage',
      'Halbrook shall maintain, during the term of this Agreement and for two (2) years thereafter, professional liability insurance of not less than $10,000,000 per claim, commercial general liability insurance of not less than $2,000,000 per occurrence, cyber liability insurance of not less than $5,000,000 per claim, and workers’ compensation insurance as required by law, and shall provide certificates on request.',
    ),

    art('11', 'Term and Termination'),
    sub(
      '11.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until August 31, 2028, unless terminated earlier in accordance with this Article 11 or extended by written agreement. Termination or expiration of this Agreement does not terminate an SOW that is in effect, which continues on the terms of this Agreement until completed or terminated.',
    ),
    sub(
      '11.2',
      'Termination for convenience',
      'Client may terminate this Agreement or any SOW for convenience on thirty (30) days’ prior written notice to Halbrook. Client shall pay Fees for the Services properly performed, and expenses properly incurred, up to the effective date of termination.',
    ),
    sub(
      '11.3',
      'Termination for cause',
      'Either party may terminate this Agreement or any SOW by written notice if the other party commits a material breach that is not cured within thirty (30) days after written notice of it.',
    ),
    sub(
      '11.4',
      'Effect of termination',
      'On termination or expiration Halbrook shall promptly deliver to Client all Deliverables, in whatever state of completion, and return all Client materials. Articles 6 (as to amounts accrued), 7, 8 and 9 survive.',
    ),

    art('12', 'General Provisions'),
    sub(
      '12.1',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York, without regard to its conflict of laws principles, and the state and federal courts located in New York County, New York have exclusive jurisdiction over any dispute arising out of it.',
    ),
    sub(
      '12.2',
      'Independent contractor',
      'Halbrook is an independent contractor. Nothing in this Agreement creates an employment, partnership or agency relationship between the parties or between Client and any Halbrook personnel.',
    ),
    sub(
      '12.3',
      'Notices',
      'Notices must be in writing and delivered by hand, courier or email with confirmation of transmission, addressed in the case of Client to its Senior Counsel, Commercial, at its Dallas, Texas offices, with a copy to its Director, Indirect Procurement, and in the case of Halbrook to its Managing Partner at its Boston offices.',
    ),
    sub(
      '12.4',
      'Assignment',
      'Neither party may assign this Agreement without the other’s prior written consent, which shall not be unreasonably withheld, except that Client may assign it to an Affiliate or to a successor to the business to which it relates.',
    ),
    sub(
      '12.5',
      'Entire agreement and amendment',
      'This Agreement and each SOW are the entire agreement between the parties on their subject matter and supersede all prior understandings. They may be amended only in writing signed by authorized representatives of both parties.',
    ),
    sub(
      '12.6',
      'Counterparts and electronic signature',
      'This Agreement may be executed in counterparts and by electronic signature, each of which is an original and all of which together are one instrument.',
    ),
  ],
  signatures: {
    buyerSigner: 'Daniel Reyes',
    buyerTitle: 'Director, Indirect Procurement',
    supplierSigner: 'Thomas Ellery',
    supplierTitle: 'Managing Partner',
    signedDate: '2023-08-21',
    envelopeId: 'E-1D5C8A36-E247-4B90-A1F7-83B6D2E04C59',
  },
};

const SOW_HALB_2026_03: ContractDocument = {
  contractId: 'SOW-HALB-2026-03',
  title: 'Statement of Work: Tariff and Network Resilience Study',
  subtitle:
    'Issued under Master Consulting Services Agreement MSA-HALB-2023-011',
  buyer: BUYER,
  supplier: {
    name: 'Halbrook & Partners LLP',
    location: 'Boston, Massachusetts',
  },
  effectiveDate: '2026-05-01',
  recitals: [
    'This Statement of Work (this “SOW”) is entered into as of May 1, 2026 (the “SOW Effective Date”) by Pillar Industrial Supply, Inc. (“Client”) and Halbrook & Partners LLP (“Halbrook”) under the Master Consulting Services Agreement between them effective September 1, 2023, number MSA-HALB-2023-011 (the “MSA”). Capitalized terms not defined in this SOW have the meanings given in the MSA.',
    'Halbrook was selected to perform the Services described in this SOW through Client’s request for proposal RFP-2026-019. This SOW forms part of the MSA and is governed by its terms.',
  ],
  sections: [
    art('1', 'Background and Objectives'),
    sub(
      '1.1',
      'Background',
      'Changes in the tariff and trade environment have increased the delivered cost of the electrical products that Client buys from overseas and importing suppliers, including LED lighting, variable frequency drives, transformers and wire, and have raised questions about the resilience of the supplier network that serves Client’s Dallas, Phoenix and Hayward distribution centers, its Dallas panel shop and its branches to further changes in trade policy, port and freight disruption and single-source dependency. Client wishes to engage Halbrook to quantify its exposure and to recommend actions.',
    ),
    sub(
      '1.2',
      'Objectives',
      'The objectives of the engagement are to (a) quantify Client’s tariff exposure across its direct product spend by supplier, country of origin and tariff classification; (b) map and score the resilience of Client’s supplier network for its critical product categories; (c) evaluate mitigation options, including dual sourcing, regional sourcing, contract changes and customs strategies; and (d) deliver a prioritized, costed implementation roadmap.',
    ),

    art('2', 'Scope of Services'),
    sub(
      '2.1',
      'Workstreams',
      'Halbrook shall perform the following workstreams. Workstream 1, tariff exposure analysis: classify Client’s direct product spend by tariff code and origin, model current and scenario tariff rates, and quantify the effect on landed cost by product and distribution center. Workstream 2, network resilience assessment: map first- and second-tier suppliers for the critical product categories and the inbound freight lanes to the distribution centers, and score concentration, geographic, financial and regulatory risk. Workstream 3, mitigation options: develop and evaluate scenarios and their costs, timing and risks. Workstream 4, roadmap and recommendations: prepare an implementation roadmap and the business case for each recommended action, and present it to Client’s leadership.',
    ),
    sub(
      '2.2',
      'Exclusions',
      'This SOW does not include legal advice on customs or trade law, the negotiation of supplier contracts on Client’s behalf, the implementation of any recommendation, or the validation of Client’s customs filings. Client remains responsible for decisions made on the basis of Halbrook’s recommendations.',
    ),

    art('3', 'Deliverables and Timeline'),
    sub(
      '3.1',
      'Deliverables',
      'Halbrook shall deliver (a) a diagnostic report on tariff exposure and the supplier network by June 12, 2026; (b) an interim readout of the mitigation scenarios by August 28, 2026; and (c) a final report, roadmap and executive presentation by November 13, 2026.',
    ),
    sub(
      '3.2',
      'Term',
      'The Services under this SOW begin on May 1, 2026 and end on November 30, 2026, unless this SOW is extended by change order or terminated earlier under the MSA.',
    ),
    sub(
      '3.3',
      'Governance',
      'Client’s sponsor for this engagement is its Director, Indirect Procurement, and its business owner is its VP Supply Chain. Halbrook’s engagement partner shall meet with the sponsor every two (2) weeks and shall provide a written status report, including hours and fees to date, each month.',
    ),

    art('4', 'Staffing and Rate Card'),
    sub(
      '4.1',
      'Engagement team',
      'Halbrook shall staff the engagement with one (1) Partner, one (1) Principal, two (2) Consultants and two (2) Analysts, who shall be assigned for the duration of the SOW in accordance with Section 3.1 of the MSA.',
    ),
    sub(
      '4.2',
      'Rate card',
      'The hourly rates for the roles on this engagement are: Partner, $950 per hour; Principal, $750 per hour; Consultant, $420 per hour; and Analyst, $280 per hour. These rates are fixed for the term of this SOW. Any other role requires a written change order under Section 6.1 before the role is used or billed.',
    ),

    art('5', 'Fees and Expenses'),
    sub(
      '5.1',
      'Time and materials',
      'The Services are performed on a time and materials basis. Halbrook shall bill the hours actually worked by each individual at the rate for their role in Section 4.2, in increments of one-quarter (1/4) hour.',
    ),
    sub(
      '5.2',
      'Not-to-exceed amount',
      'The total of the Fees and expenses billed under this SOW shall not exceed Four Hundred Fifty Thousand Dollars ($450,000) (the “Not-to-Exceed Amount”). Halbrook shall not perform work that would cause the Not-to-Exceed Amount to be exceeded unless Client has approved an increase in a change order signed before that work is performed. Client is not obliged to pay any amount above the Not-to-Exceed Amount that has not been so approved.',
    ),
    sub(
      '5.3',
      'Expenses',
      'Expenses are reimbursable only as provided in Section 6.4 of the MSA, and may not exceed ten percent (10%) of the professional Fees invoiced under this SOW. Each expense must be supported by an itemized receipt.',
    ),
    sub(
      '5.4',
      'Invoicing and payment',
      'Halbrook shall invoice monthly in arrears in accordance with Section 6.3 of the MSA, itemizing hours by individual, role and rate and stating the cumulative amount billed against the Not-to-Exceed Amount. Invoices are payable on the terms of Section 6.3 of the MSA.',
    ),
    sub(
      '5.5',
      'Budget reporting',
      'Halbrook shall give Client a written forecast of the Fees and expenses to completion with each monthly status report, and shall notify Client immediately when eighty percent (80%) of the Not-to-Exceed Amount has been billed or incurred.',
    ),

    art('6', 'Change Orders'),
    sub(
      '6.1',
      'Procedure',
      'A change to the scope, timeline, staffing, roles, rates or Not-to-Exceed Amount of this SOW is effective only if documented in a written change order signed by Client’s Director, Indirect Procurement and by Halbrook’s engagement partner before the work concerned is performed, in accordance with Section 2.3 of the MSA.',
    ),

    art('7', 'Assumptions and Client Responsibilities'),
    sub(
      '7.1',
      'Assumptions',
      'The fees and timeline assume that Client will provide the data described in Section 7.2 within ten (10) business days after request, and that Client’s subject matter experts will be available for interviews and workshops as scheduled.',
    ),
    sub(
      '7.2',
      'Client responsibilities',
      'Client shall provide access to its purchasing, supplier master and landed cost data for the period under review, its customs entry data, and its supplier risk assessments, together with a project coordinator, and shall provide timely feedback on interim deliverables.',
    ),
  ],
  signatures: {
    buyerSigner: 'Daniel Reyes',
    buyerTitle: 'Director, Indirect Procurement',
    supplierSigner: 'Thomas Ellery',
    supplierTitle: 'Managing Partner',
    signedDate: '2026-04-24',
    envelopeId: 'E-94B0E7C1-3F68-4A25-8CD9-072A5E1B46F3',
  },
};

const MSA_TRAN_2025_006: ContractDocument = {
  contractId: 'MSA-TRAN-2025-006',
  title: 'Master Transportation Services Agreement',
  subtitle:
    'Truckload, less-than-truckload and intermodal freight services, including inbound freight to Pillar’s distribution centers',
  buyer: BUYER,
  supplier: {
    name: 'Transcontinental Freight Co.',
    location: 'Memphis, Tennessee',
  },
  effectiveDate: '2025-04-01',
  recitals: [
    'This Master Transportation Services Agreement (this “Agreement”) is entered into as of April 1, 2025 (the “Effective Date”) by and between Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Shipper”), and Transcontinental Freight Co., a Tennessee corporation having its principal offices in Memphis, Tennessee (“Carrier”).',
    'Carrier is a motor carrier licensed to transport freight in interstate commerce. Shipper distributes wire, cable, lighting, circuit protection, drives, conduit, enclosures and other electrical products from its Dallas, Phoenix and Hayward distribution centers and its Dallas panel shop, and wishes to engage Carrier to transport its freight, including inbound freight to those facilities, and Carrier wishes to provide those services, on the terms of this Agreement.',
    'NOW, THEREFORE, in consideration of the mutual promises in this Agreement, the parties agree as follows:',
  ],
  sections: [
    art('1', 'Definitions'),
    sub(
      '1.1',
      'Definitions',
      '“Accessorial Charge” means a charge for a service that is additional to the transportation of a Shipment from origin to destination and that is listed in Section 5.3. “Affiliate” means any entity that directly or indirectly controls, is controlled by, or is under common control with a party. “Bill of Lading” means the document that evidences the receipt of a Shipment by Carrier. “DOE Index” means the weekly United States average retail on-highway diesel fuel price per gallon published by the United States Department of Energy, Energy Information Administration.',
      '“Line Haul” means the charge for the transportation of a Shipment from origin to destination under the rates in Section 4.1, excluding the Fuel Surcharge and Accessorial Charges. “Shipment” means a load of freight tendered by Shipper to Carrier under a single Bill of Lading. “Lane Schedule” means the schedule of origins, destinations, equipment types and rates in effect under Section 4.1.',
    ),

    art('2', 'Services and Term'),
    sub(
      '2.1',
      'Services',
      'Carrier shall transport Shipments tendered by Shipper between the origins and destinations in the Lane Schedule, in accordance with this Agreement, applicable law and the routing and delivery instructions on the tender. The Lane Schedule includes inbound lanes from supplier plants and from Carrier’s Memphis terminal to Shipper’s Dallas distribution center, Phoenix distribution center and Hayward distribution center. Carrier shall provide the equipment, drivers, fuel and all other items needed to perform the transportation.',
    ),
    sub(
      '2.2',
      'Term',
      'This Agreement begins on the Effective Date and continues for three (3) years, until March 31, 2028, unless terminated earlier under Article 10, and may be extended by written agreement.',
    ),
    sub(
      '2.3',
      'Non-exclusivity and tenders',
      'This Agreement is non-exclusive and Shipper is not obliged to tender any minimum volume. Shipper shall tender Shipments electronically or by telephone. Carrier shall accept at least ninety percent (90%) of the tenders on each lane in each calendar quarter and shall accept or reject each tender within two (2) hours after receipt.',
    ),

    art('3', 'Carrier Obligations'),
    sub(
      '3.1',
      'Equipment and drivers',
      'Carrier shall provide clean, dry equipment in good repair, appropriate for the freight (including flatbed, step-deck and enclosed equipment suited to reeled wire, conduit, enclosures and transformers) and equipped with the straps, edge protectors and blocking needed to secure it, and drivers who are licensed, trained, drug-tested and compliant with federal hours-of-service rules.',
    ),
    sub(
      '3.2',
      'Regulatory compliance and safety',
      'Carrier shall maintain all operating authorities, registrations and permits required for the services, shall maintain a satisfactory safety rating issued by the Federal Motor Carrier Safety Administration, and shall comply with all applicable laws, including laws on safety, hazardous materials, cargo security and employment.',
    ),
    sub(
      '3.3',
      'Subcontracting and brokering',
      'Carrier shall perform all Shipments with its own equipment and drivers and shall not subcontract, broker or re-tender a Shipment without Shipper’s prior written consent in each instance.',
    ),
    sub(
      '3.4',
      'Visibility and security',
      'Carrier shall provide electronic shipment tracking with location updates at least every two (2) hours and shall notify Shipper immediately of any delay, accident, theft or damage. Carrier shall implement security measures that meet the requirements of the Customs-Trade Partnership Against Terrorism program, and shall seal trailers at origin.',
    ),

    art('4', 'Rates'),
    sub(
      '4.1',
      'Line haul rates',
      'Shipper shall pay Carrier for the transportation of each Shipment the Line Haul rate stated in the Lane Schedule for the origin, destination and equipment type. The Line Haul rate is inclusive of all costs of transportation, including driver and equipment costs, tolls, empty miles, permits and administrative costs, other than the Fuel Surcharge and Accessorial Charges that are expressly permitted by Article 5.',
    ),
    sub(
      '4.2',
      'Rate changes',
      'The Line Haul rates are fixed for twelve (12) months after the Effective Date. After that, either party may request a review once in each twelve (12) month period. No change in rates is effective until agreed in a written amendment signed by both parties, and in any event no increase in a lane’s Line Haul rate shall exceed three percent (3%) of the rate in effect during the preceding twelve (12) months.',
    ),
    sub(
      '4.3',
      'Minimum charges and consolidation',
      'No minimum charge applies other than the Line Haul rate. Where Shipper consolidates freight from more than one origin onto a Shipment, the Line Haul rate is that for the longest lane plus the stop-off charge in Section 5.3.',
    ),

    art('5', 'Fuel and Accessorial Charges'),
    sub(
      '5.1',
      'Charges payable',
      'The only charges payable by Shipper in addition to the Line Haul rate are the Fuel Surcharge under Section 5.2 and the Accessorial Charges that are permitted by Sections 5.3 and 5.4. Carrier shall not invoice any other charge, fee or surcharge, including any administrative, technology, security, environmental, peak season or equipment fee, unless Shipper has agreed to it in a written amendment.',
    ),
    sub(
      '5.2',
      'Fuel surcharge',
      'Carrier may charge a fuel surcharge on the Line Haul for each Shipment (the “Fuel Surcharge”), calculated as a percentage of the Line Haul by reference to the DOE Index published for the week in which the Shipment is picked up, in accordance with the following table:',
      '(a) DOE Index below $3.60 per gallon: 0%;',
      '(b) $3.60 to $3.79: 4%;',
      '(c) $3.80 to $3.99: 8%;',
      '(d) $4.00 to $4.19: 12%;',
      '(e) $4.20 to $4.39: 16%;',
      '(f) $4.40 to $4.59: 20%;',
      '(g) $4.60 to $4.79: 24%; and so on, adding four (4) percentage points for each additional $0.20 per gallon.',
      'Notwithstanding the table, the Fuel Surcharge on any Shipment shall not exceed eighteen percent (18%) of the Line Haul charge for that Shipment, regardless of the level of the DOE Index. The Fuel Surcharge shall be shown as a separate line on each invoice, with the DOE Index value and the percentage applied. No fuel surcharge applies to Accessorial Charges.',
    ),
    sub(
      '5.3',
      'Accessorial charges',
      'Carrier may charge the following Accessorial Charges, where the service was requested by Shipper in advance and actually performed: (a) lift-gate service, $75 per Shipment; (b) stop-off at an intermediate location, $125 per stop; (c) driver-assisted loading or unloading requested by Shipper, $95 per hour; and (d) re-delivery requested by Shipper after a failed delivery that was not caused by Carrier, $150. Accessorial Charges are not payable for services that Carrier or its personnel perform for their own convenience.',
    ),
    sub(
      '5.4',
      'Detention',
      'Detention is payable only if (a) the equipment is held at a Shipper facility for more than two (2) hours after the scheduled appointment time, and not as a result of Carrier’s late arrival; (b) Carrier submits the detention claim with proof of the delay signed by an authorized representative of the Shipper facility, showing the arrival, check-in and departure times, such as a signed gate log or Bill of Lading; and (c) Carrier submits the claim within thirty (30) days after the date of the Shipment. Detention is charged at seventy-five dollars ($75) per hour after the first two (2) hours, in fifteen (15) minute increments. Carrier shall not invoice detention without the signed proof, and Shipper is not obliged to pay any detention charge that is not supported by it.',
    ),
    sub(
      '5.5',
      'Other charges require approval',
      'Any charge that is not permitted by this Article 5, including any charge for layover, tarping, reweighing, driver wait time at a facility outside Shipper’s control or additional equipment, is payable only if Shipper has approved it in writing before the service was performed.',
    ),

    art('6', 'Invoicing and Payment'),
    sub(
      '6.1',
      'Invoices',
      'Carrier shall invoice each Shipment, or the Shipments in a calendar week, after delivery. Each invoice shall reference this Agreement by its number, MSA-TRAN-2025-006, and the Shipper’s purchase or shipment reference, shall attach a legible Bill of Lading and proof of delivery for each Shipment, and shall separately state the Line Haul, the Fuel Surcharge (with the DOE Index value and percentage applied) and each Accessorial Charge. Carrier shall submit each invoice within ninety (90) days after delivery. Shipper is not obliged to pay an invoice that is submitted after that period, or that does not meet these requirements.',
    ),
    sub(
      '6.2',
      'Payment terms',
      'Shipper shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Any shorter payment term stated on an invoice, Bill of Lading or other Carrier document has no effect.',
    ),
    sub(
      '6.3',
      'Disputed charges and audit',
      'Shipper may withhold payment of any amount that it disputes in good faith, or short-pay an invoice to remove it, provided that it notifies Carrier of the reason. Shipper or its freight audit provider may audit Carrier’s invoices and supporting records for three (3) years after payment, and Carrier shall promptly refund any overcharge identified.',
    ),

    art('7', 'Service Performance'),
    sub(
      '7.1',
      'On-time performance',
      'Carrier shall pick up at least ninety-five percent (95%) of Shipments within the scheduled pickup window and deliver at least ninety-five percent (95%) of Shipments by the scheduled delivery time, in each case measured monthly, excluding delays caused by Shipper or by events beyond Carrier’s reasonable control.',
    ),
    sub(
      '7.2',
      'Remedy',
      'If Carrier’s performance falls below the levels in Section 7.1 in any calendar quarter, Carrier shall deliver a written corrective action plan within ten (10) business days and implement it promptly. Shipper may reduce the volume it tenders to Carrier until performance is restored.',
    ),

    art('8', 'Cargo Liability and Claims'),
    sub(
      '8.1',
      'Liability for cargo',
      'Carrier is liable to Shipper for the actual loss of, or damage to, a Shipment while it is in Carrier’s possession or control, to the full extent provided by 49 U.S.C. Section 14706 (the Carmack Amendment), without limitation to a value per pound unless the parties agree otherwise in writing. Carrier is liable for loss or damage caused by moisture, shifting or improper securement of reeled wire, palletized devices, enclosures and other electrical equipment.',
    ),
    sub(
      '8.2',
      'Claims',
      'Shipper may file a claim within nine (9) months after delivery, or after a reasonable time for delivery has elapsed in the case of non-delivery. Carrier shall acknowledge a claim within thirty (30) days and pay, decline or make a firm compromise offer within sixty (60) days. Salvage shall be handled at Carrier’s cost and for Shipper’s account.',
    ),

    art('9', 'Insurance and Indemnification'),
    sub(
      '9.1',
      'Insurance',
      'Carrier shall maintain, during the term of this Agreement, automobile liability insurance of not less than $2,000,000 per accident, cargo insurance of not less than $250,000 per vehicle, commercial general liability insurance of not less than $2,000,000 per occurrence, and workers’ compensation insurance as required by law, with insurers rated A- VII or better, and shall provide certificates on request and thirty (30) days’ notice of cancellation.',
    ),
    sub(
      '9.2',
      'Indemnification',
      'Carrier shall defend, indemnify and hold harmless Shipper and its Affiliates against all third-party claims, and resulting losses and costs, arising from bodily injury, death or property damage caused by the acts or omissions of Carrier or its drivers, or from Carrier’s violation of law.',
    ),

    art('10', 'Termination'),
    sub(
      '10.1',
      'Termination for convenience',
      'Either party may terminate this Agreement for convenience on thirty (30) days’ prior written notice. Shipments tendered and accepted before the effective date of termination shall be completed on the terms of this Agreement.',
    ),
    sub(
      '10.2',
      'Termination for cause',
      'Either party may terminate this Agreement on written notice if the other party commits a material breach that is not cured within ten (10) business days after written notice of it. Shipper may terminate immediately if Carrier’s operating authority or insurance lapses or its safety rating falls below satisfactory.',
    ),

    art('11', 'General Provisions'),
    sub(
      '11.1',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York, without regard to its conflict of laws principles, and the state and federal courts located in New York County, New York have exclusive jurisdiction over any dispute arising out of it. To the extent permitted by law, the parties waive the rights and remedies conferred by 49 U.S.C. Section 14101(b) that are inconsistent with this Agreement.',
    ),
    sub(
      '11.2',
      'Notices',
      'Notices must be in writing and delivered by hand, courier or email with confirmation of transmission, addressed in the case of Shipper to its Senior Counsel, Commercial, at its Dallas, Texas offices, with a copy to its VP Supply Chain, and in the case of Carrier to its President at its Memphis offices.',
    ),
    sub(
      '11.3',
      'Force majeure',
      'Neither party is liable for delay or failure to perform (other than a payment obligation) caused by an event beyond its reasonable control, including severe weather and highway shutdowns, provided that it gives prompt notice and uses reasonable efforts to resume performance. Increases in the price of fuel or in the cost of labor, equipment or insurance are not force majeure events.',
    ),
    sub(
      '11.4',
      'Independent contractor',
      'Carrier is an independent contractor and has exclusive control of the manner and means of transportation. Nothing in this Agreement creates an employment, partnership or agency relationship.',
    ),
    sub(
      '11.5',
      'Entire agreement and amendment',
      'This Agreement, including the Lane Schedule, is the entire agreement between the parties on its subject matter and supersedes all prior understandings and any terms printed on Carrier’s Bills of Lading, invoices, tariffs or rules circulars. It may be amended only in writing signed by authorized representatives of both parties.',
    ),
    sub(
      '11.6',
      'Assignment and counterparts',
      'Neither party may assign this Agreement without the other’s prior written consent, which shall not be unreasonably withheld, except that Shipper may assign it to an Affiliate or to a successor to the business to which it relates. This Agreement may be executed in counterparts and by electronic signature.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Gregory Dunn',
    supplierTitle: 'President',
    signedDate: '2025-03-20',
    envelopeId: 'E-6A92D5F0-C418-4E73-B09A-1F8E37C52D64',
  },
};

const EA_STRT_2024_001: ContractDocument = {
  contractId: 'EA-STRT-2024-001',
  title: 'Enterprise Agreement for Cloud Services',
  subtitle: 'Committed-spend cloud infrastructure and platform services',
  buyer: BUYER,
  supplier: { name: 'Stratus Cloud Services', location: 'Seattle, Washington' },
  effectiveDate: '2024-02-01',
  recitals: [
    'This Enterprise Agreement for Cloud Services (this “Agreement”) is entered into as of February 1, 2024 (the “Effective Date”) by and between Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Customer”), and Stratus Cloud Services, a Washington corporation having its principal offices in Seattle, Washington (“Stratus”).',
    'Stratus provides cloud infrastructure and platform services. Customer operates an industrial and electrical distribution business and wishes to consolidate its use of those services for its ERP, e-commerce, warehouse and analytics systems under a single enterprise agreement in return for a committed annual spend and enterprise pricing.',
    'NOW, THEREFORE, in consideration of the mutual promises in this Agreement, the parties agree as follows:',
  ],
  sections: [
    art('1', 'Definitions'),
    sub(
      '1.1',
      'Definitions',
      '“Affiliate” means any entity that directly or indirectly controls, is controlled by, or is under common control with a party. “Annual Committed Spend” has the meaning given in Section 3.1. “Contract Year” means each twelve (12) month period beginning on February 1. “Customer Content” means all data and content that Customer or its users upload to or create in the Services. “Eligible Usage” means Customer’s consumption of the Services in a Contract Year, valued at the rates in the Price Schedule. “Monthly Availability” has the meaning given in Section 6.1.',
      '“Price Schedule” means the schedule of enterprise rates agreed by the parties and in effect under this Agreement. “Services” means the cloud infrastructure and platform services listed in the Price Schedule, together with the enterprise support plan described in Article 8. “Security Incident” means any unauthorized access to, or acquisition, disclosure, loss or alteration of, Customer Content in the possession or control of Stratus.',
    ),

    art('2', 'Services'),
    sub(
      '2.1',
      'Provision of services',
      'Stratus shall make the Services available to Customer and its Affiliates during the Term in accordance with this Agreement and its then-current service documentation. Customer and its Affiliates may use the Services for their internal business purposes, including the hosting of Customer’s ERP, e-commerce and warehouse management systems, product data platforms and analytics workloads.',
    ),
    sub(
      '2.2',
      'Accounts and Affiliates',
      'Customer may create and manage accounts under this Agreement for itself and its Affiliates. Eligible Usage of all such accounts counts toward the Annual Committed Spend, and Customer is responsible for the use of the Services under all accounts.',
    ),
    sub(
      '2.3',
      'Changes to services',
      'Stratus may update the Services, but shall not discontinue a Service that Customer is using, or materially reduce its functionality, without at least twelve (12) months’ prior written notice and a migration path to a functionally equivalent service at no additional cost to Customer.',
    ),

    art('3', 'Committed Spend and Fees'),
    sub(
      '3.1',
      'Annual committed spend',
      'Customer commits to spend not less than Twelve Million Dollars ($12,000,000) on the Services in each Contract Year (the “Annual Committed Spend”). The Services are priced at the enterprise rates in the Price Schedule, which are fixed for the Initial Term.',
    ),
    sub(
      '3.2',
      'Monthly billing',
      'Stratus shall invoice Customer monthly, in advance, in twelve (12) equal monthly installments, each being one-twelfth (1/12) of the Annual Committed Spend, or One Million Dollars ($1,000,000), which are applied as prepayment against Eligible Usage. Eligible Usage in excess of the Annual Committed Spend shall be invoiced monthly in arrears at the rates in the Price Schedule.',
    ),
    sub(
      '3.3',
      'Under-consumption and rollover',
      'If Eligible Usage in a Contract Year is less than the Annual Committed Spend, the unused balance does not entitle Customer to a refund, but up to ten percent (10%) of the Annual Committed Spend (One Million Two Hundred Thousand Dollars ($1,200,000)) may be carried forward and applied to Eligible Usage in the next Contract Year. Customer shall not owe any amount in addition to the monthly installments as a result of under-consumption.',
    ),
    sub(
      '3.4',
      'Commitment review',
      'The parties shall meet at least once in each Contract Year, and within thirty (30) days after Customer’s written request, to review Eligible Usage against the Annual Committed Spend, and shall discuss in good faith any rebalancing of the Services, workloads or commitment that would improve Customer’s use of the Services.',
    ),

    art('4', 'Payment'),
    sub(
      '4.1',
      'Invoices',
      'Each invoice shall reference this Agreement by its number, EA-STRT-2024-001, state the period concerned, itemize Eligible Usage by Service, show any service credit under Article 6, and be sent to Customer’s accounts payable invoice intake address.',
    ),
    sub(
      '4.2',
      'Payment terms',
      'Customer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”).',
    ),
    sub(
      '4.3',
      'Taxes',
      'The fees are exclusive of sales, use, value-added and similar taxes, which Stratus shall separately state on invoices and Customer shall pay unless it provides a valid exemption certificate.',
    ),
    sub(
      '4.4',
      'Disputed amounts',
      'Customer may withhold payment of any amount that it disputes in good faith, provided that it notifies Stratus of the reason within thirty (30) days after receipt of the invoice and pays the undisputed balance when due. Stratus shall not suspend the Services in respect of amounts disputed in good faith.',
    ),

    art('5', 'Term and Renewal'),
    sub(
      '5.1',
      'Initial term',
      'This Agreement begins on the Effective Date and continues for an initial term of three (3) years, ending on January 31, 2027 (the “Initial Term”).',
    ),
    sub(
      '5.2',
      'Automatic renewal',
      'At the end of the Initial Term this Agreement renews automatically for a renewal term of thirty-six (36) months (the “Renewal Term” and, together with the Initial Term, the “Term”), unless Customer gives Stratus written notice of non-renewal at least ninety (90) days before the end of the Initial Term. Accordingly, notice of non-renewal of the Initial Term must be delivered no later than November 2, 2026. A notice of non-renewal is effective only if it is in writing and delivered to Stratus in accordance with Section 11.2.',
    ),
    sub(
      '5.3',
      'Renewal commitment and pricing',
      'During the Renewal Term the Annual Committed Spend shall be the Annual Committed Spend in effect in the last Contract Year of the Initial Term, and the Services shall be priced at the enterprise rates in the Price Schedule then in effect, unless the parties agree otherwise in writing.',
    ),
    sub(
      '5.4',
      'Termination for cause',
      'Either party may terminate this Agreement by written notice if the other party materially breaches it and fails to cure the breach within thirty (30) days after written notice, or immediately if the other party becomes insolvent. If Customer terminates for Stratus’s uncured breach, Stratus shall refund prepaid fees for the period after termination.',
    ),
    sub(
      '5.5',
      'Early termination by Customer',
      'Customer may not terminate this Agreement for convenience during the Initial Term or the Renewal Term, except that Customer may terminate it on ninety (90) days’ notice on payment of the unpaid balance of the Annual Committed Spend for the then-current Contract Year.',
    ),

    art('6', 'Service Levels'),
    sub(
      '6.1',
      'Availability commitment',
      'Stratus shall make each production Service available to Customer with Monthly Availability of at least 99.95% in each calendar month. “Monthly Availability” means the total minutes in the month, less minutes of scheduled maintenance notified at least seven (7) days in advance (not to exceed four (4) hours in the month), less minutes of unavailability, divided by the total minutes in the month less those of scheduled maintenance, expressed as a percentage.',
    ),
    sub(
      '6.2',
      'Service credit',
      'If Monthly Availability in any calendar month is below 99.95%, Stratus shall credit Customer ten percent (10%) of the monthly fees for that month (being, at the Annual Committed Spend, $100,000).',
    ),
    sub(
      '6.3',
      'Automatic application',
      'Stratus shall calculate each service credit from its own monitoring records and apply it automatically to the next invoice issued after the end of the month concerned, without any claim or request by Customer. If no further invoice will be issued, Stratus shall pay the credit to Customer within thirty (30) days.',
    ),
    sub(
      '6.4',
      'Reporting',
      'Stratus shall publish and deliver to Customer, within ten (10) business days after the end of each month, a report of Monthly Availability for each production Service used by Customer, with the incidents that affected it.',
    ),

    art('7', 'Security and Data Protection'),
    sub(
      '7.1',
      'Security program',
      'Stratus shall maintain a comprehensive information security program with administrative, technical and physical safeguards, shall maintain ISO/IEC 27001 certification and an annual SOC 2 Type II report for the Services, and shall provide the current report to Customer on request.',
    ),
    sub(
      '7.2',
      'Customer content',
      'Customer owns all Customer Content. Stratus shall use Customer Content only to provide the Services to Customer and shall not access it, use it to train machine learning models, or disclose it, except as Customer instructs or as the law requires.',
    ),
    sub(
      '7.3',
      'Security incidents',
      'Stratus shall notify Customer of any Security Incident without undue delay and in any event within seventy-two (72) hours after becoming aware of it, shall provide the information that Customer reasonably requests to assess it, and shall take prompt steps to contain and remediate it at its own cost.',
    ),
    sub(
      '7.4',
      'Data location',
      'Stratus shall store Customer Content only in the regions that Customer selects, and shall not move it to another region without Customer’s written consent.',
    ),

    art('8', 'Support'),
    sub(
      '8.1',
      'Enterprise support',
      'The enterprise support plan is included in the Annual Committed Spend, and includes twenty-four hour, seven day support, a technical account manager, and a response within fifteen (15) minutes for critical production incidents.',
    ),

    art('9', 'Warranties, Indemnification and Liability'),
    sub(
      '9.1',
      'Warranties',
      'Stratus warrants that the Services will perform materially in accordance with their documentation and this Agreement, and that it will provide them in a professional manner and in compliance with applicable laws.',
    ),
    sub(
      '9.2',
      'Indemnification',
      'Stratus shall defend and indemnify Customer and its Affiliates against third-party claims that the Services infringe a patent, copyright or trade secret of a third party, or that arise from a Security Incident caused by Stratus’s breach of Article 7.',
    ),
    sub(
      '9.3',
      'Limitation of liability',
      'Except for indemnification obligations, breach of confidentiality, gross negligence and willful misconduct, each party’s total liability arising out of this Agreement is limited to the greater of the fees paid and payable under this Agreement in the twelve (12) months preceding the claim and $12,000,000, and neither party is liable for indirect or consequential damages.',
    ),

    art('10', 'Confidentiality'),
    sub(
      '10.1',
      'Confidentiality',
      'Each party shall keep confidential all non-public information of the other party that it receives in connection with this Agreement, including the terms of this Agreement and the Price Schedule, shall use it only to perform or receive the Services, and shall disclose it only to employees, Affiliates and advisers who need to know it and are bound by equivalent obligations. These obligations do not apply to information that is public, already known, independently developed or lawfully received from a third party, and continue for five (5) years after the end of the Term.',
    ),

    art('11', 'General Provisions'),
    sub(
      '11.1',
      'Governing law',
      'This Agreement is governed by the laws of the State of Delaware, without regard to its conflict of laws principles, and the state and federal courts located in the State of Delaware have exclusive jurisdiction over any dispute arising out of it.',
    ),
    sub(
      '11.2',
      'Notices',
      'Notices must be in writing and delivered by hand or by nationally recognized overnight courier, addressed in the case of Customer to its Senior Counsel, Commercial, at its Dallas, Texas offices, with a copy to its IT Director, and in the case of Stratus to its General Counsel at its Seattle offices. Notices of non-renewal, breach and termination are not effective if sent by email alone.',
    ),
    sub(
      '11.3',
      'Force majeure',
      'Neither party is liable for delay or failure to perform (other than a payment obligation) caused by an event beyond its reasonable control, provided that it gives prompt notice and uses reasonable efforts to resume performance. A failure of the infrastructure, power or network of Stratus, or of its subcontractors, is not a force majeure event unless it is itself caused by such an event.',
    ),
    sub(
      '11.4',
      'Assignment',
      'Neither party may assign this Agreement without the other’s prior written consent, which shall not be unreasonably withheld, except that Customer may assign it to an Affiliate or to a successor to the business to which it relates.',
    ),
    sub(
      '11.5',
      'Entire agreement and amendment',
      'This Agreement, with the Price Schedule, is the entire agreement between the parties on its subject matter. The online terms of service and acceptable use policies of Stratus do not apply to Customer, except to the extent that they describe the Services. This Agreement may be amended only in writing signed by authorized representatives of both parties.',
    ),
    sub(
      '11.6',
      'Counterparts and electronic signature',
      'This Agreement may be executed in counterparts and by electronic signature, each of which is an original and all of which together are one instrument.',
    ),
  ],
  signatures: {
    buyerSigner: 'Sam Whitfield',
    buyerTitle: 'Vice President, Finance',
    supplierSigner: 'Rebecca Lindqvist',
    supplierTitle: 'Vice President, Enterprise Sales',
    signedDate: '2024-01-18',
    envelopeId: 'E-B7E41F89-2C05-4D6A-9A13-E80C5F7D2B36',
  },
};

const SA_HOLL_2024_021: ContractDocument = {
  contractId: 'SA-HOLL-2024-021',
  title:
    'Supply Agreement: Copper Building Wire and 800 A Molded-Case Circuit Breakers',
  buyer: BUYER,
  supplier: {
    name: 'Hollis Electric Products, Inc.',
    location: 'Jackson, Tennessee',
  },
  effectiveDate: '2024-07-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of July 1, 2024 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation, on behalf of itself and its Affiliates (“Buyer”), and Hollis Electric Products, Inc., a Tennessee corporation (“Supplier”), for the supply of the products listed in Section 2.1 (the “Products”).',
  ],
  sections: [
    art('1', 'Term'),
    sub(
      '1.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until June 30, 2027, unless terminated earlier under Section 6.1. It does not renew automatically.',
    ),
    art('2', 'Price'),
    sub(
      '2.1',
      'Pricing',
      'The price for 1/0 AWG THHN/THWN-2 copper building wire, Buyer part number THHN-1/0-CU, is $437.00 per 100 linear feet. The price for the 800 A frame molded-case circuit breaker, Buyer part number MCCB-800A-H, supplied as a qualified secondary source, is $4,020.00 each. Prices are fixed for the Term and include delivery under Section 4.1.',
    ),
    art('3', 'Payment'),
    sub(
      '3.1',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Invoices shall reference this Agreement by its number, SA-HOLL-2024-021, and Buyer’s purchase order number.',
    ),
    art('4', 'Delivery and Lead Time'),
    sub(
      '4.1',
      'Delivery',
      'Supplier shall deliver the Products DAP (Delivered at Place, Incoterms 2020) to Buyer’s distribution center in Dallas, Texas or Phoenix, Arizona, or to Buyer’s Dallas panel shop, as the purchase order specifies, and shall bear all freight and insurance to that point. No separate freight charge is payable.',
    ),
    sub(
      '4.2',
      'Lead time',
      'Supplier shall deliver the Products within five (5) weeks after the date of Buyer’s purchase order.',
    ),
    art('5', 'Quality'),
    sub(
      '5.1',
      'Quality',
      'Supplier shall manufacture the Products to Buyer’s specifications, their UL listings and the Quality Agreement between the parties, shall deliver a mill test report with every reel lot of wire and a certificate of conformance with every lot of breakers, and shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in manufacturing plant, process or copper-rod or component source.',
    ),
    art('6', 'Termination and General'),
    sub(
      '6.1',
      'Termination',
      'Either party may terminate this Agreement on written notice if the other commits a material breach that is not cured within thirty (30) days. Buyer may terminate for convenience on one hundred twenty (120) days’ written notice.',
    ),
    sub(
      '6.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York. The terms of any invoice or acknowledgment that conflict with this Agreement have no effect. Each party shall keep the other’s non-public information confidential for five (5) years after the Term.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Ellen Whitcombe',
    supplierTitle: 'Chief Commercial Officer',
    signedDate: '2024-06-12',
    envelopeId: 'E-3C80A5E2-6F19-4B47-8D52-A1B7E49C0F36',
  },
};

const SA_KAIS_2025_003: ContractDocument = {
  contractId: 'SA-KAIS-2025-003',
  title: 'Supply Agreement: Copper Building Wire',
  buyer: BUYER,
  supplier: {
    name: 'Kaisei Electric Wire Co., Ltd.',
    location: 'Osaka, Japan',
  },
  effectiveDate: '2025-04-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of April 1, 2025 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation, on behalf of itself and its Affiliates (“Buyer”), and Kaisei Electric Wire Co., Ltd., a company organized under the laws of Japan (“Supplier”), for the supply of 1/0 AWG THHN/THWN-2 copper building wire, Buyer part number THHN-1/0-CU (the “Product”).',
  ],
  sections: [
    art('1', 'Term'),
    sub(
      '1.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until March 31, 2028, unless terminated earlier under Section 6.1. It does not renew automatically.',
    ),
    art('2', 'Price'),
    sub(
      '2.1',
      'Pricing',
      'The price for the Product is $448.00 per 100 linear feet, FCA (Free Carrier) Osaka (Incoterms 2020), fixed for the Term. Buyer shall arrange and pay for carriage from Supplier’s plant in Osaka, and the price excludes freight, insurance and import duties.',
    ),
    art('3', 'Payment'),
    sub(
      '3.1',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within sixty (60) days after receipt of a valid invoice (“Net 60”). Invoices shall reference this Agreement by its number, SA-KAIS-2025-003, and Buyer’s purchase order number, and shall be issued in United States dollars.',
    ),
    art('4', 'Delivery and Lead Time'),
    sub(
      '4.1',
      'Delivery',
      'Supplier shall make each order available for collection by Buyer’s carrier at its Osaka plant, cleared for export, on the date confirmed in its acknowledgment. Risk passes to Buyer when the Product is loaded on Buyer’s carrier.',
    ),
    sub(
      '4.2',
      'Lead time',
      'Supplier shall make the Product available for collection within twelve (12) weeks after the date of Buyer’s purchase order.',
    ),
    art('5', 'Quality and Continuity'),
    sub(
      '5.1',
      'Quality',
      'Supplier shall manufacture the Product to Buyer’s specification, its UL listing and the Quality Agreement between the parties, shall deliver a mill test report with every reel lot, and shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in manufacturing plant, process or copper-rod source.',
    ),
    sub(
      '5.2',
      'Business continuity',
      'Supplier shall maintain a written business continuity plan that addresses seismic and other natural hazards at its manufacturing plant, and shall share it with Buyer on request.',
    ),
    art('6', 'Termination and General'),
    sub(
      '6.1',
      'Termination',
      'Either party may terminate this Agreement on written notice if the other commits a material breach that is not cured within thirty (30) days. Buyer may terminate for convenience on one hundred twenty (120) days’ written notice.',
    ),
    sub(
      '6.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York, and the United Nations Convention on Contracts for the International Sale of Goods does not apply. Each party shall keep the other’s non-public information confidential for five (5) years after the Term.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Hiroshi Tanabe',
    supplierTitle: 'Representative Director',
    signedDate: '2025-03-10',
    envelopeId: 'E-D192B6F4-A7C3-4E08-B5F1-4C60E89A2D73',
  },
};

const SA_NGTE_2024_012: ContractDocument = {
  contractId: 'SA-NGTE-2024-012',
  title: 'Supply Agreement: Pillar Pro LED High-Bay Fixtures',
  buyer: BUYER,
  supplier: { name: 'Northgate Lighting LLC', location: 'Akron, Ohio' },
  effectiveDate: '2024-06-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of June 1, 2024 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Buyer”), and Northgate Lighting LLC, an Ohio limited liability company (“Supplier”), for the supply of the 150 W, 5000K LED high-bay luminaire sold under Buyer’s Pillar Pro brand, Buyer part number LED-HB150-PP (the “Product”).',
  ],
  sections: [
    art('1', 'Term'),
    sub(
      '1.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until May 31, 2027, unless terminated earlier under Section 5.1. It does not renew automatically.',
    ),
    art('2', 'Price and Rebate'),
    sub(
      '2.1',
      'Pricing',
      'The price for the Product is $89.95 per fixture, fixed for the Term and inclusive of delivery under Section 4.1.',
    ),
    sub(
      '2.2',
      'Volume rebate',
      'Once Buyer’s Net Purchases in a calendar year exceed Eight Million Dollars ($8,000,000), Supplier shall pay Buyer a volume rebate equal to one and one-half percent (1.5%) of all Net Purchases in that calendar year, retroactive to the first dollar and not only on the amount above the threshold. “Net Purchases” means amounts invoiced for the Product and paid or payable by Buyer in the calendar year, net of credits and taxes. Supplier shall issue the rebate by credit memo within thirty (30) days after cumulative Net Purchases first exceed the threshold, and quarterly thereafter, without any claim by Buyer.',
    ),
    art('3', 'Payment'),
    sub(
      '3.1',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within thirty (30) days after receipt of a valid invoice (“Net 30”). Invoices shall reference this Agreement by its number, SA-NGTE-2024-012, and Buyer’s purchase order number.',
    ),
    art('4', 'Delivery and Quality'),
    sub(
      '4.1',
      'Delivery',
      'Supplier shall deliver the Product DAP (Delivered at Place, Incoterms 2020) to Buyer’s Dallas DC in Dallas, Texas, Phoenix DC in Phoenix, Arizona or Hayward DC in Hayward, California, as the purchase order specifies, within six (6) weeks after the date of Buyer’s purchase order, and shall bear all freight and insurance to that point. No separate freight charge is payable.',
    ),
    sub(
      '4.2',
      'Quality',
      'Supplier shall manufacture the Product to Buyer’s specification and the Quality Agreement between the parties, shall maintain the UL listing (UL 1598) and DLC listing for the Product and keep every factory at which it is made in the UL listing file, shall deliver a Certificate of Conformance with every lot, and shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in manufacturing site, LED package or driver source, housing material or process.',
    ),
    art('5', 'Termination and General'),
    sub(
      '5.1',
      'Termination',
      'Either party may terminate this Agreement on written notice if the other commits a material breach that is not cured within thirty (30) days. Buyer may terminate for convenience on one hundred twenty (120) days’ written notice.',
    ),
    sub(
      '5.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York. Each party shall keep the other’s non-public information confidential for five (5) years after the Term.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Gary Lindholm',
    supplierTitle: 'President',
    signedDate: '2024-05-14',
    envelopeId: 'E-0F5A7C38-B2D4-4963-A8E1-7D3C96B50E12',
  },
};

const SA_POLR_2022_008: ContractDocument = {
  contractId: 'SA-POLR-2022-008',
  title: 'Supply Agreement: NEMA Enclosures',
  buyer: BUYER,
  supplier: {
    name: 'Polaris Enclosures, Inc.',
    location: 'Spartanburg, South Carolina',
  },
  effectiveDate: '2022-05-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of May 1, 2022 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Buyer”), and Polaris Enclosures, Inc., a South Carolina corporation (“Supplier”), for the supply of the NEMA 4X and NEMA 12 enclosures listed in the price schedule, for resale by Buyer and for use in Buyer’s panel shop (the “Products”).',
  ],
  sections: [
    art('1', 'Term'),
    sub(
      '1.1',
      'Evergreen term',
      'This Agreement begins on the Effective Date and continues indefinitely until terminated under Section 5.1 (an evergreen term). It does not expire on a fixed date.',
    ),
    art('2', 'Price'),
    sub(
      '2.1',
      'Pricing',
      'The prices for the Products are those in the price schedule agreed by the parties. Prices are reviewed once in each calendar year, and no increase is effective unless agreed in writing at least ninety (90) days before it takes effect.',
    ),
    art('3', 'Payment'),
    sub(
      '3.1',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Invoices shall reference this Agreement by its number, SA-POLR-2022-008, and Buyer’s purchase order number.',
    ),
    art('4', 'Delivery and Quality'),
    sub(
      '4.1',
      'Delivery',
      'Supplier shall deliver the Products DAP (Delivered at Place, Incoterms 2020) to Buyer’s Dallas panel shop in Dallas, Texas, and shall bear all freight to that point. No separate freight charge is payable.',
    ),
    sub(
      '4.2',
      'Quality and continuity',
      'Supplier shall manufacture the Products to Buyer’s specifications, to the applicable UL 50 and UL 50E enclosure standards and to the Quality Agreement between the parties, shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in manufacturing site, steel or stainless sheet source or tooling, and shall hold the tooling for the Products as Buyer’s property and maintain it in good condition.',
    ),
    art('5', 'Termination and General'),
    sub(
      '5.1',
      'Termination',
      'Either party may terminate this Agreement for convenience on twelve (12) months’ written notice, or on written notice if the other commits a material breach that is not cured within thirty (30) days.',
    ),
    sub(
      '5.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York. Each party shall keep the other’s non-public information confidential for five (5) years after termination.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Dennis Okafor',
    supplierTitle: 'President',
    signedDate: '2022-04-12',
    envelopeId: 'E-7B14E9D5-4A60-4C82-9F37-B28D0C5A61E4',
  },
};

const SA_MERI_2025_001: ContractDocument = {
  contractId: 'SA-MERI-2025-001',
  title: 'Supply Agreement: Schedule 40 PVC Conduit',
  buyer: BUYER,
  supplier: {
    name: 'Meridian Plastics & Conduit LP',
    location: 'Houston, Texas',
  },
  effectiveDate: '2025-01-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of January 1, 2025 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Buyer”), and Meridian Plastics & Conduit LP, a Texas limited partnership (“Supplier”), for the supply of Schedule 40 PVC conduit, 1 in. by 10 ft lengths (the “Product”).',
  ],
  sections: [
    art('1', 'Term'),
    sub(
      '1.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until December 31, 2027, unless terminated earlier under Section 5.1. It does not renew automatically.',
    ),
    art('2', 'Price'),
    sub(
      '2.1',
      'Pricing',
      'The price for the Product is reset each calendar quarter at the posted PVC resin index price for the quarter plus a fixed conversion adder of eleven cents ($0.11) per pound of PVC compound in the Product. Supplier shall notify Buyer of each quarterly price, with the index data, at least fifteen (15) days before the quarter begins, and Buyer may reject any price that is not so supported.',
    ),
    art('3', 'Payment'),
    sub(
      '3.1',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Invoices shall reference this Agreement by its number, SA-MERI-2025-001, and Buyer’s purchase order number.',
    ),
    art('4', 'Delivery and Quality'),
    sub(
      '4.1',
      'Delivery',
      'Supplier shall deliver the Product by truckload DAP (Delivered at Place, Incoterms 2020) to Buyer’s Dallas distribution center in Dallas, Texas, and shall bear all freight to that point. No separate freight charge is payable.',
    ),
    sub(
      '4.2',
      'Quality',
      'Supplier shall manufacture the Product to UL 651 and Buyer’s specification, shall maintain the UL listing for the Product and deliver a certificate of conformance with every lot, and shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in manufacturing site, PVC resin source or extrusion process.',
    ),
    art('5', 'Termination and General'),
    sub(
      '5.1',
      'Termination',
      'Either party may terminate this Agreement on written notice if the other commits a material breach that is not cured within thirty (30) days. Buyer may terminate for convenience on ninety (90) days’ written notice.',
    ),
    sub(
      '5.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York. Each party shall keep the other’s non-public information confidential for five (5) years after the Term.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Carla Benton',
    supplierTitle: 'Vice President, Sales',
    signedDate: '2024-12-09',
    envelopeId: 'E-5D2E80B7-91C4-4F6A-A3D8-0E7B49F1C265',
  },
};

const SA_CLRW_2024_006: ContractDocument = {
  contractId: 'SA-CLRW-2024-006',
  title: 'Supply Agreement: Wiring Devices',
  buyer: BUYER,
  supplier: {
    name: 'Clearwell Wiring Devices',
    location: 'Edison, New Jersey',
  },
  effectiveDate: '2024-09-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of September 1, 2024 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Buyer”), and Clearwell Wiring Devices, a New Jersey company (“Supplier”), for the supply of the switches, receptacles, cover plates and related wiring devices listed in the price schedule (the “Products”).',
  ],
  sections: [
    art('1', 'Term'),
    sub(
      '1.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until August 31, 2027, unless terminated earlier under Section 5.1. It does not renew automatically.',
    ),
    art('2', 'Price'),
    sub(
      '2.1',
      'Pricing',
      'The prices for the Products are those in the price schedule agreed by the parties and are fixed for the Term. Supplier shall not add any surcharge to a price.',
    ),
    art('3', 'Payment'),
    sub(
      '3.1',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Invoices shall reference this Agreement by its number, SA-CLRW-2024-006, and Buyer’s purchase order number.',
    ),
    art('4', 'Delivery and Quality'),
    sub(
      '4.1',
      'Delivery',
      'Supplier shall deliver the Products DAP (Delivered at Place, Incoterms 2020) to Buyer’s Dallas distribution center in Dallas, Texas, within three (3) weeks after the date of Buyer’s purchase order, and shall bear all freight to that point.',
    ),
    sub(
      '4.2',
      'Quality',
      'Supplier shall manufacture the Products to the applicable UL 498 listing requirements and Buyer’s specification, shall maintain the UL listing for each Product and deliver a certificate of conformance with every lot, and shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in manufacturing site, process or raw-material source.',
    ),
    art('5', 'Termination and General'),
    sub(
      '5.1',
      'Termination',
      'Either party may terminate this Agreement on written notice if the other commits a material breach that is not cured within thirty (30) days. Buyer may terminate for convenience on ninety (90) days’ written notice.',
    ),
    sub(
      '5.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York. Each party shall keep the other’s non-public information confidential for five (5) years after the Term.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Anil Mehra',
    supplierTitle: 'President',
    signedDate: '2024-08-08',
    envelopeId: 'E-A86C3D10-E5F7-4B29-8014-9C2D7E3B5F48',
  },
};

const SA_VITX_2023_004: ContractDocument = {
  contractId: 'SA-VITX-2023-004',
  title: 'Supply Agreement: 800 A Molded-Case Circuit Breakers',
  buyer: BUYER,
  supplier: {
    name: 'Vitrex Electric AG',
    location: 'Basel, Switzerland',
  },
  effectiveDate: '2023-07-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of July 1, 2023 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation, on behalf of itself and its Affiliates (“Buyer”), and Vitrex Electric AG, a Swiss stock corporation (“Supplier”), for the supply of 800 A frame molded-case circuit breakers, Buyer part number MCCB-800A (the “Product”).',
  ],
  sections: [
    art('1', 'Term'),
    sub(
      '1.1',
      'Term',
      'This Agreement begins on the Effective Date and expires on June 30, 2027, unless terminated earlier under Section 5.1. It does not renew automatically.',
    ),
    art('2', 'Price'),
    sub(
      '2.1',
      'Pricing',
      'The price for the Product is $3,850.00 each, DAP (Delivered at Place, Incoterms 2020) Buyer’s panel shop or distribution center in Dallas, Texas, as the purchase order specifies, and is fixed for the Term. No separate freight, insurance or surcharge is payable.',
    ),
    art('3', 'Payment'),
    sub(
      '3.1',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within sixty (60) days after receipt of a valid invoice (“Net 60”). Invoices shall reference this Agreement by its number, SA-VITX-2023-004, and Buyer’s purchase order number.',
    ),
    art('4', 'Quality and Change Control'),
    sub(
      '4.1',
      'Quality Agreement',
      'Supplier shall manufacture, test and ship the Product in accordance with Buyer’s specification and the Quality Agreement QA-VITX-2023-004 between the parties, which prevails over this Agreement on quality matters.',
    ),
    sub(
      '4.2',
      'Change control',
      'Supplier shall not make any change in manufacturing site, subcontractor or component source without complying with Section 7.2 of the Quality Agreement, which requires one hundred eighty (180) days’ prior written notice and Buyer’s written approval. Supplier shall ship the Product only from factories identified in the UL listing file.',
    ),
    art('5', 'Termination and General'),
    sub(
      '5.1',
      'Termination',
      'Either party may terminate this Agreement on written notice if the other commits a material breach that is not cured within thirty (30) days. Buyer may terminate immediately on written notice if Supplier makes a change that breaches Section 4.2, and may terminate for convenience on one hundred twenty (120) days’ written notice.',
    ),
    sub(
      '5.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York. Each party shall keep the other’s non-public information confidential for five (5) years after the Term.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Dr. Lukas Brenner',
    supplierTitle: 'Chief Operating Officer',
    signedDate: '2023-06-22',
    envelopeId: 'E-4E91A2C6-8D03-4F57-B6A9-1C85D7F30E92',
  },
};

const SA_STLR_2024_005: ContractDocument = {
  contractId: 'SA-STLR-2024-005',
  title: 'Supply Agreement: Dry-Type Distribution Transformers',
  buyer: BUYER,
  supplier: { name: 'Stellar Transformer Co.', location: 'Mentor, Ohio' },
  effectiveDate: '2024-03-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of March 1, 2024 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Buyer”), and Stellar Transformer Co., an Ohio corporation (“Supplier”), for the supply of UL listed, dry-type general-purpose distribution transformers, 480 V primary and 208Y/120 V secondary (the “Products”).',
  ],
  sections: [
    art('1', 'Term and Supply'),
    sub(
      '1.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until February 28, 2027, unless terminated earlier under Section 5.1. It does not renew automatically.',
    ),
    sub(
      '1.2',
      'Supply',
      'Supplier shall manufacture and supply the Products that Buyer orders from the price schedule agreed by the parties, shall deliver them from its plant in Mentor, Ohio to Buyer’s Dallas DC and Phoenix DC, and shall return approved submittal drawings and an order acknowledgment with a confirmed ship date, with a turnaround of not more than five (5) business days from receipt of each purchase order.',
    ),
    art('2', 'Price'),
    sub(
      '2.1',
      'Pricing',
      'The prices for the Products are the per-unit prices by kVA rating in the price schedule agreed by the parties and are fixed for the Term.',
    ),
    sub(
      '2.2',
      'Regulatory change',
      'Supplier shall maintain all certifications and shall give Buyer at least one hundred twenty (120) days’ written notice of any change in law or efficiency standard, including the Department of Energy distribution transformer efficiency standards, or any shortage of core steel, that could affect its price, capacity or ability to supply the Products. No change in price is effective unless agreed in a written amendment signed by Buyer.',
    ),
    art('3', 'Payment'),
    sub(
      '3.1',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Invoices shall reference this Agreement by its number, SA-STLR-2024-005, and Buyer’s purchase order number.',
    ),
    art('4', 'Quality'),
    sub(
      '4.1',
      'Quality',
      'Supplier shall manufacture the Products in accordance with UL 1561, the Department of Energy distribution transformer efficiency standards at 10 CFR Part 431, its ISO 9001 certified quality management system and the Quality Agreement between the parties, shall deliver a factory test report with every unit, shall keep every factory at which the Products are made in the UL listing file, shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in manufacturing plant, process or source of grain-oriented electrical steel, and shall maintain the capacity reserved for Buyer in the price schedule.',
    ),
    art('5', 'Termination and General'),
    sub(
      '5.1',
      'Termination',
      'Either party may terminate this Agreement on written notice if the other commits a material breach that is not cured within thirty (30) days. Buyer may terminate for convenience on ninety (90) days’ written notice.',
    ),
    sub(
      '5.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York. Each party shall keep the other’s non-public information confidential for five (5) years after the Term.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Patricia Lindgren',
    supplierTitle: 'Chief Executive Officer',
    signedDate: '2024-02-12',
    envelopeId: 'E-9A3F6D21-C7B8-4E15-A042-6D1E85B9F370',
  },
};

const MSA_BRTL_2025_004: ContractDocument = {
  contractId: 'MSA-BRTL-2025-004',
  title: 'Master Services Agreement: Creative and Media Services',
  buyer: BUYER,
  supplier: {
    name: 'Brightline Creative Group',
    location: 'Chicago, Illinois',
  },
  effectiveDate: '2025-03-01',
  recitals: [
    'This Master Services Agreement (this “Agreement”) is entered into as of March 1, 2025 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Client”), and Brightline Creative Group, an Illinois corporation (“Agency”), for creative, production and media planning services, including the Pillar Pro private-label launch and trade marketing, described in statements of work signed by both parties (each an “SOW”).',
  ],
  sections: [
    art('1', 'Services'),
    sub(
      '1.1',
      'Statements of work',
      'Agency shall perform the services in each SOW, which shall state the scope, the deliverables, the milestones and the fee for each milestone. No work outside an SOW may be performed or invoiced without a written change order signed by Client.',
    ),
    sub(
      '1.2',
      'Territory',
      'This Agreement covers services for campaigns, content and media placed in the United States. Services for campaigns outside the United States, including Canada and Mexico, are outside the scope of this Agreement and require a separate agreement or a written amendment signed by both parties.',
    ),
    art('2', 'Fees and Payment'),
    sub(
      '2.1',
      'Milestone billing',
      'Agency shall invoice fees only on Client’s written acceptance of the milestone to which they relate, in the amount stated for that milestone in the SOW. Agency shall not invoice any fee in advance of acceptance.',
    ),
    sub(
      '2.2',
      'Payment terms',
      'Client shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Invoices shall reference this Agreement by its number, MSA-BRTL-2025-004, the SOW and the milestone.',
    ),
    sub(
      '2.3',
      'Third-party costs',
      'Third-party media, production and talent costs are passed through at cost, without markup, and only if Client has approved the estimate in writing before they are incurred. Agency shall pass on all rebates and discounts that it receives on them.',
    ),
    art('3', 'Work Product and Compliance'),
    sub(
      '3.1',
      'Ownership and usage rights',
      'Client owns all work product delivered under an SOW on payment. Agency shall obtain, and shall pass to Client, usage rights for third-party content, talent and music for the period and territories stated in the SOW, and shall not use any content that has not been cleared for that use.',
    ),
    sub(
      '3.2',
      'Regulatory review',
      'Agency acknowledges that Client’s advertising and promotional materials, including Pillar Pro packaging, catalog and trade marketing materials, are subject to its brand, legal and compliance review, including review of any UL listing, DLC listing, efficiency and country-of-origin claims, and shall not publish any material that has not completed that review.',
    ),
    art('4', 'Term and General'),
    sub(
      '4.1',
      'Term and termination',
      'This Agreement continues until February 28, 2028. Client may terminate it or any SOW for convenience on thirty (30) days’ written notice, paying for accepted milestones and for work in progress on the current milestone in proportion to its completion.',
    ),
    sub(
      '4.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York. Each party shall keep the other’s non-public information confidential for five (5) years after the end of this Agreement.',
    ),
  ],
  signatures: {
    buyerSigner: 'Daniel Reyes',
    buyerTitle: 'Director, Indirect Procurement',
    supplierSigner: 'Nina Alvarez',
    supplierTitle: 'Chief Executive Officer',
    signedDate: '2025-02-13',
    envelopeId: 'E-2A7D40F9-5B16-4C83-9E70-F4C1B8D36A25',
  },
};

const CPA_NSTR_2025_002: ContractDocument = {
  contractId: 'CPA-NSTR-2025-002',
  title: 'Corporate Purchase Agreement: End-User Computing Hardware',
  buyer: BUYER,
  supplier: {
    name: 'Northstar Technology Solutions',
    location: 'Dallas, Texas',
  },
  effectiveDate: '2025-02-01',
  recitals: [
    'This Corporate Purchase Agreement (this “Agreement”) is entered into as of February 1, 2025 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Customer”), and Northstar Technology Solutions, a Texas corporation (“Northstar”), under which Customer may purchase the products in Northstar’s contract catalog (the “Catalog”).',
  ],
  sections: [
    art('1', 'Catalog and Pricing'),
    sub(
      '1.1',
      'Catalog pricing',
      'Customer may purchase the products in the Catalog at the contract prices in it. The contract price of the Catalog 14-inch business laptop (16 GB memory, 512 GB solid-state drive, three-year onsite warranty) is $1,289.00 per unit. The contract prices of other Catalog products, including rugged handheld scanners for distribution center use, are the then-current list price less twelve percent (12%).',
    ),
    sub(
      '1.2',
      'Price protection',
      'Contract prices are fixed for the Term. If Northstar reduces the price of a Catalog product, or offers a lower price to a similar customer, the reduced price applies to Customer’s orders from that date.',
    ),
    art('2', 'Ordering, Delivery and Warranty'),
    sub(
      '2.1',
      'Ordering and delivery',
      'Customer may order by purchase order or through Northstar’s procurement portal. Northstar shall ship stocked products within five (5) business days and shall deliver them to Customer’s Dallas headquarters, distribution centers or branches with freight prepaid and included in the contract price.',
    ),
    sub(
      '2.2',
      'Returns and warranty',
      'Customer may return unopened products within thirty (30) days for a full refund without restocking fee. Northstar shall pass through the manufacturer’s warranty and shall provide the three-year onsite warranty described in Section 1.1 for the business laptop.',
    ),
    art('3', 'Payment'),
    sub(
      '3.1',
      'Payment terms',
      'Customer shall pay each undisputed invoice within thirty (30) days after receipt of a valid invoice (“Net 30”). Invoices shall reference this Agreement by its number, CPA-NSTR-2025-002, and Customer’s purchase order number.',
    ),
    art('4', 'Term and General'),
    sub(
      '4.1',
      'Term',
      'This Agreement continues until January 31, 2028. Either party may terminate it for convenience on ninety (90) days’ written notice, and orders accepted before termination shall be completed on its terms.',
    ),
    sub(
      '4.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York. Each party shall keep the other’s non-public information confidential for five (5) years after the end of this Agreement.',
    ),
  ],
  signatures: {
    buyerSigner: 'Daniel Reyes',
    buyerTitle: 'Director, Indirect Procurement',
    supplierSigner: 'Michael Tran',
    supplierTitle: 'Vice President, Enterprise Accounts',
    signedDate: '2025-01-15',
    envelopeId: 'E-E50B9A83-1D47-4F26-B8C0-3A62D7F91E58',
  },
};

const MSA_KEYS_2024_010: ContractDocument = {
  contractId: 'MSA-KEYS-2024-010',
  title: 'Master Services Agreement: Contingent Labor',
  buyer: BUYER,
  supplier: {
    name: 'Keystone Staffing Group',
    location: 'Fort Worth, Texas',
  },
  effectiveDate: '2024-05-01',
  recitals: [
    'This Master Services Agreement (this “Agreement”) is entered into as of May 1, 2024 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Client”), and Keystone Staffing Group, a Texas corporation (“Keystone”), under which Keystone shall supply temporary and contract workers (“Workers”) to Client, principally warehouse associates, pickers and forklift operators at Client’s Dallas, Phoenix and Hayward distribution centers.',
  ],
  sections: [
    art('1', 'Services'),
    sub(
      '1.1',
      'Supply of workers',
      'Keystone shall recruit, screen, employ and pay the Workers requested by Client in a written order. Keystone is the employer of record of each Worker and is responsible for wages, payroll taxes, workers’ compensation, benefits and compliance with employment laws.',
    ),
    art('2', 'Bill Rates'),
    sub(
      '2.1',
      'Markup cap',
      'The bill rate for each Worker is the Worker’s hourly pay rate multiplied by one plus the markup. The markup covers all of Keystone’s statutory burden, benefits, insurance, overhead and margin, and shall not exceed thirty-two percent (32%) of the Worker’s pay rate. Keystone shall state the pay rate and markup for each Worker in the order, and shall not invoice at a higher markup.',
    ),
    sub(
      '2.2',
      'Overtime approval',
      'Overtime hours, being hours in excess of forty (40) in a workweek, are billable only if the Client hiring manager has approved them in writing before they are worked, and are billed at one and one-half times the pay rate with the same markup. Keystone shall not invoice unapproved overtime.',
    ),
    sub(
      '2.3',
      'Conversion',
      'Client may hire a Worker without a conversion fee after the Worker has completed five hundred twenty (520) hours of service.',
    ),
    art('3', 'Invoicing and Payment'),
    sub(
      '3.1',
      'Invoices and payment terms',
      'Keystone shall invoice weekly or bi-weekly in arrears, attaching the timesheets approved by the Client supervisor. Client shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Invoices shall reference this Agreement by its number, MSA-KEYS-2024-010.',
    ),
    art('4', 'Term and General'),
    sub(
      '4.1',
      'Term',
      'This Agreement continues until April 30, 2027. Client may terminate it or end any assignment for convenience on ten (10) days’ written notice.',
    ),
    sub(
      '4.2',
      'Compliance and governing law',
      'Keystone shall conduct background checks and eligibility verification for each Worker as required by law and by Client’s policy, shall comply with applicable federal, state and local wage and hour laws, including those of California for Workers assigned to Client’s Hayward distribution center, and shall maintain insurance appropriate to its operations. This Agreement is governed by the laws of the State of New York.',
    ),
  ],
  signatures: {
    buyerSigner: 'Daniel Reyes',
    buyerTitle: 'Director, Indirect Procurement',
    supplierSigner: 'Brian Kowalski',
    supplierTitle: 'President',
    signedDate: '2024-04-09',
    envelopeId: 'E-6C18F7A0-D3E5-4B94-A761-8E2B05C49D17',
  },
};

const MSA_CLVW_2024_003: ContractDocument = {
  contractId: 'MSA-CLVW-2024-003',
  title: 'Master Services Agreement: Facility Services',
  buyer: BUYER,
  supplier: {
    name: 'Clearview Facility Services',
    location: 'Dallas, Texas',
  },
  effectiveDate: '2024-01-01',
  recitals: [
    'This Master Services Agreement (this “Agreement”) is entered into as of January 1, 2024 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Client”), and Clearview Facility Services, a Texas corporation (“Contractor”), for janitorial, grounds and general facility services at Client’s Dallas, Texas headquarters, distribution center and panel shop (the “Services”).',
  ],
  sections: [
    art('1', 'Services and Fees'),
    sub(
      '1.1',
      'Services',
      'Contractor shall perform the Services in accordance with the service specification and schedule agreed by the parties, with trained and supervised personnel, and in compliance with Client’s site safety requirements, including forklift traffic, warehouse and panel-shop safety rules.',
    ),
    sub(
      '1.2',
      'Fees',
      'Client shall pay the monthly fixed fee in the fee schedule agreed by the parties for the scheduled Services, and the unit prices in the schedule for additional services requested in writing by Client. Fees are fixed for the Term.',
    ),
    art('2', 'Payment'),
    sub(
      '2.1',
      'Payment terms',
      'Client shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Invoices shall reference this Agreement by its number, MSA-CLVW-2024-003, and shall be issued monthly in arrears.',
    ),
    art('3', 'Insurance and Compliance'),
    sub(
      '3.1',
      'Insurance',
      'Contractor shall maintain commercial general liability insurance of not less than $2,000,000 per occurrence, workers’ compensation insurance as required by law, and automobile liability insurance of not less than $1,000,000, and shall name Client as an additional insured.',
    ),
    sub(
      '3.2',
      'Compliance',
      'Contractor shall comply with applicable laws, including laws on wages, immigration and safety, and shall perform background checks on its personnel before they are assigned to Client’s site.',
    ),
    art('4', 'Term and General'),
    sub(
      '4.1',
      'Term and termination',
      'This Agreement continues until December 31, 2026. Client may terminate it for convenience on sixty (60) days’ written notice, and either party may terminate it on written notice if the other commits a material breach that is not cured within thirty (30) days.',
    ),
    sub(
      '4.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York. Each party shall keep the other’s non-public information confidential for five (5) years after the end of this Agreement.',
    ),
  ],
  signatures: {
    buyerSigner: 'Daniel Reyes',
    buyerTitle: 'Director, Indirect Procurement',
    supplierSigner: 'Anthony Russo',
    supplierTitle: 'President',
    signedDate: '2023-12-05',
    envelopeId: 'E-B04E2D75-9A18-4C6F-8F39-5D7A1E6C80B3',
  },
};

const NDA_KEST_2026_001: ContractDocument = {
  contractId: 'NDA-KEST-2026-001',
  title: 'Mutual Non-Disclosure Agreement',
  subtitle: 'Evaluation of potential supply of 50 HP variable frequency drives',
  buyer: BUYER,
  supplier: {
    name: 'Kestrel Drives A/S',
    location: 'Nordborg, Denmark',
  },
  effectiveDate: '2026-02-12',
  recitals: [
    'This Mutual Non-Disclosure Agreement (this “Agreement”) is entered into as of February 12, 2026 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas (“Pillar”), and Kestrel Drives A/S, a company organized under the laws of Denmark (“Kestrel”), each a “Party”, in connection with the evaluation of a potential supply relationship for 50 HP, 480 V variable frequency drives (the “Purpose”).',
  ],
  sections: [
    art('1', 'Confidential Information'),
    sub(
      '1.1',
      'Definition',
      '“Confidential Information” means all non-public information that a Party (the “Discloser”) discloses to the other Party (the “Recipient”) for the Purpose, in any form, including specifications, process and quality information, UL, CE and ISO 9001 certification files, pricing, forecasts and supplier and customer information, that is marked confidential or that a reasonable person would understand to be confidential.',
    ),
    sub(
      '1.2',
      'Exclusions',
      'Confidential Information does not include information that is or becomes public through no breach of this Agreement, was known to the Recipient without restriction, is independently developed by the Recipient, or is lawfully received from a third party without a duty of confidentiality.',
    ),
    art('2', 'Obligations'),
    sub(
      '2.1',
      'Use and protection',
      'The Recipient shall use the Discloser’s Confidential Information only for the Purpose, shall protect it with at least the care it uses for its own confidential information and no less than reasonable care, and shall disclose it only to its employees, Affiliates and advisers who need to know it and are bound by equivalent obligations.',
    ),
    sub(
      '2.2',
      'Compelled disclosure',
      'The Recipient may disclose Confidential Information to the extent required by law or court order, provided that it gives the Discloser prompt notice, where lawful, and discloses only what is required.',
    ),
    art('3', 'Term'),
    sub(
      '3.1',
      'Term and survival',
      'The Parties may exchange Confidential Information for two (2) years after the Effective Date. The obligations in this Agreement continue for five (5) years after the end of that period, and for as long as any trade secret remains a trade secret. On request the Recipient shall return or destroy the Discloser’s Confidential Information.',
    ),
    art('4', 'General'),
    sub(
      '4.1',
      'No obligation',
      'This Agreement does not oblige either Party to disclose any information or to enter into any transaction. Neither Party acquires any license or other right in the other’s Confidential Information, and all of it remains the property of the Discloser.',
    ),
    sub(
      '4.2',
      'Governing law',
      'This Agreement is governed by the laws of the State of New York, and the courts located in New York County, New York have exclusive jurisdiction over any dispute arising out of it. It may be executed in counterparts and by electronic signature.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Mikkel Sørensen',
    supplierTitle: 'Chief Executive Officer',
    signedDate: '2026-02-12',
    envelopeId: 'E-8D63C1B9-7F20-4A58-B4E6-02A95F3D7C81',
  },
};

const SA_MROS_2025_004: ContractDocument = {
  contractId: 'SA-MROS-2025-004',
  title: 'Supply Agreement: Maintenance and Industrial Supplies',
  subtitle:
    'Hydraulic fittings, strut channel, brackets and valves for Pillar’s distribution centers and branches',
  buyer: BUYER,
  supplier: { name: 'MRO Supply Co.', location: 'Houston, Texas' },
  effectiveDate: '2025-02-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of February 1, 2025 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Buyer”), and MRO Supply Co., a Texas corporation having its principal offices in Houston, Texas (“Supplier”), for the supply of the industrial and maintenance, repair and operations products described in Article 3 (the “Products”) to Buyer’s distribution centers and branches.',
    'Supplier bills through its parent company, Apex Industrial Group. The parties wish to record how that billing works, together with the price, delivery and payment terms for the Products.',
  ],
  sections: [
    art('1', 'Term'),
    sub(
      '1.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until January 31, 2028 (the “Term”), unless terminated earlier under Section 8.1. It does not renew automatically, and any extension must be agreed in a written amendment signed by both parties.',
    ),
    sub(
      '1.2',
      'Affiliates and non-exclusivity',
      'Buyer’s Affiliates, including the operators of its Dallas, Phoenix and Hayward distribution centers, may order under this Agreement, and Buyer may enforce it on their behalf. This Agreement is non-exclusive and Buyer is not obliged to purchase any minimum quantity.',
    ),

    art('2', 'Orders'),
    sub(
      '2.1',
      'Purchase orders',
      'Buyer shall order Products by written purchase orders, which may be issued electronically and shall state the item, quantity, delivery date and delivery point. Supplier shall acknowledge each purchase order within one (1) business day and shall not substitute a different item without Buyer’s written approval.',
    ),

    art('3', 'Price'),
    sub(
      '3.1',
      'Strut channel',
      'The price for Strut Channel (12 ga), 1-5/8 in x 10 ft, which Supplier also invoices under the trade name “Unistrut Channel”, is Thirty Dollars ($30.00) per length.',
    ),
    sub(
      '3.2',
      'Hydraulic quick couplers',
      'The price for Hydraulic Quick Coupler, 1/2 in NPT, flat-face, is Twenty-Four Dollars ($24.00) per coupler.',
    ),
    sub(
      '3.3',
      'Mounting brackets',
      'The price for Galvanized Mounting Bracket, heavy duty, is Twenty-Two Dollars and Fifty Cents ($22.50) per bracket.',
    ),
    sub(
      '3.4',
      'Ball valves',
      'The price for Brass Ball Valve, 1 in NPT, is Twenty-Five Dollars ($25.00) per valve.',
    ),
    sub(
      '3.5',
      'Prices fixed',
      'The prices in Sections 3.1 to 3.4 are fixed for the Term and include packaging. Supplier shall not add any fuel, handling, administrative or other surcharge to them. A price for any other item shall be agreed in writing before the item is shipped.',
    ),

    art('4', 'Invoicing and Payment'),
    sub(
      '4.1',
      'Payment terms and early payment discount',
      'Payment terms are “2% 10, Net 30”: Buyer may deduct two percent (2%) from the invoice total if it pays within ten (10) days after receipt of a valid invoice. A payment made within that period that includes the deduction is a payment in full of the invoice. Buyer is not obliged to take the discount, and the discount does not apply to any amount disputed under Section 4.5.',
    ),
    sub(
      '4.2',
      'Due date',
      'Buyer shall pay each undisputed invoice within thirty (30) days after receipt of a valid invoice (“Net 30”). Any shorter payment term stated on an invoice or other Supplier document has no effect.',
    ),
    sub(
      '4.3',
      'Invoices',
      'Supplier may invoice each shipment on or after the date on which Buyer receives it. Each invoice shall reference this Agreement by its number, SA-MROS-2025-004, and Buyer’s purchase order number, and shall state each item, the quantity delivered, the unit price and the total.',
    ),
    sub(
      '4.4',
      'Billing entity',
      'Supplier may issue invoices through its parent company, Apex Industrial Group, provided that each invoice cites this Agreement and the purchase order number and is payable to the remit-to account on file in Buyer’s vendor master. Buyer shall treat a conforming invoice from Apex Industrial Group as an invoice from Supplier.',
    ),
    sub(
      '4.5',
      'Disputed amounts',
      'Buyer may withhold payment of any amount that it disputes in good faith, provided that it notifies Supplier in writing of the reason within thirty (30) days after receipt of the invoice and pays the undisputed balance when due.',
    ),

    art('5', 'Delivery and Freight'),
    sub(
      '5.1',
      'Prepaid freight',
      'Supplier shall ship each order of Five Thousand Dollars ($5,000) or more freight prepaid, DAP (Delivered at Place, Incoterms 2020) to the Buyer distribution center or branch named in the purchase order, and shall not invoice freight, handling or fuel charges on that order. On an order below $5,000, Supplier may add freight at its actual carrier cost, shown as a separate invoice line without markup.',
    ),
    sub(
      '5.2',
      'Buyer-routed orders',
      'Buyer may direct that an order be collected from Supplier’s Houston warehouse by a carrier under contract with Buyer, for example for a time-critical or heavy palletized load (a “Buyer-Routed Order”). Supplier shall have the order ready for pickup on the requested date and shall not charge for freight on it, and the carrier shall invoice Buyer directly under Buyer’s transportation agreement.',
    ),
    sub(
      '5.3',
      'Title and risk',
      'Title and risk of loss pass to Buyer on delivery to the delivery point or, on a Buyer-Routed Order, when the order is loaded onto Buyer’s carrier.',
    ),

    art('6', 'Lead Time and Fill Rate'),
    sub(
      '6.1',
      'Lead time',
      'Supplier shall deliver Products within fourteen (14) days after the date of Buyer’s purchase order, or by any later date that the purchase order specifies.',
    ),
    sub(
      '6.2',
      'Fill rate',
      'Supplier shall fill at least ninety-five percent (95%) of purchase order lines on time and in full, measured monthly. If performance falls below that level in any calendar quarter, Supplier shall deliver a written recovery plan within ten (10) business days and implement it promptly.',
    ),

    art('7', 'Quality and Returns'),
    sub(
      '7.1',
      'Product quality',
      'Supplier shall supply new Products that conform to the manufacturer’s specification, are free from defects in material and workmanship, and carry any listing or certification marks required for their use, and shall pass through the manufacturer’s warranty to Buyer.',
    ),
    sub(
      '7.2',
      'Returns',
      'Buyer may return any nonconforming Product within twelve (12) months after delivery for replacement or credit at Supplier’s cost, including return freight. Buyer may return unused stock Product in its original packaging within thirty (30) days after delivery for a full credit.',
    ),

    art('8', 'Termination and General'),
    sub(
      '8.1',
      'Termination',
      'Either party may terminate this Agreement by written notice if the other commits a material breach that is not cured within thirty (30) days after written notice of it. Buyer may terminate for convenience on ninety (90) days’ written notice to Supplier.',
    ),
    sub(
      '8.2',
      'Governing law and general',
      'This Agreement is governed by the laws of the State of New York. Each party shall keep the other’s non-public information confidential for five (5) years after the Term. Terms printed on any invoice, acknowledgment or other Supplier document that conflict with this Agreement have no effect.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Dale Whitaker',
    supplierTitle: 'President',
    signedDate: '2025-01-16',
    envelopeId: 'E-3B7D90C4-5E12-4A86-9F03-C61D2A8E47B5',
  },
};

const SA_BRST_2024_011: ContractDocument = {
  contractId: 'SA-BRST-2024-011',
  title: 'Supply Agreement: Electrical Conduit, Boxes and Fittings',
  subtitle:
    'Fill-in and backorder supply of EMT (electrical metallic tubing) conduit, boxes and fittings to Pillar’s distribution centers and branches',
  buyer: BUYER,
  supplier: { name: 'Border States', location: 'San Antonio, Texas' },
  effectiveDate: '2024-08-01',
  recitals: [
    'This Supply Agreement (this “Agreement”) is entered into as of August 1, 2024 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Buyer”), and Border States, a Texas company having its principal offices in San Antonio, Texas (“Supplier”), for the supply of the electrical conduit, boxes and fittings described in Article 3 (the “Products”) to Buyer’s distribution centers and branches as fill-in and backorder coverage.',
  ],
  sections: [
    art('1', 'Term and Scope'),
    sub(
      '1.1',
      'Term',
      'This Agreement begins on the Effective Date and continues until July 31, 2027 (the “Term”), unless terminated earlier under Section 7.1. It does not renew automatically, and any extension must be agreed in a written amendment signed by both parties.',
    ),
    sub(
      '1.2',
      'Fill-in supply',
      'Supplier is a secondary source. Buyer shall use Supplier to cover backorders and shortages on the Products that its primary sources cannot ship in time, and is not obliged to purchase any minimum quantity or share of its requirements. Supplier shall accept every purchase order for the Products that it has in stock.',
    ),

    art('2', 'Orders'),
    sub(
      '2.1',
      'Purchase orders and lead time',
      'Buyer shall order the Products by written purchase orders, which may be issued electronically and shall state the item, quantity, delivery date and delivery point. Supplier shall ship each stocked item within three (3) business days after receipt of the purchase order, and shall confirm within one (1) business day the ship date of any item it does not have in stock.',
    ),

    art('3', 'Price'),
    sub(
      '3.1',
      'EMT conduit',
      'The price for EMT Conduit, 3/4 in x 10 ft, is Twenty-Eight Dollars ($28.00) per length.',
    ),
    sub(
      '3.2',
      'Weatherproof junction boxes',
      'The price for Weatherproof Junction Box, 1-gang, cast aluminum, is Twenty-Five Dollars ($25.00) per box.',
    ),
    sub(
      '3.3',
      'Liquidtight connectors',
      'The price for Liquidtight Connector, 3/4 in straight, is Sixteen Dollars and Fifty Cents ($16.50) per connector.',
    ),
    sub(
      '3.4',
      'Prices fixed',
      'The prices in Sections 3.1 to 3.3 are fixed for the Term and include packaging. Supplier shall not add any fuel, handling, restocking or other surcharge to them. A price for any other item shall be agreed in writing before the item is shipped.',
    ),

    art('4', 'Invoicing and Payment'),
    sub(
      '4.1',
      'Invoices',
      'Supplier may invoice each shipment on or after the date on which Buyer receives it. Each invoice shall reference this Agreement by its number, SA-BRST-2024-011, and Buyer’s purchase order number, and shall state each item, the quantity delivered, the unit price and the total.',
    ),
    sub(
      '4.2',
      'Payment terms',
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Any shorter payment term stated on an invoice or other Supplier document has no effect.',
    ),
    sub(
      '4.3',
      'Disputed amounts',
      'Buyer may withhold payment of any amount that it disputes in good faith, provided that it notifies Supplier in writing of the reason within thirty (30) days after receipt of the invoice and pays the undisputed balance when due.',
    ),

    art('5', 'Delivery'),
    sub(
      '5.1',
      'Delivery and freight',
      'Supplier shall deliver all Products DAP (Delivered at Place, Incoterms 2020) to the Buyer distribution center or branch named in the purchase order, and shall bear all freight and insurance to that point. No separate freight, handling or fuel charge is payable by Buyer in addition to the price.',
    ),
    sub(
      '5.2',
      'Title and risk',
      'Title and risk of loss pass to Buyer on delivery to the delivery point, free from liens and encumbrances.',
    ),

    art('6', 'Quality and Returns'),
    sub(
      '6.1',
      'Product quality',
      'Supplier shall supply new Products in their original packaging that conform to the manufacturer’s specification, are free from defects in material and workmanship, and are UL listed where a listing applies to the Product.',
    ),
    sub(
      '6.2',
      'Returns',
      'Buyer may return any nonconforming Product within twelve (12) months after delivery for replacement or credit at Supplier’s cost, including return freight. Buyer may return unused stock Product in its original packaging within thirty (30) days after delivery for a full credit and without a restocking fee.',
    ),

    art('7', 'Termination and General'),
    sub(
      '7.1',
      'Termination',
      'Either party may terminate this Agreement by written notice if the other commits a material breach that is not cured within thirty (30) days after written notice of it. Buyer may terminate for convenience on sixty (60) days’ written notice to Supplier.',
    ),
    sub(
      '7.2',
      'Governing law and general',
      'This Agreement is governed by the laws of the State of New York. Each party shall keep the other’s non-public information confidential for five (5) years after the Term. Terms printed on any invoice, acknowledgment or other Supplier document that conflict with this Agreement have no effect.',
    ),
  ],
  signatures: {
    buyerSigner: 'Marcus Hale',
    buyerTitle: 'Vice President, Supply Chain',
    supplierSigner: 'Curtis Maddox',
    supplierTitle: 'Vice President, Sales',
    signedDate: '2024-07-11',
    envelopeId: 'E-A91C4E27-08D5-4B63-8C7F-2E5B13D96A40',
  },
};

const MSA_BRZV_2025_002: ContractDocument = {
  contractId: 'MSA-BRZV-2025-002',
  title: 'Master Logistics Services Agreement',
  subtitle:
    'Pallet handling, ambient storage, crating and shuttle transportation for overflow inventory',
  buyer: BUYER,
  supplier: { name: 'Brazos Valley Logistics', location: 'Bryan, Texas' },
  effectiveDate: '2025-06-01',
  recitals: [
    'This Master Logistics Services Agreement (this “Agreement”) is entered into as of June 1, 2025 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Shipper”), and Brazos Valley Logistics, a Texas company having its principal offices in Bryan, Texas (“Provider”), under which Provider shall handle, store, crate and transport overflow inventory for Shipper from its warehouse in Bryan, Texas.',
  ],
  sections: [
    art('1', 'Services and Term'),
    sub(
      '1.1',
      'Services',
      'Provider shall provide the following services at its Bryan, Texas warehouse and on the shuttle lane to Shipper’s Dallas distribution center: (a) receiving and put-away, and pick and release, of pallets (“Pallet Handling”); (b) ambient storage of pallets; (c) outbound crating and export packaging; and (d) shuttle transportation between the Bryan warehouse and the Dallas distribution center.',
    ),
    sub(
      '1.2',
      'Term',
      'This Agreement begins on the Effective Date and continues until May 31, 2028 (the “Term”), unless terminated earlier under Section 7.1. It does not renew automatically, and Shipper is not obliged to tender any minimum volume.',
    ),

    art('2', 'Orders'),
    sub(
      '2.1',
      'Purchase orders and work orders',
      'Shipper shall order services by purchase orders. A Shipper facility representative may also request the accessorial service in Section 4.1 by a written work order, and an accessorial service requested that way need not appear on a purchase order. Provider shall acknowledge each order within one (1) business day.',
    ),

    art('3', 'Rates'),
    sub(
      '3.1',
      'Pallet handling',
      'The rate for Pallet Handling, inbound and outbound, is Eighteen Dollars ($18.00) per pallet.',
    ),
    sub(
      '3.2',
      'Ambient storage',
      'The rate for ambient storage is Twelve Dollars ($12.00) per pallet-month, charged for each pallet in storage on the last day of the month.',
    ),
    sub(
      '3.3',
      'Shuttle transportation',
      'The rate for shuttle transportation between the Bryan warehouse and Shipper’s Dallas distribution center is Three Hundred Eighty-Five Dollars ($385.00) per load.',
    ),
    sub(
      '3.4',
      'Rates fixed; no other charges',
      'The rates in this Article 3 and in Section 4.1 are fixed for the Term. The only charges payable by Shipper are those rates and the accessorial charge in Section 4.1. Provider shall not invoice any administrative, technology, fuel, minimum or other fee.',
    ),

    art('4', 'Accessorial Services'),
    sub(
      '4.1',
      'Crating and export packaging',
      'Provider may charge Six Hundred Dollars ($600.00) per job for outbound crating and export packaging of a Shipper shipment, where the service was requested by Shipper and has been completed. This is the only accessorial service that Provider may charge.',
    ),
    sub(
      '4.2',
      'Signed proof required',
      'An accessorial charge is payable only if Provider holds proof that the service was requested and completed, signed by an authorized Shipper facility representative, such as a signed work order or packing list. Provider shall not invoice an accessorial charge without the signed proof, shall provide it on request, and Shipper is not obliged to pay any accessorial charge that is not supported by it.',
    ),

    art('5', 'Invoicing and Payment'),
    sub(
      '5.1',
      'Invoices',
      'Provider shall invoice each purchase order after the services are performed. Each invoice shall reference this Agreement by its number, MSA-BRZV-2025-002, and Shipper’s purchase order number, and shall state each service, the quantity, the rate and the total, with each accessorial charge on its own line.',
    ),
    sub(
      '5.2',
      'Payment terms',
      'Shipper shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice (“Net 45”). Any shorter payment term stated on an invoice or other Provider document has no effect.',
    ),
    sub(
      '5.3',
      'Disputed charges and audit',
      'Shipper may withhold payment of any amount that it disputes in good faith, provided that it notifies Provider of the reason, and may audit Provider’s invoices and supporting records for three (3) years after payment. Provider shall promptly refund any overcharge identified.',
    ),

    art('6', 'Service Levels and Liability'),
    sub(
      '6.1',
      'Service levels',
      'Provider shall receive and put away inbound pallets within twenty-four (24) hours after arrival, shall release outbound pallets within four (4) hours after a request received before 2:00 p.m., and shall maintain inventory accuracy of at least ninety-nine and one-half percent (99.5%), measured by monthly cycle count.',
    ),
    sub(
      '6.2',
      'Care of goods and insurance',
      'Provider is liable to Shipper for the actual loss of, or damage to, goods in its custody, up to their replacement value, and shall maintain warehouse legal liability insurance of not less than $2,000,000 and commercial general liability insurance of not less than $2,000,000 per occurrence.',
    ),

    art('7', 'Termination and General'),
    sub(
      '7.1',
      'Termination',
      'Either party may terminate this Agreement for convenience on thirty (30) days’ written notice, or on written notice if the other commits a material breach that is not cured within ten (10) business days after written notice of it. Provider shall return all goods in its custody within ten (10) business days after termination.',
    ),
    sub(
      '7.2',
      'Governing law and general',
      'This Agreement is governed by the laws of the State of New York. Terms printed on any invoice, receipt or other Provider document that conflict with this Agreement have no effect.',
    ),
  ],
  signatures: {
    buyerSigner: 'Daniel Reyes',
    buyerTitle: 'Director, Indirect Procurement',
    supplierSigner: 'Harold Voss',
    supplierTitle: 'President',
    signedDate: '2025-05-14',
    envelopeId: 'E-5D08B3F1-C7A2-4E94-A6B0-91F4D2C8E73A',
  },
};

const MSA_SNTL_2025_003: ContractDocument = {
  contractId: 'MSA-SNTL-2025-003',
  title: 'Master Freight Services Agreement',
  subtitle:
    'Less-than-truckload and truckload transportation of inbound vendor freight to Pillar’s Dallas distribution center',
  buyer: BUYER,
  supplier: { name: 'Sentinel Freight', location: 'Irving, Texas' },
  effectiveDate: '2025-09-01',
  recitals: [
    'This Master Freight Services Agreement (this “Agreement”) is entered into as of September 1, 2025 (the “Effective Date”) by Pillar Industrial Supply, Inc., a Texas corporation having its principal offices in Dallas, Texas, on behalf of itself and its Affiliates (“Shipper”), and Sentinel Freight, a Texas company having its principal offices in Irving, Texas (“Carrier”). Carrier is a motor carrier licensed to transport freight, and Shipper wishes to engage Carrier to transport its inbound vendor freight on the terms of this Agreement.',
  ],
  sections: [
    art('1', 'Services and Term'),
    sub(
      '1.1',
      'Services',
      'Carrier shall transport Shipments tendered by Shipper between the origins and destinations in the Lane Schedule, principally inbound vendor freight from Houston, Texas to Shipper’s Dallas distribution center, with the equipment and drivers needed to perform the transportation. “Shipment” means freight tendered under a single bill of lading, and “Lane Schedule” means the schedule of origins, destinations and rates in effect under Article 2.',
    ),
    sub(
      '1.2',
      'Term',
      'This Agreement begins on the Effective Date and continues until August 31, 2028 (the “Term”), unless terminated earlier under Section 7.1. It does not renew automatically, and Shipper is not obliged to tender any minimum volume.',
    ),

    art('2', 'Rates'),
    sub(
      '2.1',
      'Houston to Dallas lane',
      'The line haul rate for a Shipment of up to twenty thousand (20,000) pounds from Houston, Texas to Shipper’s Dallas distribution center is One Thousand Two Hundred Dollars ($1,200.00) per shipment (the “Line Haul”). The rate includes pickup, delivery to the dock, driver and equipment costs, tolls and permits.',
    ),
    sub(
      '2.2',
      'Other lanes and rate changes',
      'The Line Haul rate for any other lane is stated in the Lane Schedule. Rates are fixed for the Term, and no change is effective unless agreed in a written amendment signed by both parties.',
    ),

    art('3', 'Fuel Surcharge'),
    sub(
      '3.1',
      'Fuel surcharge cap',
      'Carrier may charge a fuel surcharge on the Line Haul of a Shipment only when the weekly United States average retail on-highway diesel price published by the Department of Energy for the week of pickup exceeds $4.00 per gallon, and shall show it as a separate invoice line with the index value and percentage applied. The fuel surcharge on any Shipment shall not exceed fifteen percent (15%) of the Line Haul charge for that Shipment, and no fuel surcharge applies to accessorial charges.',
    ),

    art('4', 'Accessorial Charges'),
    sub(
      '4.1',
      'Permitted accessorials',
      'Carrier may charge the following accessorial charges where the service was requested by Shipper in advance and actually performed: (a) lift-gate service, $75 per Shipment; (b) inside delivery, $110 per Shipment; and (c) re-delivery requested by Shipper after a failed delivery that was not caused by Carrier, $150. Carrier shall not invoice any other charge, fee or surcharge unless Shipper has approved it in writing before the service was performed.',
    ),
    sub(
      '4.2',
      'Detention requires signed proof',
      'Detention is payable only if the equipment is held at a Shipper facility for more than two (2) hours after the scheduled appointment time, at seventy-five dollars ($75) per hour after the first two (2) hours, and Carrier submits the claim with proof of the delay signed by an authorized representative of the Shipper facility, such as a signed gate log or bill of lading. Carrier shall not invoice detention without the signed proof, and Shipper is not obliged to pay any detention charge that is not supported by it.',
    ),

    art('5', 'Invoicing and Payment'),
    sub(
      '5.1',
      'Invoices',
      'Carrier shall invoice each Shipment after delivery. Each invoice shall reference this Agreement by its number, MSA-SNTL-2025-003, and Shipper’s purchase order or shipment reference, shall attach the bill of lading and the signed proof of delivery, and shall separately state the Line Haul, any fuel surcharge and each accessorial charge.',
    ),
    sub(
      '5.2',
      'Payment terms',
      'Shipper shall pay each undisputed invoice within fifteen (15) days after receipt of a valid invoice (“Net 15”). Any shorter payment term stated on an invoice, bill of lading or other Carrier document has no effect.',
    ),
    sub(
      '5.3',
      'Disputed charges and audit',
      'Shipper may withhold payment of any amount that it disputes in good faith, provided that it notifies Carrier of the reason, and may audit Carrier’s invoices and supporting records for three (3) years after payment. Carrier shall promptly refund any overcharge identified.',
    ),

    art('6', 'Service and Liability'),
    sub(
      '6.1',
      'On-time performance',
      'Carrier shall pick up and deliver at least ninety-five percent (95%) of Shipments within the scheduled window, measured monthly, and shall provide a signed proof of delivery for every Shipment within one (1) business day after delivery.',
    ),
    sub(
      '6.2',
      'Cargo liability and insurance',
      'Carrier is liable to Shipper for the actual loss of, or damage to, a Shipment while it is in Carrier’s possession, to the full extent provided by 49 U.S.C. Section 14706 (the Carmack Amendment), and shall maintain automobile liability insurance of not less than $1,000,000 per accident and cargo insurance of not less than $250,000 per vehicle.',
    ),

    art('7', 'Termination and General'),
    sub(
      '7.1',
      'Termination',
      'Either party may terminate this Agreement for convenience on thirty (30) days’ prior written notice. Shipments tendered and accepted before the effective date of termination shall be completed on the terms of this Agreement.',
    ),
    sub(
      '7.2',
      'Governing law and general',
      'This Agreement is governed by the laws of the State of New York. Terms printed on any invoice, bill of lading or other Carrier document that conflict with this Agreement have no effect.',
    ),
  ],
  signatures: {
    buyerSigner: 'Daniel Reyes',
    buyerTitle: 'Director, Indirect Procurement',
    supplierSigner: 'Gloria Mendez',
    supplierTitle: 'President',
    signedDate: '2025-08-12',
    envelopeId: 'E-C2F64A19-7B30-4D85-B1E9-0A83E5D7C614',
  },
};

const ALL_DOCUMENTS: ContractDocument[] = [
  MSA_GEN_2025_001,
  SA_CORV_2024_017,
  QA_VITX_2023_004,
  SA_VITX_2023_004,
  SA_LUMN_2025_002,
  SA_AURE_2024_009,
  MSA_HALB_2023_011,
  SOW_HALB_2026_03,
  MSA_TRAN_2025_006,
  EA_STRT_2024_001,
  MSA_BRTL_2025_004,
  CPA_NSTR_2025_002,
  SA_HOLL_2024_021,
  SA_KAIS_2025_003,
  SA_NGTE_2024_012,
  SA_POLR_2022_008,
  SA_MERI_2025_001,
  SA_CLRW_2024_006,
  SA_STLR_2024_005,
  MSA_KEYS_2024_010,
  MSA_CLVW_2024_003,
  NDA_KEST_2026_001,
  SA_MROS_2025_004,
  SA_BRST_2024_011,
  MSA_BRZV_2025_002,
  MSA_SNTL_2025_003,
];

export const CONTRACT_DOCUMENTS: Record<string, ContractDocument> =
  Object.fromEntries(ALL_DOCUMENTS.map((doc) => [doc.contractId, doc]));

export function getContractDocument(id: string): ContractDocument | undefined {
  return CONTRACT_DOCUMENTS[id];
}

export function findSection(
  contractId: string,
  number: string,
): DocSection | undefined {
  return getContractDocument(contractId)?.sections.find(
    (section) => section.number === number,
  );
}
